/*
  MATHS RACER - LEVEL 2 REAL CIRCUIT - PERFORMANCE PASS 3 V23

  Level 1 keeps its original circular circuit.
  Level 2 uses a longer, purpose-built motorsport circuit.
  Quaternius and original grey buildings alternate along one
  continuous, circuit-facing facade line.
  Unsafe buildings are discarded after placement checks.
  Includes an improved continuous outer city road with broken centre markings,
  give-way markings, side streets and crossings.
  The city road remains separate from the race circuit behind continuous Armco.
  Building façade axes face the circuit on both sides.
*/

import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react";

import {
  Canvas,
  useFrame,
  useLoader,
  useThree,
} from "@react-three/fiber";

import {
  Environment,
  Sky,
  useGLTF,
  useTexture,
} from "@react-three/drei";

import * as THREE from "three";

import "./MathsRacer.css";


/* =========================================================
   GAME SETTINGS
========================================================= */

const TOTAL_LAPS = 3;

const LEVEL_1_PLAYER_SPEEDS = {
  base: 0.035,
  boost: 0.052,
  slow: 0.022,
};

const LEVEL_2_PLAYER_SPEEDS = {
  base: 0.020,
  boost: 0.030,
  slow: 0.013,
};

const LEVEL_3_PLAYER_SPEEDS = {
  base: 0.018,
  boost: 0.027,
  slow: 0.012,
};

const BOOST_DURATION = 3000;
const SLOW_DURATION = 2200;


/* =========================================================
   ROAD SETTINGS
========================================================= */

const ROAD_WIDTH = 20;

const NUMBER_OF_LANES = 4;

const LANE_WIDTH =
  ROAD_WIDTH /
  NUMBER_OF_LANES;

const ROAD_HALF_WIDTH =
  ROAD_WIDTH / 2;


/* =========================================================
   LANE CENTRES
========================================================= */

const LANE_OFFSETS = {
  FAR_LEFT:
    -ROAD_HALF_WIDTH +
    LANE_WIDTH / 2,

  LEFT:
    -LANE_WIDTH / 2,

  RIGHT:
    LANE_WIDTH / 2,

  FAR_RIGHT:
    ROAD_HALF_WIDTH -
    LANE_WIDTH / 2,
};


/* =========================================================
   LANE DIVIDERS
========================================================= */

const LANE_DIVIDER_OFFSETS = [
  -LANE_WIDTH,
  0,
  LANE_WIDTH,
];


/* =========================================================
   KERBS + BARRIERS
========================================================= */

const KERB_WIDTH = 0.9;

const KERB_OFFSET =
  ROAD_HALF_WIDTH +
  KERB_WIDTH / 2;


const BARRIER_THICKNESS =
  0.3;

const BARRIER_HEIGHT =
  1.35;


const BARRIER_OFFSET =
  ROAD_HALF_WIDTH +
  KERB_WIDTH +
  BARRIER_THICKNESS / 2;


/* =========================================================
   CAR IDS
========================================================= */

const CAR_IDS = {
  PLAYER: "player",
  BLUE: "blue",
  RED: "red",
  GREEN: "green",
};


/* =========================================================
   INITIAL RACE DATA
========================================================= */

function createInitialRaceData() {
  return {
    player: {
      lap: -1,
      progress: 0.990,
      totalProgress: -0.010,
    },

    blue: {
      lap: -1,
      progress: 0.990,
      totalProgress: -0.010,
    },

    red: {
      lap: -1,
      progress: 0.990,
      totalProgress: -0.010,
    },

    green: {
      lap: -1,
      progress: 0.990,
      totalProgress: -0.010,
    },
  };
}


/* =========================================================
   QUESTION BANK
========================================================= */

const mathsQuestions = [
  {
    question: "7 × 8",
    answer: "56",
  },

  {
    question: "12 × 6",
    answer: "72",
  },

  {
    question: "144 ÷ 12",
    answer: "12",
  },

  {
    question: "15% of 200",
    answer: "30",
  },

  {
    question: "3² + 4²",
    answer: "25",
  },

  {
    question: "0.4 × 50",
    answer: "20",
  },

  {
    question: "3/4 of 80",
    answer: "60",
  },

  {
    question: "√121",
    answer: "11",
  },
];


/* =========================================================
   BMW CAR
========================================================= */

function Car({
  color,
}) {
  const {
    scene,
  } = useGLTF(
    "/assets/bmw_e46_m3_sports_car_red.glb"
  );


  const carScene =
    useMemo(
      () => {
        const clone =
          scene.clone(true);


        clone.traverse(
          (child) => {
            if (
              !child.isMesh
            ) {
              return;
            }


            /*
              Clone materials so changing
              one car doesn't alter all cars.
            */

            if (
              Array.isArray(
                child.material
              )
            ) {
              child.material =
                child.material.map(
                  (material) =>
                    material.clone()
                );
            } else if (
              child.material
            ) {
              child.material =
                child.material.clone();
            }


            child.castShadow =
              true;

            child.receiveShadow =
              true;


            /* =============================================
               AUTOMOTIVE PAINT
            ============================================= */

            const replaceMaterial =
              (material) => {
                if (
                  !material
                ) {
                  return material;
                }


                if (
                  material.name ===
                  "Body"
                ) {
                  const carPaint =
                    new THREE
                      .MeshPhysicalMaterial({
                        name:
                          "Body",

                        color:
                          new THREE.Color(
                            color
                          ),

                        metalness:
                          0.25,

                        roughness:
                          0.14,

                        clearcoat:
                          1,

                        clearcoatRoughness:
                          0.03,

                        envMapIntensity:
                          2.2,

                        ior:
                          1.5,

                        reflectivity:
                          0.7,
                      });


                  return carPaint;
                }


                return material;
              };


            if (
              Array.isArray(
                child.material
              )
            ) {
              child.material =
                child.material.map(
                  replaceMaterial
                );
            } else {
              child.material =
                replaceMaterial(
                  child.material
                );
            }
          }
        );


        /* =============================================
           SCALE BMW
        ============================================= */

        const box =
          new THREE.Box3()
            .setFromObject(
              clone
            );


        const size =
          new THREE.Vector3();


        box.getSize(
          size
        );


        const targetLength =
          5.5;


        const largestDimension =
          Math.max(
            size.x,
            size.y,
            size.z
          );


        const scale =
          targetLength /
          largestDimension;


        clone.scale.setScalar(
          scale
        );


        /* =============================================
           CENTRE BMW
        ============================================= */

        const scaledBox =
          new THREE.Box3()
            .setFromObject(
              clone
            );


        const centre =
          new THREE.Vector3();


        scaledBox.getCenter(
          centre
        );


        clone.position.x -=
          centre.x;

        clone.position.z -=
          centre.z;


        clone.position.y -=
          scaledBox.min.y;


        return clone;
      },
      [
        scene,
        color,
      ]
    );


  return (
    <primitive
      object={
        carScene
      }
    />
  );
}


useGLTF.preload(
  "/assets/bmw_e46_m3_sports_car_red.glb"
);


/* =========================================================
   TREE MODEL
========================================================= */

function RealisticTree({

  

  position,
  rotation = 0,
  scale = 1,
}) {
  const currentLevel =
    useContext(RaceTrackLevelContext);
  const {
    scene,
  } = useGLTF(
    "/assets/realistic_tree_models_for_games.glb"
  );


  const treeScene =
    useMemo(
      () => {
        const clone =
          scene.clone(true);


       clone.traverse(
  (child) => {
    if (child.isMesh) {
      child.castShadow = false;
      child.receiveShadow = false;

      // Darken foliage ONLY in Level 2
      if (
        currentLevel === 2 &&
        child.material
      ) {
        child.material =
          child.material.clone();

        const colour =
          child.material.color;

        if (
          colour &&
          colour.g > colour.r &&
          colour.g > colour.b
        ) {
          colour.multiplyScalar(0.35);

          child.material.needsUpdate =
            true;
        }
      }
    }
  }
);


        return clone;
      },
      [
        scene,
        currentLevel,
      ]
    );


  return (
    <group
      position={
        position
      }

      rotation={[
        0,
        rotation,
        0,
      ]}

      scale={[
        scale,
        scale,
        scale,
      ]}
    >
      <primitive
        object={
          treeScene
        }
      />
    </group>
  );
}


useGLTF.preload(
  "/assets/realistic_tree_models_for_games.glb"
);


useGLTF.preload(
  "/assets/realistic_tree_models_for_games.glb"
);

useGLTF.preload(
  "/assets/game_ready_city_buildings_pack.glb"
);


/* =========================================================
   LEVEL 2 - QUATERNIUS CITY ASSETS
========================================================= */

const CITY_BUILDING_MODELS = [
  {
    path:
      "/assets/downtown-city/Building_Small_1.gltf",

    weight:
      5,

    targetHeights: [
     5.2,
  5.6,
  6.0,
  6.4,
    ],
  },

  {
    path:
      "/assets/downtown-city/Building_Medium_2_001.gltf",

    weight:
      5,

    targetHeights: [
      6.2,
  6.7,
  7.2,
  7.6,
    ],
  },

  {
    path:
      "/assets/downtown-city/Building_Large_2.gltf",

    weight:
      1,

    targetHeights: [
      7.5,
      8.2,
    ],
  },
];


/*
  Every building system uses these same positions.
  This keeps deliberate openings between city blocks so
  each side street remains visible and unobstructed.
*/

const CITY_SIDE_STREET_POINTS = [
  0.07,
  0.235,
  0.40,
  0.565,
  0.73,
  0.895,
];

function CityPedestrianCrossings() {
  const curve =
    useRaceTrackCurve();

  /*
    We deliberately DON'T put the asphalt texture
    directly onto the white paint.

    The dark road underneath remains visible between
    the individual white stripes, which produces a
    much more convincing pedestrian crossing.
  */

  const crossings =
    useMemo(
      () => {
        const result = [];

        CITY_SIDE_STREET_POINTS.forEach(
          (t) => {
            const point =
              curve.getPointAt(t);

            const tangent =
              curve
                .getTangentAt(t)
                .normalize();

            /*
              SIDE points away from the main circuit
              towards the side street.
            */

            const side =
              new THREE.Vector3(
                -tangent.z,
                0,
                tangent.x
              ).normalize();

            /*
              Rotation of the crossing in world space.
            */

            const angle =
              Math.atan2(
                tangent.x,
                tangent.z
              );

            /*
              Put one crossing on each side of
              the racing circuit.
            */

            [-1, 1].forEach(
              (direction) => {
                const centre =
                  point
                    .clone()
                    .add(
                      side
                        .clone()
                        .multiplyScalar(
                          direction * 18
                        )
                    );

                result.push({
                  id:
                    `${t}-${direction}`,

                  position: [
  centre.x,
  0.086,
  centre.z,
],

rotation: angle,
                });
              }
            );
          }
        );

        return result;
      },
      [curve]
    );

  /*
    CROSSING DIMENSIONS

    These are deliberately fairly chunky.

    stripeLength:
      width of each white bar across the side road

    stripeDepth:
      thickness of each stripe along the road

    stripeGap:
      visible asphalt between stripes
  */

  const stripeCount = 7;

  const stripeLength = 6.6;

  const stripeDepth = 0.62;

  const stripeGap = 0.52;

  return (
    <>
      {crossings.map(
        (crossing) => (
          <group
            key={crossing.id}
            position={
              crossing.position
            }
            rotation={[
              0,
              crossing.rotation,
              0,
            ]}
          >
            {Array.from({
              length:
                stripeCount,
            }).map(
              (_, index) => {
                /*
                  Centre the complete set of stripes.
                */

                const totalDepth =
                  stripeCount *
                    stripeDepth +
                  (stripeCount - 1) *
                    stripeGap;

                const stripeZ =
                  -totalDepth / 2 +
                  stripeDepth / 2 +
                  index *
                    (
                      stripeDepth +
                      stripeGap
                    );

                return (
                  <mesh
                    key={index}
                    position={[
                      0,
                      0,
                      stripeZ,
                    ]}
                    rotation={[
                      -Math.PI / 2,
                      0,
                      0,
                    ]}
                    receiveShadow
                  >
                    <planeGeometry
                      args={[
                        stripeLength,
                        stripeDepth,
                      ]}
                    />

                    <meshStandardMaterial
                      color="#e7e6df"
                      roughness={0.92}
                      metalness={0}
                      side={
                        THREE.DoubleSide
                      }
                      polygonOffset
                      polygonOffsetFactor={
                        -2
                      }
                      polygonOffsetUnits={
                        -2
                      }
                    />
                  </mesh>
                );
              }
            )}
          </group>
        )
      )}
    </>
  );
}



/* =========================================================
   LEVEL 2 - PEDESTRIAN CROSSING SIGNALS
========================================================= */

/* =========================================================
   LEVEL 2 - REAL GLB TRAFFIC LIGHT

   Replaces the old box/cylinder pedestrian traffic light with
   the actual "TerafficLight-1" object contained in:

   /public/assets/city-props/city-props.glb

   The source pack places this object away from the GLB origin,
   so the cloned model is automatically re-centred and grounded
   before it is placed beside each zebra crossing.
========================================================= */

function RealCityTrafficLight() {
  const { scene } = useGLTF(
    "/assets/city-props/city-props.glb"
  );

  const trafficLightScene =
    useMemo(
      () => {
        let source =
          scene.getObjectByName(
            "TerafficLight-1"
          );

        /*
          Fallback in case the exporter changes punctuation or
          capitalisation in a later copy of the GLB.
        */
        if (!source) {
          scene.traverse(
            (child) => {
              if (
                !source &&
                child.name &&
                (
                  child.name
                    .toLowerCase()
                    .includes(
                      "terafficlight-1"
                    ) ||
                  child.name
                    .toLowerCase()
                    .includes(
                      "trafficlight-1"
                    )
                )
              ) {
                source = child;
              }
            }
          );
        }

        if (!source) {
          console.warn(
            'No "TerafficLight-1" object was found in city-props.glb'
          );

          return null;
        }

        const clone =
          source.clone(true);

        clone.traverse(
          (child) => {
            if (!child.isMesh) {
              return;
            }

            /*
              Clone materials so this instance can be adjusted later
              without mutating the original GLB scene.
            */
            if (
              Array.isArray(
                child.material
              )
            ) {
              child.material =
                child.material.map(
                  (material) =>
                    material.clone()
                );
            } else if (
              child.material
            ) {
              child.material =
                child.material.clone();
            }

            child.castShadow = false;
            child.receiveShadow = false;
          }
        );

        /*
          The source model is already approximately real-world size
          (about 3.56 units high). Normalise it to 3.65 m so every
          crossing gets a consistent, believable signal height.
        */
        const originalBox =
          new THREE.Box3()
            .setFromObject(clone);

        const originalSize =
          new THREE.Vector3();

        originalBox.getSize(
          originalSize
        );

        const TARGET_HEIGHT =
          3.65;

        if (
          originalSize.y > 0
        ) {
          const scale =
            TARGET_HEIGHT /
            originalSize.y;

          clone.scale.setScalar(
            scale
          );
        }

        /*
          Recalculate after scaling, centre the model horizontally,
          and put its lowest point exactly on the pavement.
        */
        const scaledBox =
          new THREE.Box3()
            .setFromObject(clone);

        const centre =
          new THREE.Vector3();

        scaledBox.getCenter(
          centre
        );

        clone.position.x -=
          centre.x;

        clone.position.z -=
          centre.z;

        clone.position.y -=
          scaledBox.min.y;

        return clone;
      },
      [scene]
    );

  if (!trafficLightScene) {
    return null;
  }

  return (
    <group>
      <primitive
        object={
          trafficLightScene
        }
      />
    </group>
  );
}


function CityPedestrianTrafficLights() {
  const curve =
    useRaceTrackCurve();

  const INNER_PAVEMENT_WIDTH = 3.4;
  const CITY_ROAD_WIDTH = 7.2;
  const OUTER_PAVEMENT_WIDTH = 2.6;
  const SIDE_STREET_LENGTH = 28;

  const armcoOuterEdge =
    BARRIER_OFFSET +
    BARRIER_THICKNESS / 2;

  const cityRoadOuterEdge =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH +
    CITY_ROAD_WIDTH;

  const buildingSidePavementOuterEdge =
    cityRoadOuterEdge +
    OUTER_PAVEMENT_WIDTH;

  const lights =
    useMemo(
      () => {
        const result = [];

        CITY_SIDE_STREET_POINTS.forEach(
          (t) => {
            const point =
              curve.getPointAt(t);

            const tangent =
              curve
                .getTangentAt(t)
                .normalize();

            const side =
              new THREE.Vector3(
                -tangent.z,
                0,
                tangent.x
              ).normalize();

            const trackAngle =
              Math.atan2(
                tangent.x,
                tangent.z
              );

            [-1, 1].forEach(
              (direction) => {
                const roadStartDistance =
                  buildingSidePavementOuterEdge -
                  0.8;

                const centreDistance =
                  roadStartDistance +
                  SIDE_STREET_LENGTH / 2;

                const streetCentre =
                  point
                    .clone()
                    .add(
                      side
                        .clone()
                        .multiplyScalar(
                          direction *
                          centreDistance
                        )
                    );

                const roadOutward =
                  side
                    .clone()
                    .multiplyScalar(
                      direction
                    );

                const crossingCentre =
                  streetCentre
                    .clone()
                    .add(
                      roadOutward
                        .clone()
                        .multiplyScalar(
                          -SIDE_STREET_LENGTH / 2 +
                          4.0
                        )
                    );

                /* One signal on each pavement edge of the crossing. */
                [-1, 1].forEach(
                  (end) => {
                    const position =
                      crossingCentre
                        .clone()
                        .add(
                          tangent
                            .clone()
                            .multiplyScalar(
                              end * 4.15
                            )
                        );

                    result.push({
                      id:
                        `${t}-${direction}-${end}`,

                      position: [
                        position.x,
                        0,
                        position.z,
                      ],

                      rotation:
                        trackAngle +
                        (
                          end === 1
                            ? Math.PI
                            : 0
                        ),
                    });
                  }
                );
              }
            );
          }
        );

        return result;
      },
      [
        curve,
        buildingSidePavementOuterEdge,
      ]
    );

  return (
    <group>
      {lights.map(
        (light) => (
          <group
            key={light.id}
            position={light.position}
            rotation={[
              0,
              light.rotation,
              0,
            ]}
          >
            <RealCityTrafficLight />
          </group>
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 2 - SKY AND CLOUDS
========================================================= */

function LevelTwoSky() {
  return (
    <Sky
      distance={450000}
      sunPosition={[-80, 18, -120]}
      turbidity={18}
      rayleigh={5.5}
      mieCoefficient={0.025}
      mieDirectionalG={0.93}
    />
  );
}


/* =========================================================
   LEVEL 2 - RAIN

   The rain volume follows the camera rather than covering the
   entire city. This gives us thousands of visible drops without
   wasting particles in places the player cannot see.

   Level 1 never renders this component.
========================================================= */

function LevelTwoRain({
  count = 1400,
}) {
  const rainRef =
    useRef();

  /*
    PERFORMANCE PASS:
    The CPU no longer loops through every raindrop on every frame.

    Instead, each drop stores only:
      - a random starting position
      - a random fall speed
      - a random streak length

    The GPU vertex shader calculates the animated position of every
    streak from a single uTime uniform.
  */
  const rainGeometry =
    useMemo(
      () => {
        const vertexCount =
          count * 2;

        const positions =
          new Float32Array(
            vertexCount * 3
          );

        const seeds =
          new Float32Array(
            vertexCount
          );

        const speeds =
          new Float32Array(
            vertexCount
          );

        const lengths =
          new Float32Array(
            vertexCount
          );

        const ends =
          new Float32Array(
            vertexCount
          );

        for (
          let i = 0;
          i < count;
          i++
        ) {
          const x =
            (Math.random() - 0.5) *
            90;

          const y =
            Math.random() *
            58;

          const z =
            (Math.random() - 0.5) *
            100;

          const seed =
            Math.random();

          const speed =
            26 +
            Math.random() *
            22;

          const length =
            0.7 +
            Math.random() *
            1.0;

          for (
            let end = 0;
            end < 2;
            end++
          ) {
            const vertex =
              i * 2 + end;

            const base =
              vertex * 3;

            positions[base] =
              x;

            positions[base + 1] =
              y;

            positions[base + 2] =
              z;

            seeds[vertex] =
              seed;

            speeds[vertex] =
              speed;

            lengths[vertex] =
              length;

            ends[vertex] =
              end;
          }
        }

        const geometry =
          new THREE.BufferGeometry();

        geometry.setAttribute(
          "position",
          new THREE.BufferAttribute(
            positions,
            3
          )
        );

        geometry.setAttribute(
          "aSeed",
          new THREE.BufferAttribute(
            seeds,
            1
          )
        );

        geometry.setAttribute(
          "aSpeed",
          new THREE.BufferAttribute(
            speeds,
            1
          )
        );

        geometry.setAttribute(
          "aLength",
          new THREE.BufferAttribute(
            lengths,
            1
          )
        );

        geometry.setAttribute(
          "aEnd",
          new THREE.BufferAttribute(
            ends,
            1
          )
        );

        return geometry;
      },
      [count]
    );

  const rainMaterial =
    useMemo(
      () =>
        new THREE.ShaderMaterial({
          transparent: true,
          depthWrite: false,
          toneMapped: false,

          uniforms: {
            uTime: {
              value: 0,
            },

            uColour: {
              value:
                new THREE.Color(
                  "#d6e2e8"
                ),
            },

            uOpacity: {
              value: 0.40,
            },
          },

          vertexShader: `
            uniform float uTime;

            attribute float aSeed;
            attribute float aSpeed;
            attribute float aLength;
            attribute float aEnd;

            varying float vFade;

            void main() {
              vec3 p = position;

              /*
                A 64-unit vertical loop keeps every streak inside
                the camera-centred rain volume.
              */
              float travelled =
                mod(
                  p.y -
                  uTime * aSpeed +
                  12.0,
                  64.0
                );

              p.y =
                travelled -
                12.0;

              /*
                Small wind lean. The lower endpoint is offset,
                producing a diagonal streak without CPU animation.
              */
              p.x +=
                uTime * 2.4;

              p.x =
                mod(
                  p.x +
                  45.0,
                  90.0
                ) -
                45.0;

              if (aEnd > 0.5) {
                p.y -= aLength;
                p.x -= aLength * 0.18;
              }

              /*
                Fade some streaks slightly using their random seed.
              */
              vFade =
                0.55 +
                aSeed * 0.45;

              gl_Position =
                projectionMatrix *
                modelViewMatrix *
                vec4(
                  p,
                  1.0
                );
            }
          `,

          fragmentShader: `
            uniform vec3 uColour;
            uniform float uOpacity;

            varying float vFade;

            void main() {
              gl_FragColor =
                vec4(
                  uColour,
                  uOpacity * vFade
                );
            }
          `,
        }),
      []
    );

  useEffect(
    () => {
      return () => {
        rainGeometry.dispose();
        rainMaterial.dispose();
      };
    },
    [
      rainGeometry,
      rainMaterial,
    ]
  );

  useFrame(
    (
      state
    ) => {
      if (!rainRef.current) {
        return;
      }

      /*
        Only THREE cheap CPU updates per frame:
        one uniform + X/Z rain-volume position.
      */
      rainMaterial.uniforms
        .uTime.value =
        state.clock.elapsedTime;

      rainRef.current.position.x =
        state.camera.position.x;

      rainRef.current.position.z =
        state.camera.position.z;
    }
  );

  return (
    <lineSegments
      ref={rainRef}
      geometry={rainGeometry}
      material={rainMaterial}
      frustumCulled={false}
      renderOrder={20}
    />
  );
}

function isNearCitySideStreet(
  t,
  clearance = 0.03
) {
  return CITY_SIDE_STREET_POINTS.some(
    (streetT) => {
      const difference =
        Math.abs(
          t -
          streetT
        );


      const circularDifference =
        Math.min(
          difference,
          1 - difference
        );


      return circularDifference <
        clearance;
    }
  );
}


CITY_BUILDING_MODELS.forEach(
  (building) => {
    useGLTF.preload(
      building.path
    );
  }
);


/* =========================================================
   TRACK CURVE
========================================================= */

const RaceTrackLevelContext =
  React.createContext(1);


function createRoundedCircuitCurve(
  vertices,
  preferredTrim = 20
) {
  const entries = [];
  const exits = [];


  vertices.forEach(
    (
      vertex,
      index
    ) => {
      const previous =
        vertices[
          (
            index - 1 +
            vertices.length
          ) %
          vertices.length
        ];


      const next =
        vertices[
          (
            index + 1
          ) %
          vertices.length
        ];


      const trim =
        Math.min(
          preferredTrim,
          previous.distanceTo(
            vertex
          ) * 0.34,
          vertex.distanceTo(
            next
          ) * 0.34
        );


      entries.push(
        vertex
          .clone()
          .add(
            previous
              .clone()
              .sub(vertex)
              .normalize()
              .multiplyScalar(trim)
          )
      );


      exits.push(
        vertex
          .clone()
          .add(
            next
              .clone()
              .sub(vertex)
              .normalize()
              .multiplyScalar(trim)
          )
      );
    }
  );


  const circuit =
    new THREE.CurvePath();


  vertices.forEach(
    (
      vertex,
      index
    ) => {
      const previousExit =
        exits[
          (
            index - 1 +
            vertices.length
          ) %
          vertices.length
        ];


      const entry =
        entries[index];


      const exit =
        exits[index];


      circuit.add(
        new THREE.LineCurve3(
          previousExit,
          entry
        )
      );


      circuit.add(
        new THREE
          .QuadraticBezierCurve3(
            entry,
            vertex,
            exit
          )
      );
    }
  );


  circuit.autoClose =
    true;


  /*
    The default curve lookup is too coarse for a long racing
    circuit and can make getPointAt() appear to advance in steps.
  */

  circuit.arcLengthDivisions =
    2400;

  circuit.updateArcLengths();


  return circuit;
}


function createRaceTrackCurve(
  currentLevel
) {
  const levelOnePoints = [

        // ==========================================
        // START / FINISH STRAIGHT
        // ==========================================

        new THREE.Vector3(
          0,
          0,
          38
        ),

        new THREE.Vector3(
          -12,
          0,
          38
        ),

        new THREE.Vector3(
          -24,
          0,
          38
        ),

        // ==========================================
        // LEFT-HAND BEND
        // ==========================================

        new THREE.Vector3(
          -34,
          0,
          32
        ),

        new THREE.Vector3(
          -40,
          0,
          20
        ),

        new THREE.Vector3(
          -41,
          0,
          5
        ),

        new THREE.Vector3(
          -38,
          0,
          -12
        ),

        // ==========================================
        // BOTTOM OF CIRCUIT
        // ==========================================

        new THREE.Vector3(
          -28,
          0,
          -28
        ),

        new THREE.Vector3(
          -12,
          0,
          -37
        ),

        new THREE.Vector3(
          8,
          0,
          -39
        ),

        new THREE.Vector3(
          25,
          0,
          -34
        ),

        // ==========================================
        // RIGHT-HAND BEND
        // ==========================================

        new THREE.Vector3(
          38,
          0,
          -22
        ),

        new THREE.Vector3(
          43,
          0,
          -7
        ),

        new THREE.Vector3(
          42,
          0,
          10
        ),

        new THREE.Vector3(
          37,
          0,
          24
        ),

        new THREE.Vector3(
          28,
          0,
          34
        ),

        // ==========================================
        // RETURN TO START / FINISH STRAIGHT
        // ==========================================

        new THREE.Vector3(
          20,
          0,
          38
        ),

        new THREE.Vector3(
          10,
          0,
          38
        ),
  ];


      /*
        LEVEL 2: A longer, recognisable motorsport circuit.

        Layout features:
        - long start / finish straight
        - distinct straight braking zones
        - rounded multi-corner western sector
        - back-straight chicane
        - fast eastern straight and final sector
      */

  const levelTwoPoints = [
        // Start / finish straight.
        new THREE.Vector3(38, 0, 50),
        new THREE.Vector3(5, 0, 50),
        new THREE.Vector3(-30, 0, 50),

        // Turns 1-3 and the western straight.
        new THREE.Vector3(-58, 0, 46),
        new THREE.Vector3(-78, 0, 30),
        new THREE.Vector3(-85, 0, 8),
        new THREE.Vector3(-82, 0, -18),

        // Long lower-left bend into the back straight.
        new THREE.Vector3(-68, 0, -38),
        new THREE.Vector3(-45, 0, -50),
        new THREE.Vector3(-18, 0, -50),

        // Chicane on the back section.
        new THREE.Vector3(0, 0, -44),
        new THREE.Vector3(18, 0, -52),
        new THREE.Vector3(45, 0, -52),

        // Fast eastern straight and final sector.
        new THREE.Vector3(68, 0, -40),
        new THREE.Vector3(82, 0, -18),
        new THREE.Vector3(82, 0, 8),
        new THREE.Vector3(72, 0, 28),
        new THREE.Vector3(55, 0, 38),
  ];

  /*
    LEVEL 3: BEACH GRAND PRIX

    A more technical, winding final circuit with a mixture of
    flowing bends, direction changes and a short beach-front straight.
  */
  const levelThreePoints = [
    // BEACH GRAND PRIX - widened layout.
    // The old infield loop brought separate sections of the 20-unit-wide
    // circuit too close together, so the Armco from neighbouring bends
    // overlapped. This version keeps the same flowing beach-race character
    // but gives every bend substantially more physical separation.
    new THREE.Vector3(58, 0, 58),
    new THREE.Vector3(24, 0, 66),
    new THREE.Vector3(-18, 0, 66),
    new THREE.Vector3(-55, 0, 55),
    new THREE.Vector3(-82, 0, 36),
    new THREE.Vector3(-96, 0, 10),
    new THREE.Vector3(-94, 0, -18),
    new THREE.Vector3(-78, 0, -44),
    new THREE.Vector3(-50, 0, -62),
    new THREE.Vector3(-16, 0, -70),
    new THREE.Vector3(18, 0, -66),
    new THREE.Vector3(48, 0, -74),
    new THREE.Vector3(76, 0, -58),
    new THREE.Vector3(96, 0, -34),
    new THREE.Vector3(103, 0, -5),
    new THREE.Vector3(96, 0, 24),
    new THREE.Vector3(78, 0, 45),
  ];


  if (
    currentLevel === 3
  ) {
    return createRoundedCircuitCurve(
      levelThreePoints,
      12
    );
  }

  if (
    currentLevel === 2
  ) {
    return createRoundedCircuitCurve(
      levelTwoPoints,
      20
    );
  }


  return new THREE
    .CatmullRomCurve3(
      levelOnePoints,
      true,
      "catmullrom",
      0.18
    );
}


function useRaceTrackCurve() {
  const currentLevel =
    useContext(
      RaceTrackLevelContext
    );


  return useMemo(
    () =>
      createRaceTrackCurve(
        currentLevel
      ),
    [currentLevel]
  );
}


/* =========================================================
   CURVED FLAT STRIP
========================================================= */

function createTrackStripGeometry(
  curve,
  centreOffset,
  stripWidth,
  y = 0.04,
  segments = 500
) {
  const vertices = [];
  const indices = [];
  const uvs = [];


  const textureRepeatsAlongTrack =
    45;


  for (
    let i = 0;
    i <= segments;
    i++
  ) {
    const t =
      i /
      segments;


    const point =
      curve.getPointAt(
        t
      );


    const tangent =
      curve
        .getTangentAt(
          t
        )
        .normalize();


    const side =
      new THREE.Vector3(
        -tangent.z,
        0,
        tangent.x
      ).normalize();


    const centre =
      point
        .clone()
        .add(
          side
            .clone()
            .multiplyScalar(
              centreOffset
            )
        );


    const left =
      centre
        .clone()
        .add(
          side
            .clone()
            .multiplyScalar(
              stripWidth /
              2
            )
        );


    const right =
      centre
        .clone()
        .add(
          side
            .clone()
            .multiplyScalar(
              -stripWidth /
              2
            )
        );


    vertices.push(
      left.x,
      y,
      left.z
    );


    vertices.push(
      right.x,
      y,
      right.z
    );


    const v =
      t *
      textureRepeatsAlongTrack;


    uvs.push(
      0,
      v
    );


    uvs.push(
      1,
      v
    );


    if (
      i <
      segments
    ) {
      const a =
        i * 2;

      const b =
        a + 1;

      const c =
        a + 2;

      const d =
        a + 3;


      indices.push(
        a,
        b,
        c
      );


      indices.push(
        b,
        d,
        c
      );
    }
  }


  const geometry =
    new THREE
      .BufferGeometry();


  geometry.setAttribute(
    "position",

    new THREE
      .Float32BufferAttribute(
        vertices,
        3
      )
  );


  geometry.setAttribute(
    "uv",

    new THREE
      .Float32BufferAttribute(
        uvs,
        2
      )
  );


  geometry.setIndex(
    indices
  );


  geometry.computeVertexNormals();


  return geometry;
}


/* =========================================================
   TRACK
========================================================= */

function Track() {
  const currentLevel =
    useContext(RaceTrackLevelContext);

  const curve =
    useRaceTrackCurve();


  const grassTexture =
    useLoader(
      THREE.TextureLoader,
      "/assets/grass.png"
    );


  grassTexture.wrapS =
    THREE.RepeatWrapping;

  grassTexture.wrapT =
    THREE.RepeatWrapping;


  grassTexture.repeat.set(
    20,
    20
  );


  grassTexture.colorSpace =
    THREE.SRGBColorSpace;


  grassTexture.anisotropy =
    8;


  const asphaltTexture =
    useLoader(
      THREE.TextureLoader,
      "/assets/asphalt.png"
    );


  asphaltTexture.wrapS =
    THREE.RepeatWrapping;

  asphaltTexture.wrapT =
    THREE.RepeatWrapping;


  asphaltTexture.repeat.set(
    5,
    1
  );


  asphaltTexture.colorSpace =
    THREE.SRGBColorSpace;


  asphaltTexture.anisotropy =
    16;


  const trackGeometry =
    useMemo(
      () =>
        createTrackStripGeometry(
          curve,
          0,
          ROAD_WIDTH,
          0.02,
          600
        ),
      [
        curve,
      ]
    );


  return (
    <>

      {/* GRASS */}

      <mesh
        rotation={[
          -Math.PI / 2,
          0,
          0,
        ]}

        position={[
          0,
          -0.025,
          0,
        ]}

        receiveShadow={
          currentLevel === 1
        }
      >
        <planeGeometry
          args={[
            180,
            180,
          ]}
        />

        <meshStandardMaterial
          map={
            grassTexture
          }

          roughness={
            1
          }

          metalness={
            0
          }
        />
      </mesh>


      {/* ASPHALT */}

      <mesh
        geometry={
          trackGeometry
        }

        receiveShadow={
          currentLevel === 1
        }
      >
        <meshStandardMaterial
          map={
            asphaltTexture
          }

          color={
            currentLevel === 2
              ? "#8d9498"
              : "#d8d8d8"
          }

          roughness={
            currentLevel === 2
              ? 0.42
              : 0.95
          }

          metalness={
            currentLevel === 2
              ? 0.08
              : 0
          }

          envMapIntensity={
            currentLevel === 2
              ? 1.15
              : 1
          }

          side={
            THREE.DoubleSide
          }
        />
      </mesh>


      <ContinuousLaneLines
        curve={
          curve
        }
      />


      <TrackKerbs
        curve={
          curve
        }
      />


      <TrackBarriers
        curve={
          curve
        }
      />

    </>
  );
}


/* =========================================================
   WHITE LANE LINES
========================================================= */

function ContinuousLaneLines({
  curve,
}) {
  const geometries =
    useMemo(
      () =>
        LANE_DIVIDER_OFFSETS.map(
          (offset) =>
            createTrackStripGeometry(
              curve,
              offset,
              0.18,
              0.055,
              650
            )
        ),
      [
        curve,
      ]
    );


  return (
    <>
      {geometries.map(
        (
          geometry,
          index
        ) => (
          <mesh
            key={
              index
            }

            geometry={
              geometry
            }
          >
            <meshStandardMaterial
              color="#ffffff"

              roughness={
                0.75
              }

              side={
                THREE.DoubleSide
              }
            />
          </mesh>
        )
      )}
    </>
  );
}


/* =========================================================
   KERB SECTION
========================================================= */

function createKerbSectionGeometry(
  curve,
  startT,
  endT,
  offset,
  width,
  height
) {
  const subdivisions =
    8;

  const vertices = [];
  const indices = [];


  for (
    let i = 0;
    i <= subdivisions;
    i++
  ) {
    const localT =
      i /
      subdivisions;


    const t =
      startT +
      (
        endT -
        startT
      ) *
      localT;


    const point =
      curve.getPointAt(
        t
      );


    const tangent =
      curve
        .getTangentAt(
          t
        )
        .normalize();


    const side =
      new THREE.Vector3(
        -tangent.z,
        0,
        tangent.x
      ).normalize();


    const centre =
      point
        .clone()
        .add(
          side
            .clone()
            .multiplyScalar(
              offset
            )
        );


    const direction =
      offset >= 0
        ? 1
        : -1;


    const outer =
      centre
        .clone()
        .add(
          side
            .clone()
            .multiplyScalar(
              direction *
              width /
              2
            )
        );


    const inner =
      centre
        .clone()
        .add(
          side
            .clone()
            .multiplyScalar(
              -direction *
              width /
              2
            )
        );


    vertices.push(
      outer.x,
      height,
      outer.z
    );


    vertices.push(
      inner.x,
      height,
      inner.z
    );


    vertices.push(
      outer.x,
      0.02,
      outer.z
    );


    vertices.push(
      inner.x,
      0.02,
      inner.z
    );
  }


  for (
    let i = 0;
    i < subdivisions;
    i++
  ) {
    const base =
      i * 4;

    const next =
      base + 4;


    indices.push(
      base,
      base + 1,
      next
    );

    indices.push(
      base + 1,
      next + 1,
      next
    );


    indices.push(
      base,
      next,
      base + 2
    );

    indices.push(
      next,
      next + 2,
      base + 2
    );


    indices.push(
      base + 1,
      base + 3,
      next + 1
    );

    indices.push(
      base + 3,
      next + 3,
      next + 1
    );
  }


  const geometry =
    new THREE
      .BufferGeometry();


  geometry.setAttribute(
    "position",

    new THREE
      .Float32BufferAttribute(
        vertices,
        3
      )
  );


  geometry.setIndex(
    indices
  );


  geometry.computeVertexNormals();


  return geometry;
}


function mergeStaticGeometries(
  geometries
) {
  const positions = [];
  const normals = [];

  geometries.forEach(
    (geometry) => {
      const source =
        geometry.index
          ? geometry.toNonIndexed()
          : geometry.clone();

      const positionAttribute =
        source.getAttribute(
          "position"
        );

      const normalAttribute =
        source.getAttribute(
          "normal"
        );

      for (
        let i = 0;
        i < positionAttribute.array.length;
        i++
      ) {
        positions.push(
          positionAttribute.array[i]
        );
      }

      if (normalAttribute) {
        for (
          let i = 0;
          i < normalAttribute.array.length;
          i++
        ) {
          normals.push(
            normalAttribute.array[i]
          );
        }
      }

      source.dispose();
    }
  );

  const merged =
    new THREE.BufferGeometry();

  merged.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(
      positions,
      3
    )
  );

  if (
    normals.length ===
    positions.length
  ) {
    merged.setAttribute(
      "normal",
      new THREE.Float32BufferAttribute(
        normals,
        3
      )
    );
  } else {
    merged.computeVertexNormals();
  }

  merged.computeBoundingSphere();

  return merged;
}


/* =========================================================
   KERBS
========================================================= */

function TrackKerbs({
  curve,
}) {
  /*
    PERFORMANCE PASS 2

    Previously every red/white kerb section was its own mesh:
      220 sections × 2 circuit sides = 440 meshes.

    The geometry is now generated exactly as before, but all white
    sections are merged into one static geometry and all red sections
    into another. Visually the kerbs remain alternating red/white,
    while the renderer only has two kerb draw calls.
  */
  const kerbGeometries =
    useMemo(
      () => {
        const white = [];
        const red = [];

        const sections =
          220;

        for (
          let i = 0;
          i < sections;
          i++
        ) {
          const startT =
            i /
            sections;

          const endT =
            (
              i + 1
            ) /
            sections;

          const destination =
            i % 2 === 0
              ? white
              : red;

          destination.push(
            createKerbSectionGeometry(
              curve,
              startT,
              endT,
              KERB_OFFSET,
              KERB_WIDTH,
              0.1
            )
          );

          destination.push(
            createKerbSectionGeometry(
              curve,
              startT,
              endT,
              -KERB_OFFSET,
              KERB_WIDTH,
              0.1
            )
          );
        }

        const whiteMerged =
          mergeStaticGeometries(
            white
          );

        const redMerged =
          mergeStaticGeometries(
            red
          );

        white.forEach(
          (geometry) =>
            geometry.dispose()
        );

        red.forEach(
          (geometry) =>
            geometry.dispose()
        );

        return {
          white:
            whiteMerged,

          red:
            redMerged,
        };
      },
      [curve]
    );

  useEffect(
    () => {
      return () => {
        kerbGeometries.white.dispose();
        kerbGeometries.red.dispose();
      };
    },
    [kerbGeometries]
  );

  return (
    <>
      <mesh
        geometry={
          kerbGeometries.white
        }
      >
        <meshStandardMaterial
          color="#ffffff"
          roughness={0.7}
          side={
            THREE.DoubleSide
          }
        />
      </mesh>

      <mesh
        geometry={
          kerbGeometries.red
        }
      >
        <meshStandardMaterial
          color="#ef3038"
          roughness={0.7}
          side={
            THREE.DoubleSide
          }
        />
      </mesh>
    </>
  );
}



/* =========================================================
   ARMCO RAIL GEOMETRY
========================================================= */

function getContinuousArmcoOffset(
  curve,
  t,
  requestedOffset,
  currentLevel
) {
  /*
    Level 3: keep the Armco at ONE constant offset from the exact
    circuit centre-line. This keeps it parallel and adjacent to the
    red/white kerbing instead of wandering inward/outward.
  */
  if (currentLevel !== 3) {
    return requestedOffset;
  }

  const direction =
    requestedOffset >= 0
      ? 1
      : -1;

  /*
    Level 3 needs a little more clearance than the earlier 10.15 value.
    The road reaches 10 units from the centre line and the kerb continues
    beyond that, so 11.18 puts the centre of the rail just outside the
    outer edge of the red/white kerb.
  */
  const LEVEL_THREE_ARMCO_OFFSET =
    ROAD_HALF_WIDTH +
    KERB_WIDTH +
    0.28;

  return (
    direction *
    LEVEL_THREE_ARMCO_OFFSET
  );
}


/*
  LEVEL 3 ARMCO PINCH PROTECTION

  The beach circuit contains a deliberately tight infield section. A normal
  constant-offset curve can fold across a neighbouring part of the track at
  that pinch point. Rather than drawing Armco through the racing surface, we
  detect barrier samples that are too close to a NON-LOCAL section of the
  centre-line. Those few samples are omitted, producing a clean safety opening
  instead of a barrier crossing the road/cars. The circuit itself is unchanged.
*/
function isLevelThreeArmcoSampleSafe(
  curve,
  t,
  barrierPoint,
  segments
) {
  const exclusion =
    Math.max(
      18,
      Math.round(segments * 0.035)
    );

  const sampleCount = 260;
  const minimumClearance =
    ROAD_HALF_WIDTH +
    KERB_WIDTH +
    0.18;

  const currentIndex =
    Math.round(t * sampleCount) % sampleCount;

  for (let j = 0; j < sampleCount; j++) {
    const directDifference =
      Math.abs(j - currentIndex);

    const circularDifference =
      Math.min(
        directDifference,
        sampleCount - directDifference
      );

    /* Nearby samples belong to the same bit of circuit and are expected. */
    if (circularDifference < exclusion) {
      continue;
    }

    const otherPoint =
      curve.getPointAt(j / sampleCount);

    const dx = barrierPoint.x - otherPoint.x;
    const dz = barrierPoint.z - otherPoint.z;

    if (
      dx * dx + dz * dz <
      minimumClearance * minimumClearance
    ) {
      return false;
    }
  }

  return true;
}


function createArmcoRailGeometry(
  curve,
  offset,
  thickness,
  bottomY,
  topY,
  segments = 450,
  currentLevel = 1
) {
  const vertices = [];
  const indices = [];
  const safeSamples = [];

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const point = curve.getPointAt(t);
    const tangent =
      curve.getTangentAt(t).normalize();

    const side =
      new THREE.Vector3(
        -tangent.z,
        0,
        tangent.x
      ).normalize();

    const effectiveOffset =
      getContinuousArmcoOffset(
        curve,
        t,
        offset,
        currentLevel
      );

    const centre =
      point.clone().add(
        side.clone().multiplyScalar(
          effectiveOffset
        )
      );

    // The widened Level 3 layout no longer has neighbouring track sections
    // close enough to clash, so every Armco sample remains continuous.
    safeSamples.push(true);

    const direction =
      effectiveOffset >= 0 ? 1 : -1;

    const outer =
      centre.clone().add(
        side.clone().multiplyScalar(
          direction * thickness / 2
        )
      );

    const inner =
      centre.clone().add(
        side.clone().multiplyScalar(
          -direction * thickness / 2
        )
      );

    vertices.push(
      outer.x, bottomY, outer.z,
      inner.x, bottomY, inner.z,
      outer.x, topY, outer.z,
      inner.x, topY, inner.z
    );
  }

  for (let i = 0; i < segments; i++) {
    /*
      Never bridge across a Level 3 pinch-protection opening. This is the
      important part: the old mesh connected every consecutive sample, which
      is what allowed a rail to cut diagonally across the circuit.
    */
    if (!safeSamples[i] || !safeSamples[i + 1]) {
      continue;
    }

    const base = i * 4;
    const next = (i + 1) * 4;

    indices.push(
      base, next, base + 2,
      next, next + 2, base + 2,
      base + 1, base + 3, next + 1,
      next + 1, base + 3, next + 3,
      base + 2, next + 2, base + 3,
      next + 2, next + 3, base + 3,
      base, base + 1, next,
      next, base + 1, next + 1
    );
  }

  const geometry =
    new THREE.BufferGeometry();

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(
      vertices,
      3
    )
  );

  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();

  return geometry;
}


/* =========================================================
   ARMCO POSTS
========================================================= */

function ArmcoPosts({
  curve,
  offset,
  currentLevel,
}) {
  const meshRef =
    useRef();

  const postCount =
    120;

  const postTransforms =
    useMemo(
      () => {
        const result = [];

        for (
          let i = 0;
          i < postCount;
          i++
        ) {
          const t =
            i /
            postCount;

          const point =
            curve.getPointAt(
              t
            );

          const tangent =
            curve
              .getTangentAt(
                t
              )
              .normalize();

          const side =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();

          const effectiveOffset =
            getContinuousArmcoOffset(
              curve,
              t,
              offset,
              currentLevel
            );


          const postPosition =
            point
              .clone()
              .add(
                side
                  .clone()
                  .multiplyScalar(
                    effectiveOffset +
                    (
                      effectiveOffset >= 0
                        ? 0.16
                        : -0.16
                    )
                  )
              );

          const angle =
            Math.atan2(
              tangent.x,
              tangent.z
            );

          // The widened Level 3 circuit has enough clearance for a fully
          // continuous run of posts on both sides.
          const postIsSafe = true;

          if (postIsSafe) {
            result.push({
              position:
                postPosition,

              angle,
            });
          }
        }

        return result;
      },
      [
        curve,
        offset,
        currentLevel,
      ]
    );

  useEffect(
    () => {
      const mesh =
        meshRef.current;

      if (!mesh) {
        return;
      }

      const dummy =
        new THREE.Object3D();

      postTransforms.forEach(
        (
          post,
          index
        ) => {
          dummy.position.set(
            post.position.x,
            0.62,
            post.position.z
          );

          dummy.rotation.set(
            0,
            post.angle,
            0
          );

          dummy.scale.set(
            1,
            1,
            1
          );

          dummy.updateMatrix();

          mesh.setMatrixAt(
            index,
            dummy.matrix
          );
        }
      );

      mesh.instanceMatrix
        .setUsage(
          THREE.StaticDrawUsage
        );

      mesh.instanceMatrix
        .needsUpdate =
        true;

      mesh.computeBoundingSphere();
    },
    [postTransforms]
  );

  return (
    <instancedMesh
      ref={meshRef}
      args={[
        null,
        null,
        postTransforms.length,
      ]}
    >
      <boxGeometry
        args={[
          0.16,
          1.25,
          0.18,
        ]}
      />

      <meshStandardMaterial
        color="#666a6d"
        metalness={0.75}
        roughness={0.38}
      />
    </instancedMesh>
  );
}



/* =========================================================
   ARMCO BARRIERS
========================================================= */

function TrackBarriers({
  curve,
}) {
  const currentLevel =
    useContext(
      RaceTrackLevelContext
    );

  const rails =
    useMemo(
      () => {
        const result = [];


        const railLevels = [
          {
            bottom: 0.16,
            top: 0.5,
          },

          {
            bottom: 0.47,
            top: 0.81,
          },

          {
            bottom: 0.78,
            top: 1.12,
          },
        ];


        railLevels.forEach(
          (level) => {
            result.push(
              createArmcoRailGeometry(
                curve,
                BARRIER_OFFSET,
                0.18,
                level.bottom,
                level.top,
                currentLevel === 3
                  ? 1000
                  : 700,
                currentLevel
              )
            );


            result.push(
              createArmcoRailGeometry(
                curve,
                -BARRIER_OFFSET,
                0.18,
                level.bottom,
                level.top,
                currentLevel === 3
                  ? 1000
                  : 700,
                currentLevel
              )
            );
          }
        );


        return result;
      },
      [
        curve,
        currentLevel,
      ]
    );


  return (
    <>

      {rails.map(
        (
          geometry,
          index
        ) => (
          <mesh
            key={
              `rail-${index}`
            }

            geometry={
              geometry
            }

          >
            <meshStandardMaterial
              color="#aeb3b7"

              metalness={
                0.82
              }

              roughness={
                0.32
              }

              side={
                THREE.DoubleSide
              }
            />
          </mesh>
        )
      )}


      <ArmcoPosts
        curve={
          curve
        }

        offset={
          BARRIER_OFFSET
        }

        currentLevel={
          currentLevel
        }
      />


      <ArmcoPosts
        curve={
          curve
        }

        offset={
          -BARRIER_OFFSET
        }

        currentLevel={
          currentLevel
        }
      />

    </>
  );
}


/* =========================================================
   FINISH LINE
========================================================= */

function FinishLine() {
  const curve =
    useRaceTrackCurve();


  const point =
    curve.getPointAt(
      0
    );


  const tangent =
    curve
      .getTangentAt(
        0
      )
      .normalize();


  const side =
    new THREE.Vector3(
      -tangent.z,
      0,
      tangent.x
    ).normalize();


  const angle =
    Math.atan2(
      tangent.x,
      tangent.z
    );


  /*
    20 squares across the road.

    ROAD_WIDTH = 20, so each square
    is exactly 1 unit wide.
  */

  const squareSize =
    1;

  const squareCount =
    Math.round(
      ROAD_WIDTH /
      squareSize
    );


  /*
    Two rows makes it look like a
    proper chequered start/finish line.
  */

  const rowCount =
    2;


  return (
    <>
      {Array
        .from({
          length:
            rowCount,
        })
        .map(
          (
            _,
            row
          ) =>
            Array
              .from({
                length:
                  squareCount,
              })
              .map(
                (
                  _,
                  index
                ) => {
                  /*
                    Centre each square
                    perfectly across road.
                  */

                  const sideOffset =
                    -ROAD_HALF_WIDTH +
                    squareSize / 2 +
                    index *
                    squareSize;


                  /*
                    Move second row slightly
                    along track direction.
                  */

                  const forwardOffset =
                    (
                      row -
                      (
                        rowCount -
                        1
                      ) /
                        2
                    ) *
                    squareSize;


                  const position =
                    point
                      .clone()
                      .add(
                        side
                          .clone()
                          .multiplyScalar(
                            sideOffset
                          )
                      )
                      .add(
                        tangent
                          .clone()
                          .multiplyScalar(
                            forwardOffset
                          )
                      );


                  /*
                    Alternate colours in both
                    directions to create proper
                    checkerboard pattern.
                  */

                  const isWhite =
                    (
                      index +
                      row
                    ) %
                      2 ===
                    0;


                  return (
                    <mesh
                      key={
                        `${row}-${index}`
                      }

                      position={[
                        position.x,
                        0.072,
                        position.z,
                      ]}

                      rotation={[
                        -Math.PI / 2,
                        0,
                        -angle,
                      ]}
                    >
                      <planeGeometry
                        args={[
                          squareSize,
                          squareSize,
                        ]}
                      />

                      <meshStandardMaterial
                        color={
                          isWhite
                            ? "#ffffff"
                            : "#111111"
                        }

                        roughness={
                          0.82
                        }

                        metalness={
                          0
                        }

                        side={
                          THREE.DoubleSide
                        }
                      />
                    </mesh>
                  );
                }
              )
        )}
    </>
  );
}

/* =========================================================
   START / FINISH TRAFFIC LIGHTS
========================================================= */

function TrafficLightUnit({
  position,
  rotation,
  activeLight,
}) {
  const redOn =
    activeLight === "red";

  const amberOn =
    activeLight === "amber";

  const greenOn =
    activeLight === "green";


  return (
    <group
  position={
    position
  }

  rotation={[
    0,
    rotation,
    0,
  ]}

  scale={[
    1.1,
    1.1,
    1.1,
  ]}
>

      {/* POLE */}

      <mesh
        position={[
          0,
          1.65,
          0,
        ]}
        castShadow
      >
        <cylinderGeometry
          args={[
            0.12,
            0.15,
            3.3,
            16,
          ]}
        />

        <meshStandardMaterial
          color="#222222"
          metalness={0.75}
          roughness={0.3}
        />
      </mesh>


      {/* BASE */}

      <mesh
        position={[
          0,
          0.1,
          0,
        ]}
        castShadow
      >
        <cylinderGeometry
          args={[
            0.42,
            0.5,
            0.2,
            20,
          ]}
        />

        <meshStandardMaterial
          color="#171717"
          metalness={0.65}
          roughness={0.4}
        />
      </mesh>


      {/* BLACK LIGHT HOUSING */}

      <mesh
        position={[
          0,
          4.05,
          0,
        ]}
        castShadow
      >
        <boxGeometry
          args={[
            1.15,
            2.75,
            0.55,
          ]}
        />

        <meshStandardMaterial
          color="#101010"
          metalness={0.4}
          roughness={0.35}
        />
      </mesh>


      {/* RED LIGHT */}

      <mesh
        position={[
          0,
          4.88,
          -0.32,
        ]}
      >
        <sphereGeometry
          args={[
            0.38,
            32,
            32,
          ]}
        />

        <meshStandardMaterial
          color={
            redOn
              ? "#ff2020"
              : "#3a0808"
          }

          emissive={
            redOn
              ? "#ff0000"
              : "#000000"
          }

          emissiveIntensity={
            redOn
              ? 4
              : 0
          }

          roughness={0.25}
        />
      </mesh>


      {/* AMBER LIGHT */}

      <mesh
        position={[
          0,
          4.05,
          -0.32,
        ]}
      >
        <sphereGeometry
          args={[
            0.38,
            32,
            32,
          ]}
        />

        <meshStandardMaterial
          color={
            amberOn
              ? "#ffae00"
              : "#412900"
          }

          emissive={
            amberOn
              ? "#ff8c00"
              : "#000000"
          }

          emissiveIntensity={
            amberOn
              ? 4
              : 0
          }

          roughness={0.25}
        />
      </mesh>


      {/* GREEN LIGHT */}

      <mesh
        position={[
          0,
          3.22,
          -0.32,
        ]}
      >
        <sphereGeometry
          args={[
            0.38,
            32,
            32,
          ]}
        />

        <meshStandardMaterial
          color={
            greenOn
              ? "#20f060"
              : "#073b18"
          }

          emissive={
            greenOn
              ? "#00ff44"
              : "#000000"
          }

          emissiveIntensity={
            greenOn
              ? 4
              : 0
          }

          roughness={0.25}
        />
      </mesh>

    </group>
  );
}


/* =========================================================
   START LIGHT PAIR
========================================================= */

function StartTrafficLights({
  raceStatus,
  countdown,
  showGo,
}) {
  const curve =
    useRaceTrackCurve();


  const point =
    curve.getPointAt(
      0
    );


  const tangent =
    curve
      .getTangentAt(
        0
      )
      .normalize();


  const side =
    new THREE.Vector3(
      -tangent.z,
      0,
      tangent.x
    ).normalize();


  const angle =
    Math.atan2(
      tangent.x,
      tangent.z
    );


  /* =====================================================
     CHOOSE ACTIVE LIGHT
  ===================================================== */

  let activeLight =
    "red";


  /*
    At 2 and 1:
    change from red to amber.
  */

  if (
    raceStatus ===
      "countdown" &&
    countdown <= 2
  ) {
    activeLight =
      "amber";
  }


  /*
    GO and racing:
    green.
  */

  if (
    showGo ||
    raceStatus ===
      "racing"
  ) {
    activeLight =
      "green";
  }


  /* =====================================================
     POSITION LIGHTS EITHER SIDE OF TRACK
  ===================================================== */

  const lightOffset =
  ROAD_HALF_WIDTH +
  KERB_WIDTH +
  BARRIER_THICKNESS +
  -1.2;


  const leftPosition =
    point
      .clone()
      .add(
        side
          .clone()
          .multiplyScalar(
            lightOffset
          )
      );


  const rightPosition =
    point
      .clone()
      .add(
        side
          .clone()
          .multiplyScalar(
            -lightOffset
          )
      );


  return (
    <>
      <TrafficLightUnit
        position={[
          leftPosition.x,
          0,
          leftPosition.z,
        ]}

        rotation={
          angle
        }

        activeLight={
          activeLight
        }
      />


      <TrafficLightUnit
        position={[
          rightPosition.x,
          0,
          rightPosition.z,
        ]}

        rotation={
          angle
        }

        activeLight={
          activeLight
        }
      />
    </>
  );
}

/* =========================================================
   RACING CAR
========================================================= */

function RacingCar({
  id,
  curve,
  color,
  initialProgress,
  speed,
  laneOffset = 0,
  isPlayer = false,
  onProgress,
  finalVictory = false,
}) {
  const carRef =
    useRef(
      null
    );


  const progressRef =
    useRef(
      initialProgress
    );


  const lapRef =
    useRef(
      initialProgress >
        0.9
        ? -1
        : 0
    );


  const reportTimer =
    useRef(
      0
    );


  const cameraDirectionRef =
    useRef(
      null
    );


  const cameraLookAtRef =
    useRef(
      null
    );


  const cameraRotationMatrixRef =
    useRef(
      new THREE.Matrix4()
    );


  const cameraTargetQuaternionRef =
    useRef(
      new THREE.Quaternion()
    );

  /*
    PERFORMANCE PASS 3:
    Reuse all temporary vectors. The old movement/camera code created
    many new Vector3 objects every frame, for every car. Those short-lived
    objects caused frequent garbage collection, which can show up as
    little hitches even when the average FPS looks acceptable.
  */
  const framePointRef =
    useRef(new THREE.Vector3());

  const frameTangentRef =
    useRef(new THREE.Vector3());

  const frameSideRef =
    useRef(new THREE.Vector3());

  const frameFinalPositionRef =
    useRef(new THREE.Vector3());

  const framePreviewPointRef =
    useRef(new THREE.Vector3());

  const frameDesiredDirectionRef =
    useRef(new THREE.Vector3());

  const frameCameraTargetRef =
    useRef(new THREE.Vector3());

  const frameLookAheadRef =
    useRef(new THREE.Vector3());


  useFrame(
    (
      state,
      delta
    ) => {
      if (
        !carRef.current
      ) {
        return;
      }


      /* MOVE */

      progressRef.current +=
        speed *
        delta;


      if (
        progressRef.current >=
        1
      ) {
        progressRef.current -=
          1;

        lapRef.current +=
          1;
      }


      const t =
        progressRef.current;


      const point =
        curve.getPointAt(
          t,
          framePointRef.current
        );


      const tangent =
        curve
          .getTangentAt(
            t,
            frameTangentRef.current
          )
          .normalize();


      const side =
        frameSideRef.current
          .set(
            -tangent.z,
            0,
            tangent.x
          )
          .normalize();


      const finalPosition =
        frameFinalPositionRef.current
          .copy(point)
          .addScaledVector(
            side,
            laneOffset
          );


      carRef.current
        .position
        .set(
          finalPosition.x,
          0,
          finalPosition.z
        );


      /* ROTATION */

      const angle =
        Math.atan2(
          tangent.x,
          tangent.z
        );


      carRef.current
        .rotation
        .y =
        angle;


      /* REPORT RACE POSITION */

      reportTimer.current +=
        delta;


      if (
        reportTimer.current >=
        0.1
      ) {
        reportTimer.current =
          0;


        onProgress(
          id,
          {
            lap:
              lapRef.current,

            progress:
              progressRef.current,

            totalProgress:
              lapRef.current +
              progressRef.current,
          }
        );
      }


      /* PLAYER CAMERA */

      if (
        isPlayer
      ) {
        /*
          Preview the circuit ahead so the camera turns into a bend
          progressively instead of reacting at the last moment.
        */

        const previewPoint =
          curve.getPointAt(
            (
              t + 0.025
            ) % 1,
            framePreviewPointRef.current
          );


        const desiredDirection =
          frameDesiredDirectionRef.current
            .copy(previewPoint)
            .sub(point)
            .normalize();


        if (
          !cameraDirectionRef.current
        ) {
          cameraDirectionRef.current =
            desiredDirection.clone();
        }


        cameraDirectionRef.current
          .lerp(
            desiredDirection,
            1 -
              Math.exp(
                -4 * delta
              )
          )
          .normalize();


        const cameraDirection =
          cameraDirectionRef.current;


        /*
          FINAL LEVEL VICTORY CAMERA

          During a Level 3 first-place celebration the camera eases
          backwards and upwards, revealing more of the circuit,
          promenade, beach, confetti and fireworks.
        */
        const victoryDistance =
          finalVictory
            ? -25
            : -13;

        const victoryHeight =
          finalVictory
            ? 10.5
            : 3.7;

        const cameraTarget =
          frameCameraTargetRef.current
            .copy(finalPosition)
            .addScaledVector(
              cameraDirection,
              victoryDistance
            );


        cameraTarget.y +=
          victoryHeight;


        state.camera
          .position
          .lerp(
            cameraTarget,
            1 -
              Math.exp(
                -5 * delta
              )
          );


        const lookAhead =
          frameLookAheadRef.current
            .copy(finalPosition)
            .addScaledVector(
              cameraDirection,
              finalVictory
                ? 5
                : 10
            );


        lookAhead.y +=
          0.8;


        if (
          !cameraLookAtRef.current
        ) {
          cameraLookAtRef.current =
            lookAhead.clone();
        }


        cameraLookAtRef.current
          .lerp(
            lookAhead,
            1 -
              Math.exp(
                -5 * delta
              )
          );


        cameraRotationMatrixRef.current
          .lookAt(
            state.camera.position,
            cameraLookAtRef.current,
            state.camera.up
          );


        cameraTargetQuaternionRef.current
          .setFromRotationMatrix(
            cameraRotationMatrixRef.current
          );


        state.camera.quaternion
          .slerp(
            cameraTargetQuaternionRef.current,
            1 -
              Math.exp(
                -5 * delta
              )
        );
      }
    }
  );


  return (
    <group
      ref={
        carRef
      }
    >
      <Car
        color={
          color
        }
      />
    </group>
  );
}


/* =========================================================
   RACE
========================================================= */

function Race({
  onProgress,
  playerSpeed,
  raceStatus,
  currentLevel,
  finalVictory = false,
}) {
  const curve =
    useRaceTrackCurve();


  return (
    <>

      {/* BLUE */}

      <RacingCar
        id={
          CAR_IDS.BLUE
        }

        curve={
          curve
        }

        color="#008cff"

        initialProgress={
          0.990
        }

        speed={
          raceStatus ===
          "racing"
            ? (
                currentLevel === 3
                  ? 0.0188
                  : currentLevel === 2
                  ? 0.0208
                  : 0.0358
              )
            : 0
        }

        laneOffset={
          LANE_OFFSETS
            .FAR_LEFT
        }

        onProgress={
          onProgress
        }
      />


      {/* PLAYER */}

      <RacingCar
        id={
          CAR_IDS.PLAYER
        }

        curve={
          curve
        }

        color="#ffd900"

        initialProgress={
          0.990
        }

        speed={
          raceStatus ===
          "racing"
            ? playerSpeed
            : 0
        }

        laneOffset={
          LANE_OFFSETS.LEFT
        }

        isPlayer

        finalVictory={
          finalVictory
        }

        onProgress={
          onProgress
        }
      />


      {/* GREEN */}

      <RacingCar
        id={
          CAR_IDS.GREEN
        }

        curve={
          curve
        }

        color="#16c83b"

        initialProgress={
          0.990
        }

        speed={
          raceStatus ===
          "racing"
            ? (
                currentLevel === 3
                  ? 0.0196
                  : currentLevel === 2
                  ? 0.0216
                  : 0.0366
              )
            : 0
        }

        laneOffset={
          LANE_OFFSETS.RIGHT
        }

        onProgress={
          onProgress
        }
      />


      {/* RED */}

      <RacingCar
        id={
          CAR_IDS.RED
        }

        curve={
          curve
        }

        color="#f02020"

        initialProgress={
          0.990
        }

        speed={
          raceStatus ===
          "racing"
            ? (
                currentLevel === 3
                  ? 0.0204
                  : currentLevel === 2
                  ? 0.0224
                  : 0.0374
              )
            : 0
        }

        laneOffset={
          LANE_OFFSETS
            .FAR_RIGHT
        }

        onProgress={
          onProgress
        }
      />

    </>
  );
}



/* =========================================================
   TRACKSIDE TREES
========================================================= */

function TracksideTrees() {
  const curve =
    useRaceTrackCurve();


  const trees =
    useMemo(
      () => {
        const result = [];

        const treePositions =
          20;


        const minimumDistance =
          BARRIER_OFFSET +
          4.5;


        for (
          let i = 0;
          i < treePositions;
          i++
        ) {
          const variation =
            Math.sin(
              i *
              12.9898
            ) *
            0.004;


          let t =
            i /
              treePositions +
            variation;


          t =
            (
              (
                t %
                1
              ) +
              1
            ) %
            1;


          const point =
            curve.getPointAt(
              t
            );


          const tangent =
            curve
              .getTangentAt(
                t
              )
              .normalize();


          const side =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();


          const distanceVariation1 =
            (
              Math.sin(
                i *
                4.37
              ) +
              1
            ) *
            2.4;


          const distanceVariation2 =
            (
              Math.cos(
                i *
                5.73
              ) +
              1
            ) *
            2.8;


          /* LEFT TREE */

          const leftPosition =
            point
              .clone()
              .add(
                side
                  .clone()
                  .multiplyScalar(
                    minimumDistance +
                    distanceVariation1
                  )
              );


          leftPosition.y =
            0;


          const leftScale =
            0.82 +
            (
              (
                Math.sin(
                  i *
                  8.19
                ) +
                1
              ) /
              2
            ) *
            0.38;


          const leftRotation =
            (
              i *
              1.618
            ) %
            (
              Math.PI *
              2
            );


          result.push({
            id:
              `left-${i}`,

            position: [
              leftPosition.x,
              leftPosition.y,
              leftPosition.z,
            ],

            scale:
              leftScale,

            rotation:
              leftRotation,
          });


          /* RIGHT TREE */

          const rightPosition =
            point
              .clone()
              .add(
                side
                  .clone()
                  .multiplyScalar(
                    -(
                      minimumDistance +
                      distanceVariation2
                    )
                  )
              );


          rightPosition.y =
            0;


          const rightScale =
            0.78 +
            (
              (
                Math.cos(
                  i *
                  7.41
                ) +
                1
              ) /
              2
            ) *
            0.42;


          const rightRotation =
            (
              i *
              2.173
            ) %
            (
              Math.PI *
              2
            );


          result.push({
            id:
              `right-${i}`,

            position: [
              rightPosition.x,
              rightPosition.y,
              rightPosition.z,
            ],

            scale:
              rightScale,

            rotation:
              rightRotation,
          });
        }


        return result;
      },
      [
        curve,
      ]
    );


  return (
    <group>
      {trees.map(
        (tree) => (
          <RealisticTree
            key={
              tree.id
            }

            position={
              tree.position
            }

            rotation={
              tree.rotation
            }

            scale={
              tree.scale
            }
          />
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 2 - URBAN PAVEMENT TREES

   Reuses the Level 1 tree model at a smaller urban scale.
   Trees sit on the outer part of the pavement, with openings
   left at every side street and pedestrian crossing.
========================================================= */

function CityTrees() {
  const curve =
    useRaceTrackCurve();


  const trees =
    useMemo(
      () => {
        const result = [];

        const treePositions =
          16;

        const pavementTreeOffset =
          BARRIER_OFFSET +
          BARRIER_THICKNESS / 2 +
          2.7;


        for (
          let i = 0;
          i < treePositions;
          i++
        ) {
          const t =
            (
              i + 0.5
            ) /
            treePositions;


          if (
            isNearCitySideStreet(
              t,
              0.018
            )
          ) {
            continue;
          }


          const point =
            curve.getPointAt(
              t
            );


          const tangent =
            curve
              .getTangentAt(
                t
              )
              .normalize();


          const side =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();


          [
            1,
            -1,
          ].forEach(
            (direction) => {
              const position =
                point
                  .clone()
                  .add(
                    side
                      .clone()
                      .multiplyScalar(
                        pavementTreeOffset *
                        direction
                      )
                  );


              result.push({
                id:
                  `${direction}-${i}`,

                position: [
                  position.x,
                  0,
                  position.z,
                ],

                rotation:
                  (
                    i * 1.91 +
                    (
                      direction === 1
                        ? 0
                        : 0.7
                    )
                  ) %
                  (
                    Math.PI * 2
                  ),

                scale:
                  0.04 +
                  (
                    (
                      Math.sin(
                        i * 4.27
                      ) + 1
                    ) /
                    2
                  ) * 0.08,
              });
            }
          );
        }


        return result;
      },
      [curve]
    );


  return (
    <group>
      {trees.map(
        (tree) => (
          <RealisticTree
            key={
              tree.id
            }
            position={
              tree.position
            }
            rotation={
              tree.rotation
            }
            scale={
              tree.scale
            }
          />
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 2 - CITY GROUND
========================================================= */



function CityGround() {
  const pavementTexture =
    useTexture("/textures/pavement.png");

  const texturedPavement =
    useMemo(
      () => {
        const texture =
          pavementTexture.clone();

        texture.wrapS =
          THREE.RepeatWrapping;

        texture.wrapT =
          THREE.RepeatWrapping;

        /*
          This is the large city surface underneath the buildings,
          so repeat the paving slabs in both directions rather than
          stretching one image across the whole city.
        */
        texture.repeat.set(
          70,
          70
        );

        texture.colorSpace =
          THREE.SRGBColorSpace;

        texture.anisotropy =
          8;

        texture.needsUpdate =
          true;

        return texture;
      },
      [
        pavementTexture,
      ]
    );

  return (
    <mesh
      rotation={[
        -Math.PI / 2,
        0,
        0,
      ]}
      position={[
        0,
        -0.018,
        0,
      ]}
      receiveShadow
    >
      <planeGeometry
        args={[
          260,
          260,
        ]}
      />

      <meshStandardMaterial
        map={
          texturedPavement
        }
        color="#b8bdc0"
        roughness={0.72}
        metalness={0.02}
        envMapIntensity={0.7}
      />
    </mesh>
  );
}


/* =========================================================
   LEVEL 2 - CITY PAVEMENTS
========================================================= */


/* =========================================================
   LEVEL 2 - TEXTURED CITY SURFACES

   Required files:
   public/textures/pavement.png
   public/textures/city-road-asphalt.png
========================================================= */

function useCitySurfaceTextures() {
  const pavementSource =
    useTexture("/textures/pavement.png");

  const cityRoadSource =
    useTexture("/textures/city-road-asphalt.png");

  return useMemo(() => {
    const pavement =
      pavementSource.clone();

    const cityRoad =
      cityRoadSource.clone();

    [pavement, cityRoad].forEach(
      (texture) => {
        texture.wrapS =
          THREE.RepeatWrapping;
        texture.wrapT =
          THREE.RepeatWrapping;
        texture.colorSpace =
          THREE.SRGBColorSpace;
        texture.anisotropy = 8;
        texture.needsUpdate = true;
      }
    );

    /*
      Repeat rather than stretch the images.
      These are the two values to adjust later if the
      paving slabs / asphalt aggregate look too large.
    */
    pavement.repeat.set(22, 3.2);
    cityRoad.repeat.set(30, 5);

    return {
      pavement,
      cityRoad,
    };
  }, [
    pavementSource,
    cityRoadSource,
  ]);
}

useTexture.preload(
  "/textures/pavement.png"
);

useTexture.preload(
  "/textures/city-road-asphalt.png"
);


function CityPavements() {
  const curve =
    useRaceTrackCurve();

  const {
    pavement: pavementTexture,
    cityRoad: cityRoadTexture,
  } = useCitySurfaceTextures();

  /*
    PASS 3 - PROPER CITY PAVEMENTS / FOOTPATHS

    Layout from the race circuit outwards:

      RACE CIRCUIT
      -> continuous Armco
      -> wide inner footpath
      -> normal two-way city road
      -> building-side pavement
      -> buildings

    The pavements are now slightly raised and use warmer concrete
    tones so they read as real pedestrian areas rather than unused
    grey ground.

    IMPORTANT:
    - The Armco remains completely continuous.
    - The city road remains separate from the racing circuit.
    - The building-side kerb is broken at side-street junctions.
    - No kerb runs across the mouths of the side streets.
  */

  const INNER_PAVEMENT_WIDTH = 3.4;
  const CITY_ROAD_WIDTH = 7.2;
  const OUTER_PAVEMENT_WIDTH = 2.9;

  const PAVEMENT_Y = 0.085;
  const ROAD_Y = 0.045;

  const KERB_WIDTH_CITY = 0.24;
  const KERB_TOP_Y = 0.125;

  const armcoOuterEdge =
    BARRIER_OFFSET +
    BARRIER_THICKNESS / 2;

  const innerPavementOffset =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH / 2;

  const cityRoadOffset =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH +
    CITY_ROAD_WIDTH / 2;

  const cityRoadInnerEdge =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH;

  const cityRoadOuterEdge =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH +
    CITY_ROAD_WIDTH;

  const outerPavementOffset =
    cityRoadOuterEdge +
    OUTER_PAVEMENT_WIDTH / 2;

  const geometries =
    useMemo(
      () => ({
        innerPavements: [
          createTrackStripGeometry(
            curve,
            innerPavementOffset,
            INNER_PAVEMENT_WIDTH,
            PAVEMENT_Y,
            720
          ),
          createTrackStripGeometry(
            curve,
            -innerPavementOffset,
            INNER_PAVEMENT_WIDTH,
            PAVEMENT_Y,
            720
          ),
        ],

        cityRoads: [
          createTrackStripGeometry(
            curve,
            cityRoadOffset,
            CITY_ROAD_WIDTH,
            ROAD_Y,
            720
          ),
          createTrackStripGeometry(
            curve,
            -cityRoadOffset,
            CITY_ROAD_WIDTH,
            ROAD_Y,
            720
          ),
        ],

        outerPavements: [
          createTrackStripGeometry(
            curve,
            outerPavementOffset,
            OUTER_PAVEMENT_WIDTH,
            PAVEMENT_Y,
            720
          ),
          createTrackStripGeometry(
            curve,
            -outerPavementOffset,
            OUTER_PAVEMENT_WIDTH,
            PAVEMENT_Y,
            720
          ),
        ],

        /*
          Continuous kerb on the Armco side of each city road.
          This edge never contains a junction because the side streets
          are entirely outside the perimeter road.
        */
        innerRoadKerbs: [
          createTrackStripGeometry(
            curve,
            cityRoadInnerEdge,
            KERB_WIDTH_CITY,
            KERB_TOP_Y,
            720
          ),
          createTrackStripGeometry(
            curve,
            -cityRoadInnerEdge,
            KERB_WIDTH_CITY,
            KERB_TOP_Y,
            720
          ),
        ],
      }),
      [
        curve,
        innerPavementOffset,
        cityRoadOffset,
        outerPavementOffset,
        cityRoadInnerEdge,
      ]
    );

  /*
    The helper above creates a full loop, so for the broken outer kerb
    we instead generate short visible kerb stones as meshes positioned
    around the curve. These also give the pavement a more convincing
    city-street edge.
  */
  const outerKerbStones =
    useMemo(
      () => {
        const result = [];
        const STONE_COUNT = 180;

        [-1, 1].forEach(
          (direction) => {
            for (
              let i = 0;
              i < STONE_COUNT;
              i++
            ) {
              const t =
                (i + 0.5) /
                STONE_COUNT;

              if (
                isNearCitySideStreet(
                  t,
                  0.020
                )
              ) {
                continue;
              }

              const point =
                curve.getPointAt(t);

              const tangent =
                curve
                  .getTangentAt(t)
                  .normalize();

              const side =
                new THREE.Vector3(
                  -tangent.z,
                  0,
                  tangent.x
                ).normalize();

              const position =
                point
                  .clone()
                  .add(
                    side
                      .clone()
                      .multiplyScalar(
                        direction *
                        cityRoadOuterEdge
                      )
                  );

              result.push({
                id:
                  `${direction}-${i}`,

                position: [
                  position.x,
                  KERB_TOP_Y,
                  position.z,
                ],

                rotation: [
                  0,
                  Math.atan2(
                    tangent.x,
                    tangent.z
                  ),
                  0,
                ],
              });
            }
          }
        );

        return result;
      },
      [
        curve,
        cityRoadOuterEdge,
      ]
    );

  /*
    Longitudinal paving joints divide each footpath into believable
    paving bands. Transverse slab joints are already added by
    CityRoadDressing, so these lines combine with them to create a
    rectangular paving-slab pattern.
  */
  const pavingBandGeometries =
    useMemo(
      () => {
        const result = [];

        [-1, 1].forEach(
          (direction) => {
            /*
              Inner footpath:
              two subtle longitudinal joints.
            */
            [
              -INNER_PAVEMENT_WIDTH / 6,
              INNER_PAVEMENT_WIDTH / 6,
            ].forEach(
              (localOffset) => {
                result.push(
                  createTrackStripGeometry(
                    curve,
                    direction *
                      innerPavementOffset +
                      localOffset,
                    0.035,
                    PAVEMENT_Y + 0.006,
                    720
                  )
                );
              }
            );

            /*
              Building-side pavement:
              one central longitudinal joint.
            */
            result.push(
              createTrackStripGeometry(
                curve,
                direction *
                  outerPavementOffset,
                0.035,
                PAVEMENT_Y + 0.006,
                720
              )
            );
          }
        );

        return result;
      },
      [
        curve,
        innerPavementOffset,
        outerPavementOffset,
      ]
    );

  return (
    <group>
      {/* =============================================
          INNER FOOTPATHS - BETWEEN ARMCO AND CITY ROAD
      ============================================= */}

      {geometries.innerPavements.map(
        (geometry, index) => (
          <mesh
            key={`inner-city-pavement-${index}`}
            geometry={geometry}
            receiveShadow
          >
            <meshStandardMaterial
              map={pavementTexture}
              color="#c4c8ca"
              roughness={0.68}
              metalness={0.02}
              envMapIntensity={0.7}
              side={THREE.DoubleSide}
            />
          </mesh>
        )
      )}


      {/* =============================================
          NORMAL OUTER-CIRCUIT CITY ROAD
      ============================================= */}

      {geometries.cityRoads.map(
        (geometry, index) => (
          <mesh
            key={`city-ring-road-${index}`}
            geometry={geometry}
            receiveShadow
          >
            <meshStandardMaterial
              map={cityRoadTexture}
              /*
                Darken only the OUTER city road.
                The texture is retained, but this grey tint makes
                the road noticeably darker than the pavements.
              */
              color="#4f5559"
              roughness={0.40}
              metalness={0.07}
              envMapIntensity={1.05}
              side={THREE.DoubleSide}
            />
          </mesh>
        )
      )}


      {/* =============================================
          BUILDING-SIDE PAVEMENTS
      ============================================= */}

      {geometries.outerPavements.map(
        (geometry, index) => (
          <mesh
            key={`outer-city-pavement-${index}`}
            geometry={geometry}
            receiveShadow
          >
            <meshStandardMaterial
              map={pavementTexture}
              color="#c4c8ca"
              roughness={0.70}
              metalness={0.02}
              envMapIntensity={0.7}
              side={THREE.DoubleSide}
            />
          </mesh>
        )
      )}


      {/* =============================================
          CONTINUOUS INNER CITY-ROAD KERBS
      ============================================= */}

      {geometries.innerRoadKerbs.map(
        (geometry, index) => (
          <mesh
            key={`inner-city-road-kerb-${index}`}
            geometry={geometry}
            receiveShadow
          >
            <meshStandardMaterial
              color="#d1cec3"
              roughness={0.92}
              metalness={0}
              side={THREE.DoubleSide}
            />
          </mesh>
        )
      )}


      {/* =============================================
          BROKEN BUILDING-SIDE KERB STONES

          Deliberately absent at side-street junctions.
      ============================================= */}

      {outerKerbStones.map(
        (stone) => (
          <mesh
            key={`outer-kerb-stone-${stone.id}`}
            position={stone.position}
            rotation={stone.rotation}
            castShadow
            receiveShadow
          >
            <boxGeometry
              args={[
                KERB_WIDTH_CITY,
                0.12,
                1.55,
              ]}
            />

            <meshStandardMaterial
              color="#d0cdc2"
              roughness={0.93}
              metalness={0}
            />
          </mesh>
        )
      )}


      {/* =============================================
          SUBTLE PAVING-SLAB BAND JOINTS
      ============================================= */}

      {pavingBandGeometries.map(
        (geometry, index) => (
          <mesh
            key={`paving-band-joint-${index}`}
            geometry={geometry}
            receiveShadow
          >
            <meshStandardMaterial
              color="#96958f"
              roughness={1}
              metalness={0}
              side={THREE.DoubleSide}
            />
          </mesh>
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 2 - CITY ROAD NETWORK

   IMPORTANT:
   - The Armco remains continuous.
   - The city road NEVER joins the racing circuit.
   - Radial side streets terminate at the outer city ring road.
   - Zebra crossings belong to the city streets, not the circuit.
========================================================= */

function CityStreetNetwork() {
  const curve =
    useRaceTrackCurve();

  const INNER_PAVEMENT_WIDTH = 3.4;
  const CITY_ROAD_WIDTH = 7.2;
  const OUTER_PAVEMENT_WIDTH = 2.6;
  const SIDE_STREET_WIDTH = 7.2;
  const SIDE_PAVEMENT_WIDTH = 1.8;
  const SIDE_STREET_LENGTH = 28;

  const armcoOuterEdge =
    BARRIER_OFFSET +
    BARRIER_THICKNESS / 2;

  const cityRoadCentreOffset =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH +
    CITY_ROAD_WIDTH / 2;

  const cityRoadOuterEdge =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH +
    CITY_ROAD_WIDTH;

  const buildingSidePavementOuterEdge =
    cityRoadOuterEdge +
    OUTER_PAVEMENT_WIDTH;

  /*
    Kerb only on the Armco-side edge of the city road.
    The building-side edge is deliberately flush so the zebra-crossing
    side streets flow directly into the outer city road with no kerb
    cutting across the T-junction.
  */
  const cityKerbs =
    useMemo(
      () => {
        const innerKerbOffset =
          armcoOuterEdge +
          INNER_PAVEMENT_WIDTH;

        return [
          createTrackStripGeometry(
            curve,
            innerKerbOffset,
            0.22,
            0.105,
            620
          ),
          createTrackStripGeometry(
            curve,
            -innerKerbOffset,
            0.22,
            0.105,
            620
          ),
        ];
      },
      [
        curve,
        armcoOuterEdge,
        cityRoadOuterEdge,
      ]
    );

  /*
    Proper broken centre markings for the perimeter city road.

    These are individual painted dashes rather than one continuous
    strip, so the outer road reads as an ordinary two-way city street
    rather than another racing lane.
  */
  const cityRoadCentreDashes =
    useMemo(
      () => {
        const result = [];
        const DASH_COUNT = 72;
        const DASH_LENGTH = 2.4;
        const DASH_WIDTH = 0.11;

        [-1, 1].forEach(
          (direction) => {
            for (
              let i = 0;
              i < DASH_COUNT;
              i++
            ) {
              /* Leave an equal painted/unpainted rhythm around the ring. */
              const t =
                (i + 0.5) /
                DASH_COUNT;

              /* Keep the centre line clear immediately beside T-junctions. */
              if (
                isNearCitySideStreet(
                  t,
                  0.0105
                )
              ) {
                continue;
              }

              const point =
                curve.getPointAt(t);

              const tangent =
                curve
                  .getTangentAt(t)
                  .normalize();

              const side =
                new THREE.Vector3(
                  -tangent.z,
                  0,
                  tangent.x
                ).normalize();

              const position =
                point
                  .clone()
                  .add(
                    side
                      .clone()
                      .multiplyScalar(
                        direction *
                        cityRoadCentreOffset
                      )
                  );

              result.push({
                id:
                  `${direction}-${i}`,
                position: [
                  position.x,
                  0.098,
                  position.z,
                ],
                rotation: [
                  0,
                  Math.atan2(
                    tangent.x,
                    tangent.z
                  ),
                  0,
                ],
                length:
                  DASH_LENGTH,
                width:
                  DASH_WIDTH,
              });
            }
          }
        );

        return result;
      },
      [
        curve,
        cityRoadCentreOffset,
      ]
    );

  const sideStreets =
    useMemo(
      () => {
        const result = [];

        CITY_SIDE_STREET_POINTS.forEach(
          (t, streetIndex) => {
            const point =
              curve.getPointAt(t);

            const tangent =
              curve
                .getTangentAt(t)
                .normalize();

            const side =
              new THREE.Vector3(
                -tangent.z,
                0,
                tangent.x
              ).normalize();

            const angle =
              Math.atan2(
                tangent.x,
                tangent.z
              );

            [-1, 1].forEach(
              (direction) => {
                /*
                  Start at the OUTER edge of the perimeter road.
                  The side street therefore forms a T-junction with
                  the city road and never reaches the Armco/circuit.
                */
                const roadStartDistance =
                  buildingSidePavementOuterEdge -
                  0.8;

                const centreDistance =
                  roadStartDistance +
                  SIDE_STREET_LENGTH / 2;

                const position =
                  point
                    .clone()
                    .add(
                      side
                        .clone()
                        .multiplyScalar(
                          direction *
                          centreDistance
                        )
                    );

                result.push({
                  id:
                    `${streetIndex}-${direction}`,

                  t,
                  direction,

                  position: [
                    position.x,
                    0.055,
                    position.z,
                  ],

                  rotation:
                    angle +
                    (
                      direction === 1
                        ? Math.PI / 2
                        : -Math.PI / 2
                    ),

                  length:
                    SIDE_STREET_LENGTH,
                });
              }
            );
          }
        );

        return result;
      },
      [
        curve,
        buildingSidePavementOuterEdge,
      ]
    );

  return (
    <group>
      {/* Inner city-road kerbs only. The outer edge stays flush at side-street junctions. */}
      {cityKerbs.map(
        (geometry, index) => (
          <mesh
            key={`city-road-kerb-${index}`}
            geometry={geometry}
            receiveShadow
          >
            <meshStandardMaterial
              color="#b8b6ad"
              roughness={0.94}
              metalness={0}
              side={THREE.DoubleSide}
            />
          </mesh>
        )
      )}

      {/* Broken white centre lines on the parallel city road. */}
      {cityRoadCentreDashes.map(
        (dash) => (
          <mesh
            key={`city-road-centre-${dash.id}`}
            position={dash.position}
            rotation={dash.rotation}
            receiveShadow
          >
            <boxGeometry
              args={[
                dash.width,
                0.022,
                dash.length,
              ]}
            />
            <meshStandardMaterial
              color="#e6e6e1"
              roughness={0.88}
              metalness={0}
            />
          </mesh>
        )
      )}

      {/* Streets leading from the city blocks into the perimeter road. */}
      {sideStreets.map(
        (street) => (
          <group
            key={street.id}
            position={street.position}
            rotation={[
              0,
              street.rotation,
              0,
            ]}
          >
            {/* Side-street asphalt. */}
            <mesh receiveShadow>
              <boxGeometry
                args={[
                  SIDE_STREET_WIDTH,
                  0.08,
                  street.length,
                ]}
              />

              <meshStandardMaterial
                color="#343638"
                roughness={0.94}
                metalness={0}
              />
            </mesh>

            {/* Pavements on both sides of the side street. */}
            {[
              -(SIDE_STREET_WIDTH / 2 +
                SIDE_PAVEMENT_WIDTH / 2),
              SIDE_STREET_WIDTH / 2 +
                SIDE_PAVEMENT_WIDTH / 2,
            ].map(
              (xOffset) => (
                <mesh
                  key={`side-pavement-${xOffset}`}
                  position={[
                    xOffset,
                    0.06,
                    0,
                  ]}
                  receiveShadow
                >
                  <boxGeometry
                    args={[
                      SIDE_PAVEMENT_WIDTH,
                      0.12,
                      street.length,
                    ]}
                  />

                  <meshStandardMaterial
                    color="#777b7e"
                    roughness={0.96}
                    metalness={0}
                  />
                </mesh>
              )
            )}

            {/*
              Zebra crossing just before the T-junction.
              It crosses the SIDE STREET only; it never crosses the
              race circuit and never requires an Armco opening.
            */}
            <group
              position={[
                0,
                0.12,
                -street.length / 2 + 4.0,
              ]}
            >
              {Array.from({
                length: 9,
              }).map(
                (_, stripeIndex) => (
                  <mesh
                    key={`zebra-${stripeIndex}`}
                    position={[
                      0,
                      0,
                      (stripeIndex - 4) *
                        0.54,
                    ]}
                    receiveShadow
                  >
                    <boxGeometry
                      args={[
                        5.55,
                        0.022,
                        0.30,
                      ]}
                    />

                    <meshStandardMaterial
                      color="#eeeeea"
                      roughness={0.9}
                      metalness={0}
                    />
                  </mesh>
                )
              )}
            </group>

            {/* Raised kerbs with dropped sections beside the crossing. */}
            {[
              -(SIDE_STREET_WIDTH / 2 + 0.10),
              SIDE_STREET_WIDTH / 2 + 0.10,
            ].map(
              (xOffset) => {
                const crossingCentreZ =
                  -street.length / 2 + 4.0;

                const droppedLength =
                  5.2;

                const kerbWidth =
                  0.20;

                const kerbHeight =
                  0.14;

                const streetStart =
                  -street.length / 2;

                const streetEnd =
                  street.length / 2;

                const dropStart =
                  crossingCentreZ -
                  droppedLength / 2;

                const dropEnd =
                  crossingCentreZ +
                  droppedLength / 2;

                const beforeLength =
                  Math.max(
                    0.01,
                    dropStart -
                    streetStart
                  );

                const afterLength =
                  Math.max(
                    0.01,
                    streetEnd -
                    dropEnd
                  );

                return (
                  <group
                    key={`side-street-kerb-${xOffset}`}
                  >
                    <mesh
                      position={[
                        xOffset,
                        kerbHeight / 2 +
                          0.08,
                        streetStart +
                          beforeLength / 2,
                      ]}
                      castShadow
                      receiveShadow
                    >
                      <boxGeometry
                        args={[
                          kerbWidth,
                          kerbHeight,
                          beforeLength,
                        ]}
                      />
                      <meshStandardMaterial
                        color="#b8b6ad"
                        roughness={0.94}
                        metalness={0}
                      />
                    </mesh>

                    <mesh
                      position={[
                        xOffset,
                        0.105,
                        crossingCentreZ,
                      ]}
                      receiveShadow
                    >
                      <boxGeometry
                        args={[
                          kerbWidth,
                          0.05,
                          droppedLength,
                        ]}
                      />
                      <meshStandardMaterial
                        color="#c3c0b6"
                        roughness={0.96}
                        metalness={0}
                      />
                    </mesh>

                    {/* Yellow tactile paving beside the dropped kerb. */}
                    <mesh
                      position={[
                        xOffset < 0
                          ? xOffset - 0.58
                          : xOffset + 0.58,
                        0.145,
                        crossingCentreZ,
                      ]}
                      receiveShadow
                    >
                      <boxGeometry
                        args={[
                          0.9,
                          0.035,
                          2.35,
                        ]}
                      />
                      <meshStandardMaterial
                        color="#d7c65b"
                        roughness={0.9}
                        metalness={0}
                      />
                    </mesh>

                    <mesh
                      position={[
                        xOffset,
                        kerbHeight / 2 +
                          0.08,
                        dropEnd +
                          afterLength / 2,
                      ]}
                      castShadow
                      receiveShadow
                    >
                      <boxGeometry
                        args={[
                          kerbWidth,
                          kerbHeight,
                          afterLength,
                        ]}
                      />
                      <meshStandardMaterial
                        color="#b8b6ad"
                        roughness={0.94}
                        metalness={0}
                      />
                    </mesh>
                  </group>
                );
              }
            )}

            {/*
              UK-style give-way marking at the T-junction with the
              perimeter road. Two rows of short white blocks sit just
              before the side street reaches the main city road.
            */}
            <group
              position={[
                0,
                0.112,
                -street.length / 2 + 0.72,
              ]}
            >
              {[0, 0.34].map(
                (rowOffset) => (
                  <group
                    key={`give-way-row-${rowOffset}`}
                    position={[
                      0,
                      0,
                      rowOffset,
                    ]}
                  >
                    {[-2.7, -1.8, -0.9, 0, 0.9, 1.8, 2.7].map(
                      (xOffset) => (
                        <mesh
                          key={`give-way-${rowOffset}-${xOffset}`}
                          position={[
                            xOffset,
                            0,
                            0,
                          ]}
                          receiveShadow
                        >
                          <boxGeometry
                            args={[
                              0.52,
                              0.022,
                              0.16,
                            ]}
                          />
                          <meshStandardMaterial
                            color="#eeeeea"
                            roughness={0.88}
                            metalness={0}
                          />
                        </mesh>
                      )
                    )}
                  </group>
                )
              )}
            </group>

            {/* Broken centre line farther into the city block. */}
            {[
              7.0,
              12.0,
            ].map(
              (zOffset) => (
                <mesh
                  key={`side-centre-${zOffset}`}
                  position={[
                    0,
                    0.105,
                    zOffset -
                      street.length / 2,
                  ]}
                >
                  <boxGeometry
                    args={[
                      0.12,
                      0.025,
                      2.3,
                    ]}
                  />
                  <meshStandardMaterial
                    color="#e4e4df"
                    roughness={0.85}
                  />
                </mesh>
              )
            )}
          </group>
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 2 - OUTER CITY ROAD DRESSING

   Adds:
   - paving-slab joints
   - darker kerb/edge detail beside the Armco-side pavement
   - bins
   - benches
   - bollards
   - planters
   - small parked city cars

   Everything stays OUTSIDE the continuous Armco.
========================================================= */


/* =========================================================
   LEVEL 2 - REAL CITY BENCHES

   Uses the renamed Urban Bits GLB:
   /public/assets/city-props/city-props.glb

   The source pack contains multiple props. We clone only a
   bench object from the GLB and automatically normalise its
   size so it fits the existing pavement furniture layout.
========================================================= */

function RealCityBench({
  variant = 0,
}) {
  const { scene } = useGLTF(
    "/assets/city-props/city-props.glb"
  );

  const benchScene =
    useMemo(
      () => {
        const possibleNames =
          variant % 2 === 0
            ? ["Bench", "Bench1"]
            : ["Bench1", "Bench"];

        let source = null;

        for (
          const name of possibleNames
        ) {
          source =
            scene.getObjectByName(name);

          if (source) {
            break;
          }
        }

        /*
          Fallback in case the exporter has altered the exact
          object name slightly.
        */
        if (!source) {
          scene.traverse(
            (child) => {
              if (
                !source &&
                child.name &&
                child.name
                  .toLowerCase()
                  .startsWith("bench")
              ) {
                source = child;
              }
            }
          );
        }

        if (!source) {
          console.warn(
            "No Bench object was found in city-props.glb"
          );

          return null;
        }

        const clone =
          source.clone(true);

        clone.traverse(
          (child) => {
            if (!child.isMesh) {
              return;
            }

            /*
              Clone the material so future prop changes do not
              mutate the original GLB or another bench instance.
            */
            if (
              Array.isArray(
                child.material
              )
            ) {
              child.material =
                child.material.map(
                  (material) =>
                    material.clone()
                );
            } else if (
              child.material
            ) {
              child.material =
                child.material.clone();
            }

            child.castShadow = true;
            child.receiveShadow = true;
          }
        );

        /*
          Work out the model's original dimensions and scale it
          automatically to a sensible city-bench width.
        */
        const originalBox =
          new THREE.Box3()
            .setFromObject(clone);

        const originalSize =
          new THREE.Vector3();

        originalBox.getSize(
          originalSize
        );

        const horizontalSize =
          Math.max(
            originalSize.x,
            originalSize.z
          );

        const TARGET_BENCH_WIDTH =
          1.9;

        if (
          horizontalSize > 0
        ) {
          const scale =
            TARGET_BENCH_WIDTH /
            horizontalSize;

          clone.scale.setScalar(
            scale
          );
        }

        /*
          Recalculate the box after scaling, then centre the
          bench on X/Z and place its lowest point on the pavement.
        */
        const scaledBox =
          new THREE.Box3()
            .setFromObject(clone);

        const centre =
          new THREE.Vector3();

        scaledBox.getCenter(
          centre
        );

        clone.position.x -=
          centre.x;

        clone.position.z -=
          centre.z;

        clone.position.y -=
          scaledBox.min.y;

        return clone;
      },
      [
        scene,
        variant,
      ]
    );

  if (!benchScene) {
    return null;
  }

  return (
    <group
      /*
        If the bench faces backwards in your particular export,
        change this Y rotation from 0 to Math.PI.
      */
      rotation={[
        0,
        0,
        0,
      ]}
    >
      <primitive
        object={benchScene}
      />
    </group>
  );
}


useGLTF.preload(
  "/assets/city-props/city-props.glb"
);


function CityRoadDressing() {
  const curve = useRaceTrackCurve();

  const INNER_PAVEMENT_WIDTH = 3.4;
  const CITY_ROAD_WIDTH = 7.2;
  const OUTER_PAVEMENT_WIDTH = 2.6;

  const armcoOuterEdge =
    BARRIER_OFFSET +
    BARRIER_THICKNESS / 2;

  const innerPavementCentre =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH / 2;

  const cityRoadCentre =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH +
    CITY_ROAD_WIDTH / 2;

  const cityRoadOuterEdge =
    armcoOuterEdge +
    INNER_PAVEMENT_WIDTH +
    CITY_ROAD_WIDTH;

  const outerPavementCentre =
    cityRoadOuterEdge +
    OUTER_PAVEMENT_WIDTH / 2;

  const outerPavementOuterEdge =
    cityRoadOuterEdge +
    OUTER_PAVEMENT_WIDTH;

  const getPlacement = React.useCallback(
    (t, signedOffset, y = 0) => {
      const p = curve.getPointAt(t);
      const tangent =
        curve.getTangentAt(t).normalize();

      const side =
        new THREE.Vector3(
          -tangent.z,
          0,
          tangent.x
        ).normalize();

      const pos =
        p.clone().add(
          side
            .clone()
            .multiplyScalar(signedOffset)
        );

      return {
        position: [
          pos.x,
          y,
          pos.z,
        ],
        rotation: [
          0,
          Math.atan2(
            tangent.x,
            tangent.z
          ),
          0,
        ],
      };
    },
    [curve]
  );

  /*
    Slab joints are short dark strips across the pavement.
    They break up the large flat grey ribbons without adding
    heavy texture files.
  */
  const slabJoints =
    useMemo(() => {
      const result = [];
      const COUNT = 84;

      [-1, 1].forEach(
        (direction) => {
          for (
            let i = 0;
            i < COUNT;
            i++
          ) {
            const t =
              (i + 0.35) / COUNT;

            if (
              isNearCitySideStreet(
                t,
                0.012
              )
            ) {
              continue;
            }

            [
              {
                offset:
                  direction *
                  innerPavementCentre,
                width:
                  INNER_PAVEMENT_WIDTH -
                  0.20,
              },
              {
                offset:
                  direction *
                  outerPavementCentre,
                width:
                  OUTER_PAVEMENT_WIDTH -
                  0.18,
              },
            ].forEach(
              (pavement, pavementIndex) => {
                const placement =
                  getPlacement(
                    t,
                    pavement.offset,
                    0.132
                  );

                result.push({
                  id:
                    `${direction}-${i}-${pavementIndex}`,
                  ...placement,
                  width:
                    pavement.width,
                });
              }
            );
          }
        }
      );

      return result;
    }, [
      getPlacement,
      innerPavementCentre,
      outerPavementCentre,
    ]);

  /*
    Street furniture is deliberately concentrated on the
    BUILDING-SIDE pavement so the road still has clear sight
    lines from the racing camera.
  */
  const furniture =
    useMemo(() => {
      const result = [];

      const points = [
        0.035,
        0.105,
        0.185,
        0.265,
        0.345,
        0.425,
        0.535,
        0.615,
        0.695,
        0.775,
        0.855,
        0.935,
      ];

      [-1, 1].forEach(
        (direction) => {
          points.forEach(
            (t, index) => {
              if (
                isNearCitySideStreet(
                  t,
                  0.018
                )
              ) {
                return;
              }

              const signedOffset =
                direction *
                (
                  outerPavementOuterEdge -
                  0.72
                );

              const placement =
                getPlacement(
                  t,
                  signedOffset,
                  0
                );

              const typeCycle =
                index % 4;

              result.push({
                id:
                  `${direction}-${index}`,
                type:
                  typeCycle === 0
                    ? "bench"
                    : typeCycle === 1
                    ? "bin"
                    : typeCycle === 2
                    ? "planter"
                    : "bollards",
                direction,
                ...placement,
              });
            }
          );
        }
      );

      return result;
    }, [
      getPlacement,
      outerPavementOuterEdge,
    ]);

  /*
    A few simple parked cars make the perimeter road read as
    an ordinary city street rather than another race lane.
    They are placed close to the building-side kerb and kept
    away from side-street junctions.
  */
  const parkedCars =
    useMemo(() => {
      const result = [];

      const parkingPoints = [
        0.075,
        0.225,
        0.385,
        0.565,
        0.725,
        0.895,
      ];

      [-1, 1].forEach(
        (direction) => {
          parkingPoints.forEach(
            (t, index) => {
              if (
                isNearCitySideStreet(
                  t,
                  0.024
                )
              ) {
                return;
              }

              const offset =
                direction *
                (
                  cityRoadCentre +
                  CITY_ROAD_WIDTH *
                  0.27
                );

              const placement =
                getPlacement(
                  t,
                  offset,
                  0.18
                );

              result.push({
                id:
                  `${direction}-${index}`,
                ...placement,
                bodyColor:
                  [
                    "#6d747a",
                    "#9a9b96",
                    "#315d78",
                    "#6f4d42",
                    "#45584d",
                    "#77706a",
                  ][
                    (index +
                      (direction > 0
                        ? 1
                        : 3)) %
                      6
                  ],
              });
            }
          );
        }
      );

      return result;
    }, [
      getPlacement,
      cityRoadCentre,
    ]);

  return (
    <group>
      {/* Subtle paving-slab joints. */}
      {slabJoints.map(
        (joint) => (
          <mesh
            key={`slab-joint-${joint.id}`}
            position={joint.position}
            rotation={joint.rotation}
            receiveShadow
          >
            <boxGeometry
              args={[
                joint.width,
                0.012,
                0.045,
              ]}
            />
            <meshStandardMaterial
              color="#666a69"
              roughness={1}
              metalness={0}
            />
          </mesh>
        )
      )}

      {/* Dark edge immediately beside the Armco-side pavement. */}
      {[-1, 1].map(
        (direction) => {
          const geometry =
            createTrackStripGeometry(
              curve,
              direction *
                (
                  armcoOuterEdge +
                  0.10
                ),
              0.20,
              0.09,
              620
            );

          return (
            <mesh
              key={`armco-pavement-edge-${direction}`}
              geometry={geometry}
              receiveShadow
            >
              <meshStandardMaterial
                color="#55595a"
                roughness={0.95}
                metalness={0}
                side={THREE.DoubleSide}
              />
            </mesh>
          );
        }
      )}

      {/* Street furniture. */}
      {furniture.map(
        (item) => (
          <group
            key={`furniture-${item.id}`}
            position={item.position}
            rotation={item.rotation}
          >
            {item.type ===
              "bin" && (
              <group>
                <mesh
                  position={[
                    0,
                    0.48,
                    0,
                  ]}
                  castShadow
                  receiveShadow
                >
                  <boxGeometry
                    args={[
                      0.48,
                      0.96,
                      0.48,
                    ]}
                  />
                  <meshStandardMaterial
                    color="#252a29"
                    roughness={0.8}
                    metalness={0.12}
                  />
                </mesh>
                <mesh
                  position={[
                    0,
                    0.98,
                    0,
                  ]}
                  castShadow
                >
                  <boxGeometry
                    args={[
                      0.53,
                      0.08,
                      0.53,
                    ]}
                  />
                  <meshStandardMaterial
                    color="#171b1a"
                    roughness={0.75}
                    metalness={0.18}
                  />
                </mesh>
              </group>
            )}

            {item.type ===
              "bench" && (
              <RealCityBench
                variant={
                  Math.abs(
                    Number(
                      item.id
                        .split("-")
                        .pop()
                    )
                  ) % 2
                }
              />
            )}

            {item.type ===
              "planter" && (
              <group>
                <mesh
                  position={[
                    0,
                    0.36,
                    0,
                  ]}
                  castShadow
                  receiveShadow
                >
                  <boxGeometry
                    args={[
                      1.15,
                      0.72,
                      1.15,
                    ]}
                  />
                  <meshStandardMaterial
                    color="#55514a"
                    roughness={0.92}
                  />
                </mesh>

                {[
                  [-0.28, 0.78, -0.18],
                  [0.24, 0.72, 0.12],
                  [0.02, 0.92, 0.26],
                ].map(
                  (
                    [
                      x,
                      y,
                      z,
                    ],
                    leafIndex
                  ) => (
                    <mesh
                      key={`planter-leaf-${leafIndex}`}
                      position={[
                        x,
                        y,
                        z,
                      ]}
                      castShadow
                    >
                      <sphereGeometry
                        args={[
                          0.42,
                          8,
                          6,
                        ]}
                      />
                      <meshStandardMaterial
                        color="#284d2b"
                        roughness={0.95}
                      />
                    </mesh>
                  )
                )}
              </group>
            )}

            {item.type ===
              "bollards" && (
              <group>
                {[-0.58, 0, 0.58].map(
                  (x) => (
                    <mesh
                      key={`bollard-${x}`}
                      position={[
                        x,
                        0.44,
                        0,
                      ]}
                      castShadow
                    >
                      <cylinderGeometry
                        args={[
                          0.085,
                          0.10,
                          0.88,
                          10,
                        ]}
                      />
                      <meshStandardMaterial
                        color="#202426"
                        roughness={0.62}
                        metalness={0.25}
                      />
                    </mesh>
                  )
                )}
              </group>
            )}
          </group>
        )
      )}

      {/* Simple parked city cars. */}
      {parkedCars.map(
        (car) => (
          <group
            key={`parked-car-${car.id}`}
            position={car.position}
            rotation={car.rotation}
          >
            <mesh
              position={[
                0,
                0.25,
                0,
              ]}
              castShadow
              receiveShadow
            >
              <boxGeometry
                args={[
                  1.62,
                  0.50,
                  3.45,
                ]}
              />
              <meshStandardMaterial
                color={car.bodyColor}
                roughness={0.55}
                metalness={0.18}
              />
            </mesh>

            <mesh
              position={[
                0,
                0.64,
                -0.10,
              ]}
              castShadow
            >
              <boxGeometry
                args={[
                  1.40,
                  0.48,
                  1.78,
                ]}
              />
              <meshStandardMaterial
                color={car.bodyColor}
                roughness={0.52}
                metalness={0.18}
              />
            </mesh>

            <mesh
              position={[
                0,
                0.70,
                -0.11,
              ]}
              castShadow
            >
              <boxGeometry
                args={[
                  1.18,
                  0.34,
                  1.45,
                ]}
              />
              <meshStandardMaterial
                color="#26343d"
                roughness={0.35}
                metalness={0.15}
              />
            </mesh>

            {[
              [-0.78, 0.02, -1.05],
              [0.78, 0.02, -1.05],
              [-0.78, 0.02, 1.05],
              [0.78, 0.02, 1.05],
            ].map(
              (
                wheelPosition,
                wheelIndex
              ) => (
                <mesh
                  key={`parked-wheel-${wheelIndex}`}
                  position={wheelPosition}
                  rotation={[
                    0,
                    0,
                    Math.PI / 2,
                  ]}
                  castShadow
                >
                  <cylinderGeometry
                    args={[
                      0.30,
                      0.30,
                      0.18,
                      12,
                    ]}
                  />
                  <meshStandardMaterial
                    color="#101112"
                    roughness={0.8}
                  />
                </mesh>
              )
            )}
          </group>
        )
      )}
    </group>
  );
}

/* =========================================================
   LEVEL 2 - REAL CITY BUILDINGS
========================================================= */

function GreyBackgroundBuildings() {
  const curve =
    useRaceTrackCurve();


  const {
    scene,
  } = useGLTF(
    "/assets/game_ready_city_buildings_pack.glb"
  );


  const buildingNames =
    useMemo(
      () => [
        "Cube035_0",
        "Cube033_1",
        "Atlas131_2",
        "Atlas006_3",
        "Atlas002_4",
        "atlas002_5",
        "Atlas001_40",
        "Atlas003_41",
      ],
      []
    );


  /* =======================================================
     CREATE NORMALISED BUILDING TEMPLATES
  ======================================================= */

  const buildingTemplates =
    useMemo(
      () => {
        return buildingNames
          .map(
            (
              name
            ) => {
              const source =
                scene.getObjectByName(
                  name
                );


              if (
                !source
              ) {
                console.warn(
                  "Building not found:",
                  name
                );

                return null;
              }


              const clone =
                source.clone(
                  true
                );


              clone.traverse(
                (
                  child
                ) => {
                  if (
                    child.isMesh
                  ) {
                    /*
                      Keep city relatively lightweight.
                    */

                    child.castShadow =
                      false;

                    child.receiveShadow =
                      true;
                  }
                }
              );


              /* =========================================
                 FIND ORIGINAL SIZE
              ========================================= */

              clone.updateMatrixWorld(
                true
              );


              const originalBox =
                new THREE.Box3()
                  .setFromObject(
                    clone
                  );


              const originalSize =
                new THREE.Vector3();


              originalBox.getSize(
                originalSize
              );


              if (
                originalSize.y <=
                0.0001
              ) {
                return null;
              }


              /* =========================================
                 NORMALISE TO 1 UNIT HIGH
              ========================================= */

              const normalisingScale =
                1 /
                originalSize.y;


              clone.scale.setScalar(
                normalisingScale
              );


              clone.updateMatrixWorld(
                true
              );


              /* =========================================
                 CENTRE MODEL
              ========================================= */

              const normalisedBox =
                new THREE.Box3()
                  .setFromObject(
                    clone
                  );


              const centre =
                new THREE.Vector3();


              normalisedBox.getCenter(
                centre
              );


              clone.position.x -=
                centre.x;

              clone.position.z -=
                centre.z;


              /*
                Put base at ground level.
              */

              clone.position.y -=
                normalisedBox.min.y;


              clone.updateMatrixWorld(
                true
              );


              /* =========================================
                 FINAL NORMALISED SIZE
              ========================================= */

              const finalBox =
                new THREE.Box3()
                  .setFromObject(
                    clone
                  );


              const finalSize =
                new THREE.Vector3();


              finalBox.getSize(
                finalSize
              );


              return {
                name,

                model:
                  clone,

                width:
                  finalSize.x,

                height:
                  finalSize.y,

                depth:
                  finalSize.z,
              };
            }
          )
          .filter(
            Boolean
          );
      },
      [
        scene,
        buildingNames,
      ]
    );


  /* =======================================================
     SAMPLE THE WHOLE RACETRACK

     We use these points to make sure no building can
     accidentally overlap another part of the circuit.
  ======================================================= */

  const trackSamples =
    useMemo(
      () => {
        const samples = [];

        const sampleCount =
          800;


        for (
          let i = 0;
          i < sampleCount;
          i++
        ) {
          samples.push(
            curve.getPointAt(
              i /
              sampleCount
            )
          );
        }


        return samples;
      },
      [
        curve,
      ]
    );


  /* =======================================================
     PLACE BUILDINGS
  ======================================================= */

  const buildingPlacements =
    useMemo(
      () => {
        if (
          buildingTemplates.length ===
          0
        ) {
          return [];
        }


        const result = [];


        /*
          Fewer buildings.

          This should feel like a city circuit,
          not a road buried inside skyscrapers.
        */

       /*
  Sixty-four shared plots around the circuit. Positions beside
  intersections are deliberately removed to form side roads.
*/

const buildingCount =
  96;

/*
  Primarily medium-rise buildings.

  The occasional height of 14 or 15 adds
  variation without creating isolated towers.
*/

const targetHeights = [
  5.5,
  6.2,
  5.8,
  6.8,
  5.4,
  6.4,
  5.9,
  7.0,
  5.6,
  6.3,
  5.7,
  6.6,
];

const MAX_BUILDING_WIDTH =
  6.8;

const MAX_BUILDING_DEPTH =
  7.2;

        /*
          Empty space BETWEEN the Armco
          and the nearest building.
        */

        /*
          The pavement reaches 4.15 units beyond the
          centre of the barrier. A further 0.8-unit gap
          keeps this row clear without pushing it so far
          away that it disappears from the camera view.
        */

        const BARRIER_CLEARANCE =
          4.45;


        /*
          Extra absolute safety zone around
          the whole racetrack.
        */

        const TRACK_SAFE_DISTANCE =
          BARRIER_OFFSET +
          BARRIER_CLEARANCE;


        /* =================================================
           TEST WHETHER BUILDING IS SAFE

           radius = building horizontal bounding radius.

           We compare against EVERY sampled point
           around the entire circuit.
        ================================================= */

        const isPositionSafe =
          (
            position,
            buildingRadius
          ) => {
            const requiredDistance =
              TRACK_SAFE_DISTANCE +
              buildingRadius;


            for (
              const trackPoint
              of trackSamples
            ) {
              const dx =
                position.x -
                trackPoint.x;

              const dz =
                position.z -
                trackPoint.z;


              const distance =
                Math.sqrt(
                  dx * dx +
                  dz * dz
                );


              if (
                distance <
                requiredDistance
              ) {
                return false;
              }
            }


            return true;
          };


        /* =================================================
           CREATE BUILDINGS
        ================================================= */

        for (
          let i = 0;
          i < buildingCount;
          i++
        ) {
          /*
            Grey buildings use the odd plots. Quaternius
            buildings use the even plots on the same facade
            line, producing a continuous city frontage.
          */

          
          const t =
            (
              i /
              buildingCount +
              0.002
            ) %
            1;


          /*
            Leave a proper opening wherever a side street
            meets the circuit. The remaining runs become
            recognisable, closely packed city blocks.
          */

          if (
            isNearCitySideStreet(
              t,
              0.010
            )
          ) {
            continue;
          }


          const point =
            curve.getPointAt(
              t
            );


          const tangent =
            curve
              .getTangentAt(
                t
              )
              .normalize();


          const side =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();


          const roadAngle =
            Math.atan2(
              tangent.x,
              tangent.z
            );


          /*
            One building on each side.
          */

          [
            1,
            -1,
          ].forEach(
            (
              direction
            ) => {
              const templateIndex =
                (
                  i +
                  (
                    direction ===
                    1
                      ? 0
                      : 3
                  )
                ) %
                buildingTemplates.length;


              const template =
                buildingTemplates[
                  templateIndex
                ];


              const targetHeight =
                targetHeights[
                  (
                    i +
                    (
                      direction ===
                      1
                        ? 0
                        : 2
                    )
                  ) %
                  targetHeights.length
                ];


           /*
  The template is normalised to one unit tall.

  Scale towards the target height, but prevent
  unusually wide or deep buildings becoming huge.
  Uniform scaling preserves the building's proportions.
*/

const heightScale =
  targetHeight;

const widthScale =
  MAX_BUILDING_WIDTH /
  Math.max(
    template.width,
    0.0001
  );

const depthScale =
  MAX_BUILDING_DEPTH /
  Math.max(
    template.depth,
    0.0001
  );

const scale =
  Math.min(
    heightScale,
    widthScale,
    depthScale
  );


              const scaledWidth =
                template.width *
                scale;


              const scaledDepth =
                template.depth *
                scale;


              /*
                Circular radius enclosing the
                entire building footprint.

                Using this instead of width / 2
                prevents corners clipping the track
                after rotation.
              */
const buildingRadius =
                Math.sqrt(
                  (
                    scaledWidth /
                    2
                  ) ** 2 +
                  (
                    scaledDepth /
                    2
                  ) ** 2
                );

              const buildingSetbackSize =
  scaledDepth / 2;

              /*
                Start comfortably beyond barrier.
              */

              /*
  SECOND CITY ROW

  Grey/office buildings sit behind the detailed
  Quaternius frontage rather than competing with it.
*/
const SECOND_ROW_OFFSET = 10.0;

let distanceFromTrack =
  BARRIER_OFFSET +
  BARRIER_CLEARANCE +
  buildingSetbackSize +
  SECOND_ROW_OFFSET;


              let position =
                point
                  .clone()
                  .add(
                    side
                      .clone()
                      .multiplyScalar(
                        distanceFromTrack *
                        direction
                      )
                  );


              /*
                -----------------------------------------
                CRITICAL SAFETY CHECK
                -----------------------------------------

                If ANY part of the building could
                overlap ANY section of racetrack,
                keep pushing it outward.

                Maximum 30 attempts prevents an
                accidental infinite loop.
              */

              let attempts =
                0;


              while (
                !isPositionSafe(
                  position,
                  buildingSetbackSize
                ) &&
                attempts <
                30
              ) {
                distanceFromTrack +=
                  2;


                position =
                  point
                    .clone()
                    .add(
                      side
                        .clone()
                        .multiplyScalar(
                          distanceFromTrack *
                          direction
                        )
                    );


                attempts++;
              }


              /*
                Add a little more variation
                AFTER safety has been established.
              */

             /*
  Small setback variation keeps the buildings
  from being perfectly uniform while maintaining
  a recognisable street line.
*/

const decorativeOffset =
  0;


              position =
                point
                  .clone()
                  .add(
                    side
                      .clone()
                      .multiplyScalar(
                        (
                          distanceFromTrack +
                          decorativeOffset
                        ) *
                        direction
                      )
                  );


              /*
                Recheck after the decorative offset because
                moving away from one bend can move a model
                towards a different part of the circuit.
              */

              if (
                !isPositionSafe(
                  position,
                  buildingSetbackSize
                )
              ) {
                return;
              }


              position.y =
                0;


              result.push({
                id:
                  `${direction}-${i}`,

                templateIndex,

                scale,

                position: [
                  position.x,
                  0,
                  position.z,
                ],

                rotation:
                  roadAngle +
                  (
                    direction ===
                    1
                      ? Math.PI / 2
                      : -Math.PI / 2
                  ),
              });
            }
          );
        }


        return result;
      },
      [
        curve,
        buildingTemplates,
        trackSamples,
      ]
    );


  /* =======================================================
     RENDER
  ======================================================= */

  if (
    buildingTemplates.length ===
    0
  ) {
    return null;
  }


  return (
    <group>

      {buildingPlacements.map(
        (
          placement
        ) => {
          const template =
            buildingTemplates[
              placement.templateIndex
            ];


          const building =
            template.model.clone(
              true
            );
    

          return (
           <group
  key={placement.id}
  position={placement.position}
  rotation={[
    0,
    placement.rotation,
    0,
  ]}
>
  <group
    scale={[
      placement.scale,
      placement.scale,
      placement.scale,
    ]}
  >
    <primitive
      object={building}
    />
  </group>
</group>
          );
        }
      )}

    </group>
  );
}
/* =========================================================
   LEVEL 2 - QUATERNIUS CITY BUILDINGS

   The older building system is left above only for easy
   reference. This is the component used by Level 2.
========================================================= */

function CityBuildings() {
  const cityGroupRef =
    useRef(null);

  const cityCullTimerRef =
    useRef(0);

  const cityCameraXZRef =
    useRef(new THREE.Vector2());

  const curve =
    useRaceTrackCurve();


  const smallBuilding =
    useGLTF(
      CITY_BUILDING_MODELS[0].path
    );


  const mediumBuilding =
    useGLTF(
      CITY_BUILDING_MODELS[1].path
    );


  const largeBuilding =
    useGLTF(
      CITY_BUILDING_MODELS[2].path
    );


  /*
    Normalise every source model to one unit high.

    This removes any differences in the export scales and
    also centres every model with its base exactly at y = 0.
  */

  const buildingTemplates =
    useMemo(
      () => {
        const sources = [
          smallBuilding.scene,
          mediumBuilding.scene,
          largeBuilding.scene,
        ];


        return sources.map(
          (
            source,
            index
          ) => {
            const clone =
              source.clone(
                true
              );


            clone.traverse(
              (child) => {
                if (
                  child.isMesh
                ) {
                  child.castShadow =
                    false;

                  child.receiveShadow =
                    false;
                }
              }
            );


            clone.updateMatrixWorld(
              true
            );


            const originalBox =
              new THREE.Box3()
                .setFromObject(
                  clone
                );


            const originalSize =
              new THREE.Vector3();


            originalBox.getSize(
              originalSize
            );


            const normalisingScale =
              1 /
              Math.max(
                originalSize.y,
                0.0001
              );


            clone.scale.setScalar(
              normalisingScale
            );


            clone.updateMatrixWorld(
              true
            );


            const normalisedBox =
              new THREE.Box3()
                .setFromObject(
                  clone
                );


            const centre =
              new THREE.Vector3();


            normalisedBox.getCenter(
              centre
            );


            clone.position.x -=
              centre.x;

            clone.position.z -=
              centre.z;

            clone.position.y -=
              normalisedBox.min.y;


            clone.updateMatrixWorld(
              true
            );


            const finalBox =
              new THREE.Box3()
                .setFromObject(
                  clone
                );


            const finalSize =
              new THREE.Vector3();


            finalBox.getSize(
              finalSize
            );


            return {
              model:
                clone,

              width:
                finalSize.x,

              depth:
                finalSize.z,

              settings:
                CITY_BUILDING_MODELS[index],
            };
          }
        );
      },
      [
        smallBuilding.scene,
        mediumBuilding.scene,
        largeBuilding.scene,
      ]
    );


  /*
    Sampling the complete circuit means a building cannot
    be safe beside one bend but overlap a different bend.
  */

  const trackSamples =
    useMemo(
      () => {
        const samples = [];


        for (
          let i = 0;
          i < 900;
          i++
        ) {
          samples.push(
            curve.getPointAt(
              i / 900
            )
          );
        }


        return samples;
      },
      [
        curve,
      ]
    );


  const buildingPlacements =
    useMemo(
      () => {
        const result = [];


        /*
          Keep the foreground buildings behind the COMPLETE city-road
          corridor, rather than measuring them from the old single
          pavement. This prevents the brown Quaternius buildings from
          sitting on the new outer road.
        */
        const INNER_PAVEMENT_WIDTH =
          3.4;

        const CITY_ROAD_WIDTH =
          7.2;

        const OUTER_PAVEMENT_WIDTH =
          2.6;

        const BUILDING_SETBACK =
          1.4;

        const PAVEMENT_OUTER_EDGE =
          BARRIER_OFFSET +
          BARRIER_THICKNESS / 2 +
          INNER_PAVEMENT_WIDTH +
          CITY_ROAD_WIDTH +
          OUTER_PAVEMENT_WIDTH +
          BUILDING_SETBACK;


        const BUILDING_CLEARANCE =
          0.35;


        const MAX_BUILDING_WIDTH =
          8.4;


        const MAX_BUILDING_DEPTH =
          9.2;


        const BUILDING_POSITIONS =
          120;


        /*
          Ten small/medium buildings for each large one.
          The fixed sequence keeps the result repeatable.
        */

       /*
  Foreground architectural rhythm.

  0 = small traditional building
  1 = medium building
  2 = large building

  Traditional/smaller buildings deliberately dominate.
  The sequence is asymmetric so the street doesn't look
  procedurally striped.
*/
const templateSequence = [
  0, 0, 1, 0, 0, 1, 0,
  1, 0, 0, 0, 1, 0, 0,
  1, 0, 0, 2, 0, 1, 0,
  0, 1, 0, 0, 0, 1, 0,
  1, 0, 0, 1, 0, 0, 0,
];


        const isClearOfTrack =
          (
            position,
            radius
          ) => {
            const requiredDistance =
              PAVEMENT_OUTER_EDGE +
              BUILDING_CLEARANCE +
              radius;


            return trackSamples.every(
              (trackPoint) => {
                const dx =
                  position.x -
                  trackPoint.x;

                const dz =
                  position.z -
                  trackPoint.z;


                return (
                  dx * dx +
                  dz * dz
                ) >=
                  requiredDistance *
                  requiredDistance;
              }
            );
          };


        const isClearOfBuildings =
          (
            position,
            radius
          ) => {
            return result.every(
              (other) => {
                const dx =
                  position.x -
                  other.position[0];

                const dz =
                  position.z -
                  other.position[2];


                const requiredDistance =
                  radius +
                  other.radius +
                  0.9;


                return (
                  dx * dx +
                  dz * dz
                ) >=
                  requiredDistance *
                  requiredDistance;
              }
            );
          };


        for (
          let i = 0;
          i < BUILDING_POSITIONS;
          i++
        ) {
          /*
            Quaternius facades occupy the even plots. Grey
            buildings occupy the odd plots on the same
            continuous frontage line.
          */

         /*
  Leave occasional deliberate gaps rather than
  mechanically skipping every second plot.

  These become believable alleys, side access and
  breaks between adjoining blocks.
*/
const frontagePattern =
  i % 11;

if (
  frontagePattern === 5 ||
  frontagePattern === 10
) {
  continue;
}

          const t =
            (
              i /
              BUILDING_POSITIONS +
              0.002
            ) % 1;


          if (
            isNearCitySideStreet(
              t,
              0.010
            )
          ) {
            continue;
          }


          const point =
            curve.getPointAt(
              t
            );


          const tangent =
            curve
              .getTangentAt(
                t
              )
              .normalize();


          const side =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();


          const roadAngle =
            Math.atan2(
              tangent.x,
              tangent.z
            );


          [
            1,
            -1,
          ].forEach(
            (direction) => {
              const sequenceIndex =
                (
                  i * 2 +
                  (
                    direction === 1
                      ? 0
                      : 3
                  )
                ) %
                templateSequence.length;


              const templateIndex =
                templateSequence[
                  sequenceIndex
                ];


              const template =
                buildingTemplates[
                  templateIndex
                ];


              const heightChoices =
                template.settings
                  .targetHeights;


              const targetHeight =
                heightChoices[
                  (
                    i +
                    (
                      direction === 1
                        ? 0
                        : 2
                    )
                  ) %
                  heightChoices.length
                ];


              const scale =
                Math.min(
                  targetHeight,

                  MAX_BUILDING_WIDTH /
                    Math.max(
                      template.width,
                      0.0001
                    ),

                  MAX_BUILDING_DEPTH /
                    Math.max(
                      template.depth,
                      0.0001
                    )
                );


              const scaledWidth =
                template.width *
                scale;


              const scaledDepth =
                template.depth *
                scale;


              /*
                This circle encloses the full rotated
                footprint, including every corner.
              */

              const radius =
                Math.sqrt(
                  (
                    scaledWidth / 2
                  ) ** 2 +
                  (
                    scaledDepth / 2
                  ) ** 2
                );


              let distanceFromTrack =
                PAVEMENT_OUTER_EDGE +
                BUILDING_CLEARANCE +
                scaledDepth / 2;


              let position =
                point
                  .clone()
                  .add(
                    side
                      .clone()
                      .multiplyScalar(
                        distanceFromTrack *
                        direction
                      )
                  );


              let attempts =
                0;


              while (
                (
                  !isClearOfTrack(
                    position,
                    scaledDepth / 2
                  ) ||
                  !isClearOfBuildings(
                    position,
                    radius
                  )
                ) &&
                attempts < 40
              ) {
                distanceFromTrack +=
                  0.75;


                position =
                  point
                    .clone()
                    .add(
                      side
                        .clone()
                        .multiplyScalar(
                          distanceFromTrack *
                          direction
                        )
                    );


                attempts++;
              }


              if (
                !isClearOfTrack(
                  position,
                  scaledDepth / 2
                ) ||
                !isClearOfBuildings(
                  position,
                  radius
                )
              ) {
                return;
              }


              result.push({
                id:
                  `${direction}-${i}`,

                templateIndex,

                scale,

                radius,

                position: [
                  position.x,
                  0,
                  position.z,
                ],

                rotation:
                  roadAngle +
                  (
                    direction === 1
                      ? Math.PI / 2
                      : -Math.PI / 2
                  ),
              });
            }
          );
        }


        return result;
      },
      [
        curve,
        buildingTemplates,
        trackSamples,
      ]
    );


  /*
    PERFORMANCE PASS 3:
    The foreground contains a very large number of cloned GLTF meshes.
    Only test visibility five times per second, not every frame.
    The Level 2 fog starts at 90 units, so buildings beyond 105 units
    contribute very little visually but can cost many draw calls.
  */
  useFrame(
    (state, delta) => {
      cityCullTimerRef.current +=
        delta;

      if (
        cityCullTimerRef.current <
        0.20
      ) {
        return;
      }

      cityCullTimerRef.current = 0;

      const group =
        cityGroupRef.current;

      if (!group) {
        return;
      }

      cityCameraXZRef.current.set(
        state.camera.position.x,
        state.camera.position.z
      );

      const maxDistanceSq =
        105 * 105;

      group.children.forEach(
        (child) => {
          const dx =
            child.position.x -
            cityCameraXZRef.current.x;

          const dz =
            child.position.z -
            cityCameraXZRef.current.y;

          child.visible =
            dx * dx + dz * dz <=
            maxDistanceSq;
        }
      );
    }
  );


  return (
    <group
      ref={cityGroupRef}
    >
      {buildingPlacements.map(
        (placement) => {
          const building =
            buildingTemplates[
              placement.templateIndex
            ].model.clone(
              true
            );


          return (
            <group
              key={
                placement.id
              }
              position={
                placement.position
              }
              rotation={[
                0,
                placement.rotation,
                0,
              ]}
              scale={[
                placement.scale,
                placement.scale,
                placement.scale,
              ]}
            >
              <primitive
                object={
                  building
                }
              />
            </group>
          );
        }
      )}
    </group>
  );
}


function CityBackgroundSkyline() {
  const buildings = useMemo(
    () => {
      const result = [];

      const rings = [
  {
    radius: 125,
    count: 18,
    minHeight: 12,
    maxHeight: 19,
  },
  {
    radius: 150,
    count: 22,
    minHeight: 16,
    maxHeight: 25,
  },
];

      rings.forEach(
        (ring, ringIndex) => {
          for (
            let i = 0;
            i < ring.count;
            i++
          ) {
            const angle =
              (i / ring.count) *
                Math.PI *
                2 +
              ringIndex * 0.08;

            /*
              Deterministic variation rather than
              Math.random(), so the skyline doesn't
              change every render.
            */

            const variation =
              (
                Math.sin(
                  i * 12.9898 +
                  ringIndex * 4.73
                ) +
                1
              ) /
              2;

            const height =
              ring.minHeight +
              variation *
                (
                  ring.maxHeight -
                  ring.minHeight
                );

           const width =
  5 +
  (
    (
      Math.sin(i * 5.31) +
      1
    ) /
    2
  ) *
    4;

          const depth =
  5 +
  (
    (
      Math.cos(i * 4.17) +
      1
    ) /
    2
  ) *
    4;

            const radius =
              ring.radius +
              Math.sin(i * 2.7) * 5;

            result.push({
              id:
                `${ringIndex}-${i}`,

              position: [
                Math.cos(angle) *
                  radius,

                height / 2,

                Math.sin(angle) *
                  radius,
              ],

              width,
              height,
              depth,

              rotation:
                -angle +
                Math.PI / 2,

              tone:
                (
                  i +
                  ringIndex
                ) %
                4,
            });
          }
        }
      );

      return result;
    },
    []
  );

  const colours = [
    "#59636c",
    "#68727a",
    "#505a63",
    "#747d84",
  ];

  return (
    <group>
      {buildings.map(
        (building) => (
          <group
            key={building.id}
            position={
              building.position
            }
            rotation={[
              0,
              building.rotation,
              0,
            ]}
          >
            {/* Main tower */}
            <mesh
              castShadow={false}
              receiveShadow
            >
              <boxGeometry
                args={[
                  building.width,
                  building.height,
                  building.depth,
                ]}
              />

              <meshStandardMaterial
                color={
                  colours[
                    building.tone
                  ]
                }
                roughness={0.78}
                metalness={0.08}
              />
            </mesh>

            {/* Dark glass front */}
            <mesh
              position={[
                0,
                0,
                -building.depth /
                  2 -
                  0.015,
              ]}
            >
              <planeGeometry
                args={[
                  building.width *
                    0.82,

                  building.height *
                    0.88,
                ]}
              />

              <meshStandardMaterial
                color="#263641"
                roughness={0.34}
                metalness={0.25}
              />
            </mesh>

            {/* Rooftop structure */}
            <mesh
              position={[
                0,
                building.height /
                  2 +
                  0.7,
                0,
              ]}
            >
              <boxGeometry
                args={[
                  building.width *
                    0.35,
                  1.4,
                  building.depth *
                    0.32,
                ]}
              />

              <meshStandardMaterial
                color="#555d63"
                roughness={0.8}
              />
            </mesh>
          </group>
        )
      )}
    </group>
  );
}

/* =========================================================
   LEVEL 2 - STREET LIGHTS
========================================================= */

function CityStreetLights() {
  const curve =
    useRaceTrackCurve();

  const lights =
    useMemo(
      () => {
        const result = [];

        const lightCount =
          22;

        for (
          let i = 0;
          i < lightCount;
          i++
        ) {
          const t =
            i /
            lightCount;

          const point =
            curve.getPointAt(
              t
            );

          const tangent =
            curve
              .getTangentAt(
                t
              )
              .normalize();

          const side =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();

          const angle =
            Math.atan2(
              tangent.x,
              tangent.z
            );

          [
            1,
            -1,
          ].forEach(
            (
              direction
            ) => {
              const position =
                point
                  .clone()
                  .add(
                    side
                      .clone()
                      .multiplyScalar(
                        (
                          ROAD_HALF_WIDTH +
                          4.6
                        ) *
                        direction
                      )
                  );

              result.push({
                id:
                  `${direction}-${i}`,

                position: [
                  position.x,
                  0,
                  position.z,
                ],

                rotation:
                  angle,
              });
            }
          );
        }

        return result;
      },
      [
        curve,
      ]
    );

  return (
    <group>
      {lights.map(
        (
          light
        ) => (
          <group
            key={
              light.id
            }
            position={
              light.position
            }
            rotation={[
              0,
              light.rotation,
              0,
            ]}
          >
            <mesh
              position={[
                0,
                2.8,
                0,
              ]}
              castShadow
            >
              <cylinderGeometry
                args={[
                  0.08,
                  0.11,
                  5.6,
                  10,
                ]}
              />

              <meshStandardMaterial
                color="#30343a"
                metalness={0.65}
                roughness={0.38}
              />
            </mesh>

            <mesh
              position={[
                0,
                5.6,
                -0.45,
              ]}
              castShadow
            >
              <boxGeometry
                args={[
                  0.28,
                  0.18,
                  1.0,
                ]}
              />

              <meshStandardMaterial
                color="#2a2d31"
                metalness={0.55}
                roughness={0.4}
              />
            </mesh>

            <mesh
              position={[
                0,
                5.48,
                -0.79,
              ]}
            >
              <boxGeometry
                args={[
                  0.18,
                  0.05,
                  0.28,
                ]}
              />

              <meshStandardMaterial
                color="#fff4c7"
                emissive="#ffd979"
                emissiveIntensity={1.2}
              />
            </mesh>
          </group>
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 2 - CITY ENVIRONMENT
========================================================= */

function LevelTwoEnvironment() {
  return (
    <>
      {/* HDRI is kept for realistic reflections, but the visible
          background is now the procedural sky below. */}
      <Environment
        files="/assets/rural_asphalt_road_2k.hdr"
        environmentIntensity={0.28}
      />

      <LevelTwoSky />

      {/* Softer atmospheric haze gives distant buildings depth. */}
      <fog
        attach="fog"
        args={[
          "#87949d",
          90,
          285,
        ]}
      />

      {/* Natural daylight */}
      <hemisphereLight
        args={[
          "#9caeb9",
          "#454b4e",
          0.72,
        ]}
      />

      <directionalLight
        position={[
          70,
          105,
          35,
        ]}
        intensity={0.82}
        color="#dce4e8"
      />

      <ambientLight
        intensity={0.22}
      />

      <LevelTwoRain />

      <CityGround />

      <CityPavements />

      <CityStreetNetwork />

      <CityRoadDressing />

      <CityPedestrianTrafficLights />

      <GreyBackgroundBuildings />

      <CityBuildings />

<CityBackgroundSkyline />

      <CityTrees />

      <CityStreetLights />
      
    </>
  );
}


/* =========================================================
   LEVEL 3 - BEACH PROMENADE
   Circuit -> promenade with palms -> sand -> sea
========================================================= */

function LevelThreePromenade() {
  const curve =
    useRaceTrackCurve();

  const promenadeTexture =
    useTexture(
      "/textures/promenade.png"
    );

  useEffect(
    () => {
      promenadeTexture.wrapS =
        THREE.RepeatWrapping;

      promenadeTexture.wrapT =
        THREE.RepeatWrapping;

      /*
        createTrackStripGeometry already supplies UVs along the
        circuit. Repeating the image keeps the terrazzo detail at a
        believable scale rather than stretching one image around the
        whole promenade.
      */
      promenadeTexture.repeat.set(
        1,
        2.2
      );

      promenadeTexture.colorSpace =
        THREE.SRGBColorSpace;

      promenadeTexture.anisotropy =
        8;

      promenadeTexture.needsUpdate =
        true;
    },
    [
      promenadeTexture,
    ]
  );

  /*
    LEVEL 3 PROMENADE

    This now works exactly like Level 2's outer road/pavement system:
    it is generated FROM THE RACE CIRCUIT CURVE rather than as a
    circular ring around the map.

    Layout on BOTH sides:
      circuit
      -> red/white kerb
      -> Armco
      -> promenade

    The promenade begins immediately outside the Armco and follows
    every straight and bend around the complete Level 3 circuit.
  */

  const PROMENADE_WIDTH = 17.5;

  const armcoOuterEdge =
    ROAD_HALF_WIDTH +
    KERB_WIDTH +
    0.28 +
    BARRIER_THICKNESS / 2;

  const promenadeCentreOffset =
    armcoOuterEdge +
    PROMENADE_WIDTH / 2;

  const promenadeGeometries =
    useMemo(
      () => [
        createTrackStripGeometry(
          curve,
          promenadeCentreOffset,
          PROMENADE_WIDTH,
          0.015,
          760
        ),
        createTrackStripGeometry(
          curve,
          -promenadeCentreOffset,
          PROMENADE_WIDTH,
          0.015,
          760
        ),
      ],
      [
        curve,
        promenadeCentreOffset,
      ]
    );

  const edgeWidth = 0.32;

  const outerEdgeOffset =
    armcoOuterEdge +
    PROMENADE_WIDTH -
    edgeWidth / 2;

  const outerEdgeGeometries =
    useMemo(
      () => [
        createTrackStripGeometry(
          curve,
          outerEdgeOffset,
          edgeWidth,
          0.025,
          760
        ),
        createTrackStripGeometry(
          curve,
          -outerEdgeOffset,
          edgeWidth,
          0.025,
          760
        ),
      ],
      [
        curve,
        outerEdgeOffset,
      ]
    );

  useEffect(
    () => {
      return () => {
        promenadeGeometries.forEach(
          (geometry) =>
            geometry.dispose()
        );

        outerEdgeGeometries.forEach(
          (geometry) =>
            geometry.dispose()
        );
      };
    },
    [
      promenadeGeometries,
      outerEdgeGeometries,
    ]
  );

  return (
    <group>
      {promenadeGeometries.map(
        (
          geometry,
          index
        ) => (
          <mesh
            key={
              `level-3-promenade-${index}`
            }
            geometry={geometry}
            receiveShadow={false}
          >
            <meshStandardMaterial
              map={
                promenadeTexture
              }
              color="#fffdf9"
              roughness={0.82}
              metalness={0}
              side={
                THREE.DoubleSide
              }
            />
          </mesh>
        )
      )}

      {outerEdgeGeometries.map(
        (
          geometry,
          index
        ) => (
          <mesh
            key={
              `level-3-promenade-edge-${index}`
            }
            geometry={geometry}
            receiveShadow={false}
          >
            <meshStandardMaterial
              color="#c8bda9"
              roughness={0.95}
              metalness={0}
              side={
                THREE.DoubleSide
              }
            />
          </mesh>
        )
      )}
    </group>
  );
}

/* =========================================================
   LEVEL 3 - PROMENADE / BEACH WHITE RAILING
   Inspired by the simple white seafront railing in the reference.
========================================================= */

function LevelThreePromenadeRailing() {
  const curve =
    useRaceTrackCurve();

  const PROMENADE_WIDTH =
    17.5;

  const armcoOuterEdge =
    ROAD_HALF_WIDTH +
    KERB_WIDTH +
    0.28 +
    BARRIER_THICKNESS / 2;

  /*
    Put the railing exactly along the SAND-SIDE edge of the promenade.
    A tiny inward adjustment keeps the posts visibly planted on the
    promenade instead of floating in the sand.
  */
  const railingOffset =
    armcoOuterEdge +
    PROMENADE_WIDTH -
    0.18;

  const sampleCount =
    190;

  const sides =
    [-1, 1];

  const railingData =
    useMemo(
      () =>
        sides.map(
          (direction) => {
            const points = [];

            for (
              let i = 0;
              i < sampleCount;
              i += 1
            ) {
              const t =
                i / sampleCount;

              const point =
                curve.getPointAt(t);

              const tangent =
                curve
                  .getTangentAt(t)
                  .normalize();

              const side =
                new THREE.Vector3(
                  -tangent.z,
                  0,
                  tangent.x
                ).normalize();

              const position =
                point
                  .clone()
                  .addScaledVector(
                    side,
                    direction *
                      railingOffset
                  );

              points.push({
                position,
                tangent,
              });
            }

            return points;
          }
        ),
      [
        curve,
        railingOffset,
      ]
    );

  const POST_HEIGHT =
    1.35;

  const POST_THICKNESS =
    0.13;

  const TOP_RAIL_HEIGHT =
    1.18;

  const MID_RAIL_HEIGHT =
    0.56;

  const RAIL_THICKNESS =
    0.105;

  return (
    <group>
      {railingData.map(
        (
          points,
          sideIndex
        ) => (
          <group
            key={
              `level-3-white-railing-side-${sideIndex}`
            }
          >
            {/* Upright white posts */}
            {points
              .filter(
                (
                  _,
                  index
                ) =>
                  index % 4 === 0
              )
              .map(
                (
                  item,
                  index
                ) => (
                  <mesh
                    key={
                      `railing-post-${sideIndex}-${index}`
                    }
                    position={[
                      item.position.x,
                      POST_HEIGHT / 2 +
                        0.04,
                      item.position.z,
                    ]}
                    castShadow={false}
                    receiveShadow={false}
                  >
                    <boxGeometry
                      args={[
                        POST_THICKNESS,
                        POST_HEIGHT,
                        POST_THICKNESS,
                      ]}
                    />
                    <meshStandardMaterial
                      color="#f7f7f4"
                      roughness={0.48}
                      metalness={0.08}
                    />
                  </mesh>
                )
              )}

            {/* Continuous top and middle rails */}
            {[
              TOP_RAIL_HEIGHT,
              MID_RAIL_HEIGHT,
            ].map(
              (
                railHeight,
                railIndex
              ) =>
                points.map(
                  (
                    item,
                    index
                  ) => {
                    const next =
                      points[
                        (index + 1) %
                          points.length
                      ];

                    const dx =
                      next.position.x -
                      item.position.x;

                    const dz =
                      next.position.z -
                      item.position.z;

                    const length =
                      Math.sqrt(
                        dx * dx +
                          dz * dz
                      );

                    const midX =
                      (
                        item.position.x +
                        next.position.x
                      ) /
                      2;

                    const midZ =
                      (
                        item.position.z +
                        next.position.z
                      ) /
                      2;

                    const rotationY =
                      Math.atan2(
                        dx,
                        dz
                      );

                    return (
                      <mesh
                        key={
                          `railing-rail-${sideIndex}-${railIndex}-${index}`
                        }
                        position={[
                          midX,
                          railHeight +
                            0.04,
                          midZ,
                        ]}
                        rotation={[
                          0,
                          rotationY,
                          0,
                        ]}
                        castShadow={false}
                        receiveShadow={false}
                      >
                        <boxGeometry
                          args={[
                            RAIL_THICKNESS,
                            RAIL_THICKNESS,
                            length +
                              0.05,
                          ]}
                        />
                        <meshStandardMaterial
                          color="#f7f7f4"
                          roughness={0.45}
                          metalness={0.1}
                        />
                      </mesh>
                    );
                  }
                )
            )}
          </group>
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 3 - PALM TREES
========================================================= */

function BeachPalmTree({
  position,
  rotation = 0,
  scale = 1,
}) {
  const { scene } =
    useGLTF(
      "/assets/beach-props/palmtree.glb"
    );

  const palm =
    useMemo(
      () => {
        const clone =
          scene.clone(true);

        clone.traverse(
          (child) => {
            if (
              child.isMesh
            ) {
              /*
                Keep the palms inexpensive. Level 3 already contains
                the race, Armco, sand and sea, so the palm models do
                not cast dynamic shadows.
              */
              child.castShadow =
                false;

              child.receiveShadow =
                false;
            }
          }
        );

        return clone;
      },
      [scene]
    );

  return (
    <primitive
      object={palm}
      position={position}
      rotation={[
        0,
        rotation,
        0,
      ]}
      scale={scale}
    />
  );
}


function LevelThreePalmTrees() {
  const curve =
    useRaceTrackCurve();

  /*
    The palms now follow the circuit-following promenade too.
    They sit toward the OUTER half of the promenade, leaving a clear
    pedestrian strip immediately beside the Armco.
  */

  const PALM_BASE_SCALE =
    2.4;

  const PROMENADE_WIDTH =
    17.5;

  const armcoOuterEdge =
    ROAD_HALF_WIDTH +
    KERB_WIDTH +
    0.28 +
    BARRIER_THICKNESS / 2;

  const palmOffset =
    armcoOuterEdge +
    PROMENADE_WIDTH * 0.72;

  const palmData =
    useMemo(
      () => {
        const result = [];

        const countPerSide =
          22;

        for (
          let i = 0;
          i < countPerSide;
          i++
        ) {
          const t =
            (
              i /
              countPerSide +
              0.018
            ) % 1;

          const point =
            curve.getPointAt(t);

          const tangent =
            curve
              .getTangentAt(t)
              .normalize();

          const side =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();

          [-1, 1].forEach(
            (
              direction,
              sideIndex
            ) => {
              /*
                Slight deterministic staggering keeps the palms from
                looking like a perfectly repeated fence.
              */
              const stagger =
                (
                  (i * 17 +
                    sideIndex * 11) %
                  9 -
                  4
                ) *
                0.11;

              const position =
                point
                  .clone()
                  .add(
                    side
                      .clone()
                      .multiplyScalar(
                        direction *
                        (
                          palmOffset +
                          stagger
                        )
                      )
                  );

              const rotation =
                Math.atan2(
                  tangent.x,
                  tangent.z
                ) +
                (
                  direction < 0
                    ? Math.PI
                    : 0
                ) +
                ((i * 0.61) % 0.8) -
                0.4;

              const size =
                0.86 +
                (
                  (i * 7 +
                    sideIndex * 3) %
                  7
                ) *
                0.045;

              result.push({
                id:
                  `${i}-${direction}`,

                position: [
                  position.x,
                  0.04,
                  position.z,
                ],

                rotation,
                scale:
                  PALM_BASE_SCALE *
                  size,
              });
            }
          );
        }

        return result;
      },
      [
        curve,
        palmOffset,
      ]
    );

  return (
    <group>
      {palmData.map(
        (palm) => (
          <BeachPalmTree
            key={
              `level-3-palm-${palm.id}`
            }
            position={
              palm.position
            }
            rotation={
              palm.rotation
            }
            scale={
              palm.scale
            }
          />
        )
      )}
    </group>
  );
}

useGLTF.preload(
  "/assets/beach-props/palmtree.glb"
);


/* =========================================================
   LEVEL 3 - PROMENADE BENCHES

   Reuses the exact RealCityBench prop already used in Level 2.
   The benches follow the Level 3 circuit and sit on the wide
   promenade, between the Armco-side walking area and the palms.
========================================================= */

function LevelThreePromenadeBenches() {
  const curve =
    useRaceTrackCurve();

  const PROMENADE_WIDTH =
    17.5;

  const armcoOuterEdge =
    ROAD_HALF_WIDTH +
    KERB_WIDTH +
    0.28 +
    BARRIER_THICKNESS / 2;

  /*
    Keep the benches around the middle/outer portion of the
    promenade. This leaves a generous walking strip beside the
    Armco and avoids putting them directly against the beach rail.
  */
  const benchOffset =
    armcoOuterEdge +
    PROMENADE_WIDTH * 0.48;

  const benchData =
    useMemo(
      () => {
        const result = [];

        /*
          10 benches on each side = 20 total.
          They are deliberately offset from the palm-tree positions
          so benches and trunks do not sit on top of one another.
        */
        const countPerSide =
          10;

        for (
          let i = 0;
          i < countPerSide;
          i += 1
        ) {
          const t =
            (
              i /
              countPerSide +
              0.062
            ) % 1;

          const point =
            curve.getPointAt(t);

          const tangent =
            curve
              .getTangentAt(t)
              .normalize();

          const side =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();

          [-1, 1].forEach(
            (
              direction,
              sideIndex
            ) => {
              /*
                Tiny deterministic sideways variation stops the
                furniture looking mechanically identical while
                keeping every bench safely on the promenade.
              */
              const offsetVariation =
                (
                  (
                    i * 5 +
                    sideIndex * 3
                  ) %
                    5 -
                  2
                ) *
                0.16;

              const position =
                point
                  .clone()
                  .addScaledVector(
                    side,
                    direction *
                      (
                        benchOffset +
                        offsetVariation
                      )
                  );

              /*
                RealCityBench's natural width runs across its local
                horizontal axis. Rotate it to follow the tangent of
                the promenade.

                Each bench faces outward toward the beach/sea rather
                than toward the race circuit.
              */
              const tangentRotation =
                Math.atan2(
                  tangent.x,
                  tangent.z
                );

              const rotation =
                tangentRotation +
                (
                  direction > 0
                    ? 0
                    : Math.PI
                );

              result.push({
                id:
                  `${i}-${sideIndex}`,

                position: [
                  position.x,
                  0.055,
                  position.z,
                ],

                rotation,

                variant:
                  (
                    i +
                    sideIndex
                  ) %
                  2,
              });
            }
          );
        }

        return result;
      },
      [
        curve,
        benchOffset,
      ]
    );

  return (
    <group>
      {benchData.map(
        (bench) => (
          <group
            key={
              `level-3-promenade-bench-${bench.id}`
            }
            position={
              bench.position
            }
            rotation={[
              0,
              bench.rotation,
              0,
            ]}
          >
            <RealCityBench
              variant={
                bench.variant
              }
            />
          </group>
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 3 - EXTRA PROMENADE FURNITURE

   Reuses props from the same Level 2 Urban Bits GLB:
   /assets/city-props/city-props.glb

   Added:
   - bins
   - park lights
   - a feature fountain
========================================================= */

function LevelThreeCityProp({
  names,
  targetSize = 1,
  castShadow = false,
}) {
  const { scene } =
    useGLTF(
      "/assets/city-props/city-props.glb"
    );

  const prop =
    useMemo(
      () => {
        let source = null;

        for (
          const name of names
        ) {
          source =
            scene.getObjectByName(
              name
            );

          if (source) {
            break;
          }
        }

        if (!source) {
          const lowerNames =
            names.map(
              (name) =>
                name.toLowerCase()
            );

          scene.traverse(
            (child) => {
              if (
                source ||
                !child.name
              ) {
                return;
              }

              const lower =
                child.name.toLowerCase();

              if (
                lowerNames.some(
                  (name) =>
                    lower === name ||
                    lower.startsWith(
                      name
                    )
                )
              ) {
                source = child;
              }
            }
          );
        }

        if (!source) {
          console.warn(
            `Could not find promenade prop: ${names.join(", ")}`
          );

          return null;
        }

        const clone =
          source.clone(true);

        clone.traverse(
          (child) => {
            if (!child.isMesh) {
              return;
            }

            if (
              Array.isArray(
                child.material
              )
            ) {
              child.material =
                child.material.map(
                  (material) =>
                    material.clone()
                );
            } else if (
              child.material
            ) {
              child.material =
                child.material.clone();
            }

            child.castShadow =
              castShadow;

            child.receiveShadow =
              false;
          }
        );

        const originalBox =
          new THREE.Box3()
            .setFromObject(
              clone
            );

        const originalSize =
          new THREE.Vector3();

        originalBox.getSize(
          originalSize
        );

        const largestDimension =
          Math.max(
            originalSize.x,
            originalSize.y,
            originalSize.z
          );

        if (
          largestDimension > 0
        ) {
          clone.scale.setScalar(
            targetSize /
              largestDimension
          );
        }

        const scaledBox =
          new THREE.Box3()
            .setFromObject(
              clone
            );

        const centre =
          new THREE.Vector3();

        scaledBox.getCenter(
          centre
        );

        clone.position.x -=
          centre.x;

        clone.position.z -=
          centre.z;

        clone.position.y -=
          scaledBox.min.y;

        return clone;
      },
      [
        scene,
        names.join("|"),
        targetSize,
        castShadow,
      ]
    );

  if (!prop) {
    return null;
  }

  return (
    <primitive
      object={prop}
    />
  );
}


function LevelThreePromenadeFurniture() {
  const curve =
    useRaceTrackCurve();

  const PROMENADE_WIDTH =
    17.5;

  const armcoOuterEdge =
    ROAD_HALF_WIDTH +
    KERB_WIDTH +
    0.28 +
    BARRIER_THICKNESS / 2;

  /*
    Furniture sits across the outer/middle part of the promenade,
    leaving a generous unobstructed walking/racing-viewing strip
    beside the Armco.
  */
  const binOffset =
    armcoOuterEdge +
    PROMENADE_WIDTH *
      0.57;

  const lightOffset =
    armcoOuterEdge +
    PROMENADE_WIDTH *
      0.82;

  const fountainOffset =
    armcoOuterEdge +
    PROMENADE_WIDTH *
      0.53;

  const getPlacement =
    React.useCallback(
      (
        t,
        signedOffset,
        y = 0.055
      ) => {
        const point =
          curve.getPointAt(t);

        const tangent =
          curve
            .getTangentAt(t)
            .normalize();

        const side =
          new THREE.Vector3(
            -tangent.z,
            0,
            tangent.x
          ).normalize();

        const position =
          point
            .clone()
            .addScaledVector(
              side,
              signedOffset
            );

        return {
          position: [
            position.x,
            y,
            position.z,
          ],

          rotation: [
            0,
            Math.atan2(
              tangent.x,
              tangent.z
            ),
            0,
          ],
        };
      },
      [
        curve,
      ]
    );

  const bins =
    useMemo(
      () => {
        const result = [];
        const countPerSide = 8;

        for (
          let i = 0;
          i < countPerSide;
          i += 1
        ) {
          const t =
            (
              i /
                countPerSide +
              0.032
            ) %
            1;

          [-1, 1].forEach(
            (
              direction,
              sideIndex
            ) => {
              const placement =
                getPlacement(
                  t,
                  direction *
                    binOffset
                );

              result.push({
                id:
                  `${i}-${sideIndex}`,

                ...placement,

                names:
                  (
                    i +
                    sideIndex
                  ) %
                    2 ===
                  0
                    ? [
                        "Bin-1",
                        "Bin-2",
                        "Bin",
                      ]
                    : [
                        "Bin-3",
                        "Bin-4",
                        "Bin-5",
                        "Bin",
                      ],
              });
            }
          );
        }

        return result;
      },
      [
        getPlacement,
        binOffset,
      ]
    );

  const lights =
    useMemo(
      () => {
        const result = [];
        const countPerSide = 11;

        for (
          let i = 0;
          i < countPerSide;
          i += 1
        ) {
          const t =
            (
              i /
                countPerSide +
              0.008
            ) %
            1;

          [-1, 1].forEach(
            (
              direction,
              sideIndex
            ) => {
              const placement =
                getPlacement(
                  t,
                  direction *
                    lightOffset
                );

              const variant =
                (
                  i +
                  sideIndex
                ) %
                5 +
                1;

              result.push({
                id:
                  `${i}-${sideIndex}`,

                ...placement,

                names: [
                  `ParkLight-${variant}`,
                  "ParkLight-1",
                  "ParkLight",
                ],
              });
            }
          );
        }

        return result;
      },
      [
        getPlacement,
        lightOffset,
      ]
    );

  /*
    One feature fountain on each side of the promenade.
    The positions are intentionally away from the start/finish area
    and from one another.
  */
  const fountains =
    useMemo(
      () => [
        {
          id: "left",
          ...getPlacement(
            0.31,
            fountainOffset
          ),
        },
        {
          id: "right",
          ...getPlacement(
            0.69,
            -fountainOffset
          ),
        },
      ],
      [
        getPlacement,
        fountainOffset,
      ]
    );

  return (
    <group>
      {/* BINS */}
      {bins.map(
        (item) => (
          <group
            key={
              `level-3-bin-${item.id}`
            }
            position={
              item.position
            }
            rotation={
              item.rotation
            }
          >
            <LevelThreeCityProp
              names={
                item.names
              }
              targetSize={
                1.15
              }
            />
          </group>
        )
      )}

      {/* PARK LIGHTS */}
      {lights.map(
        (item) => (
          <group
            key={
              `level-3-light-${item.id}`
            }
            position={
              item.position
            }
            rotation={
              item.rotation
            }
          >
            <LevelThreeCityProp
              names={
                item.names
              }
              targetSize={
                4.6
              }
            />
          </group>
        )
      )}

      {/* FEATURE FOUNTAINS */}
      {fountains.map(
        (item) => (
          <group
            key={
              `level-3-fountain-${item.id}`
            }
            position={
              item.position
            }
            rotation={
              item.rotation
            }
          >
            <LevelThreeCityProp
              names={[
                "Fountain",
              ]}
              targetSize={
                4.2
              }
            />
          </group>
        )
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 3 - BEACH ENVIRONMENT
========================================================= */

function LevelThreeEnvironment() {
  const sandTexture =
    useTexture(
      "/textures/sand.png"
    );

  const seaTexture =
    useTexture(
      "/textures/sea.png"
    );

  useEffect(
    () => {
      sandTexture.wrapS =
        THREE.RepeatWrapping;

      sandTexture.wrapT =
        THREE.RepeatWrapping;

      sandTexture.repeat.set(
        18,
        18
      );

      sandTexture.anisotropy =
        8;

      sandTexture.colorSpace =
        THREE.SRGBColorSpace;

      sandTexture.needsUpdate =
        true;


      seaTexture.wrapS =
        THREE.RepeatWrapping;

      seaTexture.wrapT =
        THREE.RepeatWrapping;

      seaTexture.repeat.set(
        16,
        16
      );

      seaTexture.anisotropy =
        8;

      seaTexture.colorSpace =
        THREE.SRGBColorSpace;

      seaTexture.needsUpdate =
        true;
    },
    [
      sandTexture,
      seaTexture,
    ]
  );

  return (
    <>
      {/* =====================================================
          BRIGHT MEDITERRANEAN SKY
      ===================================================== */}

      <Sky
        distance={450000}
        sunPosition={[
          -55,
          110,
          38,
        ]}
        turbidity={1.35}
        rayleigh={0.34}
        mieCoefficient={0.0022}
        mieDirectionalG={0.72}
      />


      {/* =====================================================
          VERY LIGHT COASTAL HAZE

          The fog is deliberately pushed far into the distance
          so Level 3 remains bright, colourful and clear.
      ===================================================== */}

      <fog
        attach="fog"
        args={[
          "#c8efff",
          240,
          560,
        ]}
      />


      {/* =====================================================
          SKY / BEACH FILL LIGHT
      ===================================================== */}

      <hemisphereLight
        intensity={1.32}
        color="#d7f2ff"
        groundColor="#e6c27a"
      />


      {/* =====================================================
          STRONG WARM MEDITERRANEAN SUN
      ===================================================== */}

      <directionalLight
        position={[
          -55,
          110,
          38,
        ]}
        intensity={2.18}
        color="#ffebbd"
        castShadow={false}
      />


      {/* =====================================================
          SOFT WARM FILL LIGHT

          Prevents cars, palms and the promenade from becoming
          too dark on the side facing away from the sun.
      ===================================================== */}

      <ambientLight
        intensity={0.72}
        color="#fff4dc"
      />


      {/* =====================================================
          SEA

          Uses /public/textures/sea.png.
          A light cyan tint keeps the water vivid and tropical.
      ===================================================== */}

      <mesh
        rotation={[
          -Math.PI / 2,
          0,
          0,
        ]}
        position={[
          0,
          -0.09,
          0,
        ]}
        receiveShadow={false}
      >
        <planeGeometry
          args={[
            700,
            700,
          ]}
        />

        <meshStandardMaterial
          map={seaTexture}
          color="#38cddd"
          roughness={0.32}
          metalness={0.02}
          emissive="#087487"
          emissiveIntensity={0.025}
        />
      </mesh>


      {/* =====================================================
          MAIN SAND

          The white tint means the original sand texture keeps
          its natural colour instead of being darkened.
      ===================================================== */}

      <mesh
        rotation={[
          -Math.PI / 2,
          0,
          0,
        ]}
        position={[
          0,
          -0.012,
          0,
        ]}
        receiveShadow={false}
      >
        <circleGeometry
          args={[
            135,
            96,
          ]}
        />

        <meshStandardMaterial
          map={sandTexture}
          color="#eac978"
          roughness={0.88}
          metalness={0}
        />
      </mesh>


      {/* =====================================================
          BEACH BAND OUTSIDE THE PROMENADE
      ===================================================== */}

      <mesh
        rotation={[
          -Math.PI / 2,
          0,
          0,
        ]}
        position={[
          0,
          0.01,
          0,
        ]}
        receiveShadow={false}
      >
        <ringGeometry
          args={[
            126,
            137,
            160,
          ]}
        />

        <meshStandardMaterial
          map={sandTexture}
          color="#edcd7f"
          roughness={0.89}
          metalness={0}
        />
      </mesh>


      {/* =====================================================
          WARMER SHORELINE BAND
      ===================================================== */}

      <mesh
        rotation={[
          -Math.PI / 2,
          0,
          0,
        ]}
        position={[
          0,
          -0.02,
          0,
        ]}
        receiveShadow={false}
      >
        <ringGeometry
          args={[
            130,
            137,
            96,
          ]}
        />

        <meshStandardMaterial
          map={sandTexture}
          color="#e3bd68"
          roughness={0.91}
          metalness={0}
        />
      </mesh>


      {/* =====================================================
          BEACHFRONT

          Circuit
          -> Armco
          -> wide promenade
          -> palm trees / white railing
          -> sand
          -> sea
      ===================================================== */}

      <LevelThreePromenade />

      <LevelThreePromenadeRailing />

      <LevelThreePalmTrees />

      <LevelThreePromenadeBenches />

      <LevelThreePromenadeFurniture />
    </>
  );
}

/* =========================================================
   LEVEL 1 - COUNTRYSIDE ENVIRONMENT
========================================================= */

function LevelOneEnvironment() {
  return (
    <>
      <Environment
        files="/assets/rural_asphalt_road_2k.hdr"
        background
        environmentIntensity={0.55}
        backgroundIntensity={0.85}
      />

      <fog
        attach="fog"
        args={[
          "#b9ddf5",
          90,
          190,
        ]}
      />

      <hemisphereLight
        args={[
          "#b9ddff",
          "#536b3c",
          1.4,
        ]}
      />

      <directionalLight
        position={[
          60,
          80,
          40,
        ]}
        intensity={2.8}
        color="#fff4df"
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-near={0.5}
        shadow-camera-far={180}
        shadow-camera-left={-60}
        shadow-camera-right={60}
        shadow-camera-top={60}
        shadow-camera-bottom={-60}
        shadow-bias={-0.0002}
      />

      <ambientLight
        intensity={0.18}
      />

      <TracksideTrees />
    </>
  );
}


function GameReadySignal({
  onReady,
}) {
  const framesRendered =
    useRef(0);

  const hasReportedReady =
    useRef(false);

  useFrame(() => {
    if (
      hasReportedReady.current
    ) {
      return;
    }

    framesRendered.current += 1;

    /*
      Wait for several genuine Three.js
      frames before starting countdown.
    */
    if (
      framesRendered.current >= 5
    ) {
      hasReportedReady.current =
        true;

      onReady();
    }
  });

  return null;
}

/* =========================================================
   LEVEL 3 - FINAL VICTORY CELEBRATION
   Lightweight confetti + fireworks rendered in Three.js.
========================================================= */

function VictoryConfettiBurst({
  origin,
  delay = 0,
}) {
  const pointsRef =
    useRef(null);

  const startRef =
    useRef(null);

  const particleCount =
    90;

  const data =
    useMemo(
      () => {
        const positions =
          new Float32Array(
            particleCount * 3
          );

        const velocities = [];
        const colors =
          new Float32Array(
            particleCount * 3
          );

        const palette = [
          new THREE.Color("#ffcf33"),
          new THREE.Color("#ff4f81"),
          new THREE.Color("#28d7ff"),
          new THREE.Color("#7cff72"),
          new THREE.Color("#ffffff"),
          new THREE.Color("#b67cff"),
        ];

        for (
          let i = 0;
          i < particleCount;
          i += 1
        ) {
          const base =
            i * 3;

          positions[base] =
            origin[0];

          positions[base + 1] =
            origin[1];

          positions[base + 2] =
            origin[2];

          const angle =
            Math.random() *
            Math.PI *
            2;

          const outward =
            3.5 +
            Math.random() *
              5.5;

          velocities.push(
            new THREE.Vector3(
              Math.cos(angle) *
                outward,
              6.5 +
                Math.random() *
                  7,
              Math.sin(angle) *
                outward
            )
          );

          const color =
            palette[
              Math.floor(
                Math.random() *
                  palette.length
              )
            ];

          colors[base] =
            color.r;

          colors[base + 1] =
            color.g;

          colors[base + 2] =
            color.b;
        }

        return {
          positions,
          velocities,
          colors,
        };
      },
      [
        origin[0],
        origin[1],
        origin[2],
      ]
    );

  useFrame(
    (
      state,
      delta
    ) => {
      if (
        !pointsRef.current
      ) {
        return;
      }

      if (
        startRef.current ===
        null
      ) {
        startRef.current =
          state.clock.elapsedTime +
          delay;
      }

      const elapsed =
        state.clock.elapsedTime -
        startRef.current;

      const material =
        pointsRef.current
          .material;

      if (
        elapsed < 0 ||
        elapsed > 4.8
      ) {
        material.opacity =
          0;

        return;
      }

      material.opacity =
        Math.min(
          1,
          (4.8 - elapsed) *
            0.7
        );

      const attribute =
        pointsRef.current
          .geometry
          .attributes
          .position;

      for (
        let i = 0;
        i < particleCount;
        i += 1
      ) {
        const base =
          i * 3;

        const velocity =
          data.velocities[i];

        attribute.array[base] +=
          velocity.x *
          delta;

        attribute.array[
          base + 1
        ] +=
          velocity.y *
          delta;

        attribute.array[
          base + 2
        ] +=
          velocity.z *
          delta;

        velocity.y -=
          9.5 *
          delta;

        velocity.x *=
          0.992;

        velocity.z *=
          0.992;
      }

      attribute.needsUpdate =
        true;
    }
  );

  return (
    <points
      ref={pointsRef}
      frustumCulled={false}
    >
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={
            data.positions
          }
          count={
            particleCount
          }
          itemSize={3}
        />

        <bufferAttribute
          attach="attributes-color"
          array={
            data.colors
          }
          count={
            particleCount
          }
          itemSize={3}
        />
      </bufferGeometry>

      <pointsMaterial
        size={0.34}
        vertexColors
        transparent
        opacity={0}
        depthWrite={false}
        sizeAttenuation
      />
    </points>
  );
}


function useFireworkAudio(active = false) {
  const audioRef = useRef(null);
  const crowdPlayedRef = useRef(false);
  const crowdLoadRef = useRef(null);
  const activeRef = useRef(active);
  const sourcesRef = useRef(new Set());

  useEffect(() => {
    activeRef.current = active;
    if (!active) {
      crowdPlayedRef.current = false;
      sourcesRef.current.forEach((source) => source.stop());
      sourcesRef.current.clear();
    }
  }, [active]);

  useEffect(() => {
    const unlock = () => {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      if (!audioRef.current) audioRef.current = new AudioContextClass();
      if (!crowdLoadRef.current) {
        const context = audioRef.current;
        crowdLoadRef.current = fetch('/assets/crowd-cheer.mp3')
          .then((response) => {
            if (!response.ok) throw new Error('Crowd audio could not be loaded');
            return response.arrayBuffer();
          })
          .then((data) => context.decodeAudioData(data))
          .catch(() => { crowdLoadRef.current = null; return null; });
      }
      if (audioRef.current.state === "suspended") {
        audioRef.current.resume().catch(() => {});
      }
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      const context = audioRef.current;
      audioRef.current = null;
      if (context && context.state !== "closed") context.close().catch(() => {});
    };
  }, []);

  return useCallback((explosion = false) => {
    const context = audioRef.current;
    if (!context || context.state !== "running" || document.hidden) return;
    const now = context.currentTime;
    if (explosion === "crowd" && !crowdPlayedRef.current) {
      crowdPlayedRef.current = true;
      // Real crowd recording by Gregor Quendel (CC BY 4.0).
      crowdLoadRef.current?.then((cheer) => {
        if (!cheer || !activeRef.current || context.state !== 'running') return;
        const startTime = context.currentTime;
        const duration = Math.min(5, cheer.duration);
        const crowdSource = context.createBufferSource();
        crowdSource.buffer = cheer;
        const crowdGain = context.createGain();
        crowdGain.gain.setValueAtTime(0, startTime);
        crowdGain.gain.linearRampToValueAtTime(0.18, startTime + 0.15);
        crowdGain.gain.setValueAtTime(0.18, startTime + Math.max(0.15, duration - 1.2));
        crowdGain.gain.linearRampToValueAtTime(0, startTime + duration);
        crowdSource.connect(crowdGain).connect(context.destination);
        sourcesRef.current.add(crowdSource);
        crowdSource.onended = () => {
          sourcesRef.current.delete(crowdSource);
          crowdSource.disconnect();
          crowdGain.disconnect();
        };
        crowdSource.start(startTime);
        crowdSource.stop(startTime + duration);
      });
    }
    if (explosion === "crowd") return;
    const duration = explosion ? 3.2 : 0.8;
    const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * duration), context.sampleRate);
    const samples = buffer.getChannelData(0);
    let rumble = 0;
    let hiss = 0;
    const crackles = explosion ? Array.from({ length: 26 }, () => ({
      time: 0.18 + Math.random() * 1.7,
      strength: 0.08 + Math.random() * 0.18,
      decay: 35 + Math.random() * 65,
    })) : [];
    for (let i = 0; i < samples.length; i += 1) {
      const t = i / context.sampleRate;
      const noise = Math.random() * 2 - 1;
      rumble += (noise - rumble) * (1 - Math.exp(-2 * Math.PI * 150 / context.sampleRate));
      hiss += (noise - hiss) * (1 - Math.exp(-2 * Math.PI * 2800 / context.sampleRate));
      if (explosion) {
        // Sharp report, deep rolling tail and scattered secondary detonations.
        const report = noise * Math.exp(-t * 65) * 0.8;
        const body = rumble * Math.exp(-t * 2.8) * 3.5;
        let crackle = 0;
        for (const pop of crackles) {
          const age = t - pop.time;
          if (age >= 0) crackle += noise * pop.strength * Math.exp(-age * pop.decay);
        }
        samples[i] = report + body + crackle;
      } else {
        // Short ignition followed by an airy rocket rush, without a tonal sweep.
        const envelope = Math.sin(Math.PI * t / duration) ** 2;
        samples[i] = hiss * envelope * 0.55 + noise * Math.exp(-t * 90) * 0.25;
      }
    }
    if (explosion) {
      // Quiet, diffuse echoes instead of the previous regular buzzing crackle.
      const dry = samples.slice();
      for (const [delay, level] of [[0.14, 0.2], [0.31, 0.12], [0.53, 0.07]]) {
        const offset = Math.round(delay * context.sampleRate);
        for (let i = offset; i < samples.length; i += 1) {
          samples[i] += dry[i - offset] * level;
        }
      }
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    const filter = context.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(explosion ? 6500 : 3200, now);
    filter.frequency.exponentialRampToValueAtTime(explosion ? 1800 : 2200, now + duration);
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(explosion ? 0.5 : 0.12, now + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    source.connect(filter).connect(gain).connect(context.destination);
    source.onended = () => {
      sourcesRef.current.delete(source);
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
    };
    sourcesRef.current.add(source);
    source.start(now);
    source.stop(now + duration);
  }, []);
}

function VictoryFirework({
  position,
  color,
  delay = 0,
  repeatEvery = 4.8,
  playSound,
}) {
  const pointsRef =
    useRef(null);

  const rocketRef =
    useRef(null);

  const startRef =
    useRef(null);

  const lastCycleRef =
    useRef(-1);

  const explosionCycleRef = useRef(-1);

  const particleCount =
    144;

  const burstData =
    useMemo(
      () => {
        const positions =
          new Float32Array(
            particleCount * 3
          );

        const directions = [];
        const colors = new Float32Array(particleCount * 3);
        const baseColor = new THREE.Color(color);
        const baseHue = baseColor.getHSL({ h: 0, s: 0, l: 0 }).h;

        for (
          let i = 0;
          i < particleCount;
          i += 1
        ) {
          const sparkColor = new THREE.Color().setHSL(
            (baseHue + (i % 5) * 0.19) % 1,
            1,
            i % 7 === 0 ? 0.85 : 0.6
          );
          sparkColor.toArray(colors, i * 3);
          const theta =
            Math.random() *
            Math.PI *
            2;

          const phi =
            Math.acos(
              2 *
                Math.random() -
                1
            );

          const speed =
            5.5 +
            Math.random() *
              8.5;

          directions.push(
            new THREE.Vector3(
              Math.sin(phi) *
                Math.cos(theta) *
                speed,
              Math.cos(phi) *
                speed,
              Math.sin(phi) *
                Math.sin(theta) *
                speed
            )
          );
        }

        return {
          positions,
          directions,
          colors,
        };
      },
      [color]
    );

  const velocitiesRef =
    useRef([]);

  const resetBurst =
    () => {
      if (
        !pointsRef.current
      ) {
        return;
      }

      const attribute =
        pointsRef.current
          .geometry
          .attributes
          .position;

      velocitiesRef.current =
        burstData.directions.map(
          (direction) =>
            direction.clone()
        );

      for (
        let i = 0;
        i < particleCount;
        i += 1
      ) {
        const base =
          i * 3;

        attribute.array[base] =
          position[0];

        attribute.array[
          base + 1
        ] =
          position[1];

        attribute.array[
          base + 2
        ] =
          position[2];
      }

      attribute.needsUpdate =
        true;
    };

  useFrame(
    (
      state,
      delta
    ) => {
      if (
        !pointsRef.current ||
        !rocketRef.current
      ) {
        return;
      }

      if (
        startRef.current ===
        null
      ) {
        startRef.current =
          state.clock.elapsedTime +
          delay;
      }

      const elapsed =
        state.clock.elapsedTime -
        startRef.current;

      if (elapsed < 0) {
        pointsRef.current.material.opacity =
          0;

        rocketRef.current.visible =
          false;

        return;
      }

      const cycle =
        Math.floor(
          elapsed /
            repeatEvery
        );

      const cycleTime =
        elapsed -
        cycle *
          repeatEvery;

      if (
        cycle !==
        lastCycleRef.current
      ) {
        lastCycleRef.current =
          cycle;

        resetBurst();
        if (cycleTime < 0.85) playSound?.(false);
      }

      /*
        First 0.85 s: a bright rocket rises into the sky.
        Then the particle shell explodes for ~2.4 s.
      */
      const launchDuration =
        0.85;

      const explosionTime =
        cycleTime -
        launchDuration;

      if (
        cycleTime <
        launchDuration
      ) {
        rocketRef.current.visible =
          true;

        const p =
          cycleTime /
          launchDuration;

        rocketRef.current.position.set(
          position[0],
          THREE.MathUtils.lerp(
            3,
            position[1],
            p
          ),
          position[2]
        );

        pointsRef.current.material.opacity =
          0;

        return;
      }

      rocketRef.current.visible =
        false;

      if (explosionCycleRef.current !== cycle && explosionTime <= 2.55) {
        explosionCycleRef.current = cycle;
        playSound?.(true);
      }

      if (
        explosionTime >
        2.55
      ) {
        pointsRef.current.material.opacity =
          0;

        return;
      }

      pointsRef.current.material.opacity =
        Math.max(
          0,
          1 -
            explosionTime /
              2.55
        );

      const attribute =
        pointsRef.current
          .geometry
          .attributes
          .position;

      for (
        let i = 0;
        i < particleCount;
        i += 1
      ) {
        const base =
          i * 3;

        const velocity =
          velocitiesRef.current[i];

        if (!velocity) {
          continue;
        }

        attribute.array[base] +=
          velocity.x *
          delta;

        attribute.array[
          base + 1
        ] +=
          velocity.y *
          delta;

        attribute.array[
          base + 2
        ] +=
          velocity.z *
          delta;

        velocity.y -=
          2.65 *
          delta;

        velocity.multiplyScalar(
          0.988
        );
      }

      attribute.needsUpdate =
        true;
    }
  );

  return (
    <group>
      <mesh
        ref={rocketRef}
        visible={false}
      >
        <sphereGeometry
          args={[
            0.18,
            8,
            8,
          ]}
        />

        <meshBasicMaterial
          color={color}
          toneMapped={false}
        />
      </mesh>

      <points
        ref={pointsRef}
        frustumCulled={false}
      >
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            array={
              burstData.positions
            }
            count={
              particleCount
            }
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            args={[burstData.colors, 3]}
          />
        </bufferGeometry>

        <pointsMaterial
          size={0.7}
          vertexColors
          transparent
          opacity={0}
          depthWrite={false}
          blending={
            THREE.AdditiveBlending
          }
          toneMapped={false}
          sizeAttenuation
        />
      </points>
    </group>
  );
}


function LevelThreeVictoryCelebration({
  showFinalVictoryPanel = false,
  playSound,
}) {
  const curve =
    useRaceTrackCurve();

  const finishPoint =
    useMemo(
      () =>
        curve.getPointAt(
          0.995
        ),
      [
        curve,
      ]
    );

  const fireworks = useMemo(() => {
    const forward = curve.getTangentAt(0.995).normalize();
    const side = new THREE.Vector3(-forward.z, 0, forward.x);

    // Keep the bursts in the sky ahead of the pulled-back victory camera.
    return [
      [-30, 85, 22, "#ffd84d", 0, 5.1],
      [30, 100, 28, "#40ddff", 0.8, 5.7],
      [-15, 115, 34, "#ff5da2", 1.6, 6.2],
      [15, 95, 25, "#9cff72", 2.4, 5.4],
      [0, 130, 38, "#c58cff", 3.2, 6.5],
      [-38, 110, 30, "#ff8b3d", 4, 5.9],
    ].map(([offset, distance, height, color, delay, repeatEvery]) => {
      const point = finishPoint.clone()
        .addScaledVector(forward, distance)
        .addScaledVector(side, offset);
      point.y = height;

      return { position: point.toArray(), color, delay: 4.5 + delay, repeatEvery };
    });
  }, [curve, finishPoint]);

  /*
    Confetti erupts beside the finish area.
    Fireworks are deliberately farther out and higher up so they
    appear over the beach/sea rather than directly on the circuit.
  */
  return (
    <group>
      {!showFinalVictoryPanel && (
        <>
          <VictoryConfettiBurst
            origin={[
              finishPoint.x - 8,
              1.4,
              finishPoint.z,
            ]}
            delay={0}
          />

          <VictoryConfettiBurst
            origin={[
              finishPoint.x + 8,
              1.4,
              finishPoint.z,
            ]}
            delay={0.18}
          />

          <VictoryConfettiBurst
            origin={[
              finishPoint.x,
              1.8,
              finishPoint.z + 8,
            ]}
            delay={0.38}
          />
        </>
      )}

      {showFinalVictoryPanel && (
        <>
          {fireworks.map((firework, index) => (
            <VictoryFirework key={index} {...firework} playSound={playSound} />
          ))}
        </>
      )}
    </group>
  );
}


/* =========================================================
   LEVEL 3 - VICTORY DUSK
   Starts exactly when the GAME COMPLETE panel appears.
========================================================= */

function LevelThreeVictorySunset({
  active = false,
}) {
  const { gl, scene } =
    useThree();

  const amountRef =
    useRef(0);

  const originalBackgroundRef =
    useRef(null);

  useEffect(
    () => {
      if (
        originalBackgroundRef.current ===
        null
      ) {
        originalBackgroundRef.current =
          scene.background &&
          scene.background.isColor
            ? scene.background.clone()
            : null;
      }

      return () => {
        gl.toneMappingExposure =
          1.10;

        if (
          originalBackgroundRef.current
        ) {
          scene.background =
            originalBackgroundRef.current.clone();
        }
      };
    },
    [
      gl,
      scene,
    ]
  );

  useFrame(
    (
      state,
      delta
    ) => {
      const target =
        active
          ? 1
          : 0;

      /*
        A slower cinematic fade: approximately 4-5 seconds
        from Mediterranean daylight to proper night.
      */
      amountRef.current =
        THREE.MathUtils.damp(
          amountRef.current,
          target,
          0.72,
          delta
        );

      const amount =
        amountRef.current;

      gl.toneMappingExposure =
        THREE.MathUtils.lerp(
          1.10,
          0.16,
          amount
        );

      /*
        Fade the clear sky itself to a deep navy night.
        This is important: reducing exposure alone left the
        background looking like a static daytime screenshot.
      */
      const daySky =
        new THREE.Color(
          "#d9efff"
        );

      const nightSky =
        new THREE.Color(
          "#020611"
        );

      const skyColor =
        daySky.clone().lerp(
          nightSky,
          amount
        );

      scene.background =
        skyColor;
    }
  );

  if (!active) {
    return null;
  }

  return (
    <>
      {/* Very subtle moonlight so silhouettes remain visible */}
      <hemisphereLight
        intensity={0.055}
        color="#718bc8"
        groundColor="#090b12"
      />

      <directionalLight
        position={[
          -55,
          40,
          35,
        ]}
        intensity={0.075}
        color="#9bb7ff"
      />
    </>
  );
}


function GameWorld({
  currentLevel,
  raceRunId,
  onProgress,
  playerSpeed,
  raceStatus,
  countdown,
  showGo,
  onReady,
  finalVictory = false,
  showFinalVictoryPanel = false,
}) {
  const playFireworkSound = useFireworkAudio(currentLevel === 3 && finalVictory);

  useEffect(() => {
    if (currentLevel === 3 && finalVictory) {
      playFireworkSound("crowd");
    }
  }, [currentLevel, finalVictory, playFireworkSound]);

  return (
    <RaceTrackLevelContext.Provider
      value={
        currentLevel
      }
    >
      <GameReadySignal
        onReady={
          onReady
        }
      />

      {currentLevel === 1 ? (
        <LevelOneEnvironment />
      ) : currentLevel === 2 ? (
        <LevelTwoEnvironment />
      ) : (
        <LevelThreeEnvironment />
      )}

      <Track />

      <FinishLine />

      <StartTrafficLights
        raceStatus={
          raceStatus
        }
        countdown={
          countdown
        }
        showGo={
          showGo
        }
      />

      <Race
        key={
          `${currentLevel}-${raceRunId}`
        }
        onProgress={
          onProgress
        }
        playerSpeed={
          playerSpeed
        }
        raceStatus={
          raceStatus
        }
        currentLevel={
          currentLevel
        }

        finalVictory={
          finalVictory
        }
      />

      {currentLevel === 3 && (
        <LevelThreeVictorySunset
          active={
            finalVictory &&
            showFinalVictoryPanel
          }
        />
      )}

      {finalVictory &&
        currentLevel === 3 && (
          <LevelThreeVictoryCelebration
            playSound={playFireworkSound}
            showFinalVictoryPanel={
              showFinalVictoryPanel
            }
          />
        )}
    </RaceTrackLevelContext.Provider>
  );
}


/* =========================================================
   MINI MAP
========================================================= */

function MiniMap({
  raceData,
  currentLevel,
}) {
  /*
    The old minimap used a separately hand-drawn path and a
    short array of approximate marker positions. It is kept
    here as a reference, but the live map below is generated
    directly from the same Three.js curve as the road.

  const trackPath =
    currentLevel === 2
      ? "M 60 12 L 24 12 " +
        "C 11 12, 5 24, 7 38 " +
        "C 8 48, 17 56, 27 58 " +
        "L 23 70 C 22 83, 38 91, 55 89 " +
        "C 70 87, 78 78, 76 66 " +
        "C 91 62, 96 50, 93 37 " +
        "C 91 24, 79 14, 60 12 Z"
      : "M 50 20 " +
        "C 20 20, 15 50, 25 70 " +
        "C 35 90, 70 92, 82 70 " +
        "C 95 48, 83 23, 50 20 Z";


  const levelOneMapPoints = [
    {
      x: 50,
      y: 20,
    },

    {
      x: 35,
      y: 22,
    },

    {
      x: 22,
      y: 35,
    },

    {
      x: 20,
      y: 55,
    },

    {
      x: 28,
      y: 72,
    },

    {
      x: 48,
      y: 83,
    },

    {
      x: 68,
      y: 80,
    },

    {
      x: 82,
      y: 66,
    },

    {
      x: 87,
      y: 48,
    },

    {
      x: 78,
      y: 30,
    },

    {
      x: 60,
      y: 22,
    },
  ];


  const levelTwoMapPoints = [
    { x: 60, y: 12 },
    { x: 45, y: 12 },
    { x: 30, y: 13 },
    { x: 18, y: 17 },
    { x: 9, y: 27 },
    { x: 6, y: 39 },
    { x: 8, y: 51 },
    { x: 16, y: 61 },
    { x: 25, y: 66 },
    { x: 31, y: 72 },
    { x: 30, y: 82 },
    { x: 37, y: 89 },
    { x: 50, y: 92 },
    { x: 64, y: 90 },
    { x: 76, y: 84 },
    { x: 84, y: 75 },
    { x: 85, y: 65 },
    { x: 90, y: 57 },
    { x: 96, y: 52 },
    { x: 98, y: 41 },
    { x: 95, y: 29 },
    { x: 88, y: 20 },
    { x: 76, y: 14 },
  ];


  const mapPoints =
    currentLevel === 2
      ? levelTwoMapPoints
      : levelOneMapPoints;


  const getPosition =
    (progress) => {
      const normalised =
        (
          (
            progress %
            1
          ) +
          1
        ) %
        1;


      const scaled =
        normalised *
        mapPoints.length;


      const index =
        Math.floor(
          scaled
        );


      const nextIndex =
        (
          index +
          1
        ) %
        mapPoints.length;


      const amount =
        scaled -
        index;


      const current =
        mapPoints[
          index
        ];


      const next =
        mapPoints[
          nextIndex
        ];


      return {
        x:
          current.x +
          (
            next.x -
            current.x
          ) *
          amount,

        y:
          current.y +
          (
            next.y -
            current.y
          ) *
          amount,
      };
    };


  */


  const miniMapGeometry =
    useMemo(
      () => {
        const curve =
          createRaceTrackCurve(
            currentLevel
          );


        const sampleCount =
          180;


        const worldPoints = [];


        for (
          let i = 0;
          i <= sampleCount;
          i++
        ) {
          worldPoints.push(
            curve.getPointAt(
              i / sampleCount
            )
          );
        }


        const xs =
          worldPoints.map(
            (point) =>
              point.x
          );


        const zs =
          worldPoints.map(
            (point) =>
              point.z
          );


        const minX =
          Math.min(...xs);

        const maxX =
          Math.max(...xs);

        const minZ =
          Math.min(...zs);

        const maxZ =
          Math.max(...zs);


        const padding =
          8;


        const scale =
          Math.min(
            (
              100 -
              padding * 2
            ) /
              Math.max(
                maxX - minX,
                0.0001
              ),

            (
              100 -
              padding * 2
            ) /
              Math.max(
                maxZ - minZ,
                0.0001
              )
          );


        const centreX =
          (
            minX + maxX
          ) / 2;


        const centreZ =
          (
            minZ + maxZ
          ) / 2;


        const toMapPoint =
          (point) => ({
            x:
              50 +
              (
                point.x - centreX
              ) * scale,

            y:
              50 -
              (
                point.z - centreZ
              ) * scale,
          });


        const mappedPoints =
          worldPoints.map(
            toMapPoint
          );


        const trackPath =
          mappedPoints
            .map(
              (
                point,
                index
              ) =>
                `${
                  index === 0
                    ? "M"
                    : "L"
                } ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
            )
            .join(" ") +
          " Z";


        return {
          curve,
          toMapPoint,
          trackPath,
        };
      },
      [currentLevel]
    );


  const trackPath =
    miniMapGeometry.trackPath;


  const getPosition =
    (progress) => {
      const normalised =
        (
          (
            progress % 1
          ) + 1
        ) % 1;


      return miniMapGeometry
        .toMapPoint(
          miniMapGeometry
            .curve
            .getPointAt(
              normalised
            )
        );
    };


  const cars = [
    {
      id: "player",
      color: "#ffd900",
    },

    {
      id: "blue",
      color: "#008cff",
    },

    {
      id: "green",
      color: "#16c83b",
    },

    {
      id: "red",
      color: "#f02020",
    },
  ];


  return (
    <div
      className="mini-map"
    >
      <div
        className="mini-map-title"
      >
        RACE MAP
      </div>


      <svg
        viewBox="0 0 100 100"

        className="mini-map-svg"
      >

        <path
          d={
            trackPath
          }

          fill="none"

          stroke="#555"

          strokeWidth="10"

          strokeLinecap="round"

          strokeLinejoin="round"
        />


        <path
          d={
            trackPath
          }

          fill="none"

          stroke="#2d2d2d"

          strokeWidth="7"

          strokeLinecap="round"

          strokeLinejoin="round"
        />


        {(() => {
          /*
            Draw the start/finish line from the ACTUAL circuit
            start point rather than using fixed minimap coordinates.
            This keeps it correctly positioned on every level,
            including the reshaped Level 3 circuit.
          */
          const startWorld =
            miniMapGeometry
              .curve
              .getPointAt(0);

          const tangent =
            miniMapGeometry
              .curve
              .getTangentAt(0)
              .normalize();

          const normal =
            new THREE.Vector3(
              -tangent.z,
              0,
              tangent.x
            ).normalize();

          /*
            Make a short line across the road at the start point.
            ROAD_HALF_WIDTH gives us a world-space width that then
            maps naturally onto the minimap.
          */
          const halfLineWidth =
            ROAD_HALF_WIDTH *
            0.78;

          const leftWorld =
            startWorld
              .clone()
              .addScaledVector(
                normal,
                halfLineWidth
              );

          const rightWorld =
            startWorld
              .clone()
              .addScaledVector(
                normal,
                -halfLineWidth
              );

          const left =
            miniMapGeometry
              .toMapPoint(
                leftWorld
              );

          const right =
            miniMapGeometry
              .toMapPoint(
                rightWorld
              );

          return (
            <line
              x1={left.x}
              y1={left.y}
              x2={right.x}
              y2={right.y}
              stroke="#ffffff"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          );
        })()}


        {cars.map(
          (car) => {
            const data =
              raceData[
                car.id
              ];


            if (
              !data
            ) {
              return null;
            }


            const position =
              getPosition(
                data.progress
              );


            return (
              <circle
                key={
                  car.id
                }

                cx={
                  position.x
                }

                cy={
                  position.y
                }

                r={
                  car.id ===
                  "player"
                    ? 4
                    : 3
                }

                fill={
                  car.color
                }

                stroke="#ffffff"

                strokeWidth={
                  car.id ===
                  "player"
                    ? 1.5
                    : 1
                }
              />
            );
          }
        )}

      </svg>
    </div>
  );
}


/* =========================================================
   MAIN GAME
========================================================= */

export default function MathsRacer() {
      const [
    showHowToPlay,
    setShowHowToPlay,
  ] = useState(false);

  const [
    miniMapData,
    setMiniMapData,
  ] =
    useState(
      createInitialRaceData()
    );


  const raceDataRef =
    useRef(
      createInitialRaceData()
    );


  const [
  raceStatus,
  setRaceStatus,
] =
  useState(
    "menu"
  );

const [
  showReady,
  setShowReady,
] = useState(false);

  const [
    countdown,
    setCountdown,
  ] =
    useState(
      3
    );


  const [
    showGo,
    setShowGo,
  ] =
    useState(
      false
    );

    const [
  gameReady,
  setGameReady,
] = useState(false);


  const [
    currentLevel,
    setCurrentLevel,
  ] =
    useState(
      1
    );


  const [
    raceRunId,
    setRaceRunId,
  ] =
    useState(
      0
    );


  const [
    finalPosition,
    setFinalPosition,
  ] =
    useState(
      null
    );


  const [
    finalVictory,
    setFinalVictory,
  ] =
    useState(false);


  const [
    showFinalVictoryPanel,
    setShowFinalVictoryPanel,
  ] =
    useState(false);


  const finalVictoryTimerRef =
    useRef(null);


  const finishHandledRef =
    useRef(
      false
    );

  const [
    playerLap,
    setPlayerLap,
  ] =
    useState(
      1
    );


  const [
    playerPosition,
    setPlayerPosition,
  ] =
    useState(
      3
    );


  const [
    raceFinished,
    setRaceFinished,
  ] =
    useState(
      false
    );


  const playerSpeeds =
    currentLevel === 3
      ? LEVEL_3_PLAYER_SPEEDS
      : currentLevel === 2
      ? LEVEL_2_PLAYER_SPEEDS
      : LEVEL_1_PLAYER_SPEEDS;


  const [
    playerSpeed,
    setPlayerSpeed,
  ] =
    useState(
      LEVEL_1_PLAYER_SPEEDS.base
    );


  const [
    speedState,
    setSpeedState,
  ] =
    useState(
      "normal"
    );


  useEffect(
    () => {
      setPlayerSpeed(
        currentLevel === 3
          ? LEVEL_3_PLAYER_SPEEDS.base
          : currentLevel === 2
          ? LEVEL_2_PLAYER_SPEEDS.base
          : LEVEL_1_PLAYER_SPEEDS.base
      );

      setSpeedState(
        "normal"
      );
    },
    [currentLevel]
  );


  const [
    currentQuestionIndex,
    setCurrentQuestionIndex,
  ] =
    useState(
      0
    );


  const [
    answer,
    setAnswer,
  ] =
    useState(
      ""
    );


  const [
    feedback,
    setFeedback,
  ] =
    useState(
      ""
    );


  const [
    streak,
    setStreak,
  ] =
    useState(
      0
    );


  const speedTimerRef =
    useRef(
      null
    );


  const currentQuestion =
    mathsQuestions[
      currentQuestionIndex
    ];


    
/* =======================================================
   READY
======================================================= */

useEffect(() => {
  if (
    raceStatus !== "ready"
  ) {
    return;
  }

  const timer =
    setTimeout(() => {
      setShowReady(false);
      setRaceStatus("countdown");
    }, 1200);

  return () => {
    clearTimeout(timer);
  };
}, [raceStatus]);

 /* =======================================================
   COUNTDOWN
======================================================= */

useEffect(() => {
  if (
    raceStatus !== "countdown"
  ) {
    return;
  }

  setCountdown(3);

  const timer2 =
    setTimeout(() => {
      setCountdown(2);
    }, 1000);

  const timer1 =
    setTimeout(() => {
      setCountdown(1);
    }, 2000);

  const timerGo =
    setTimeout(() => {
      setCountdown(0);
      setShowGo(true);
      setRaceStatus("racing");
    }, 3000);

  return () => {
    clearTimeout(timer2);
    clearTimeout(timer1);
    clearTimeout(timerGo);
  };
}, [raceStatus]);

/* =======================================================
   HIDE GO
======================================================= */

useEffect(() => {
  if (!showGo) {
    return;
  }

  const timer =
    setTimeout(() => {
      setShowGo(false);
    }, 900);

  return () => {
    clearTimeout(timer);
  };
}, [showGo]);


  /* =======================================================
     P KEY — PAUSE / RESUME
  ======================================================= */

  useEffect(
    () => {
      const handlePauseKey =
        (event) => {
          /*
            Don't repeatedly pause/resume
            if P is held down.
          */

          if (
            event.repeat
          ) {
            return;
          }


          if (
            event.key
              .toLowerCase() !==
            "p"
          ) {
            return;
          }


          event.preventDefault();


          setRaceStatus(
            (previousStatus) => {
              /*
                RACING → PAUSED
              */

              if (
                previousStatus ===
                "racing"
              ) {
                return "paused";
              }


              /*
                PAUSED → RACING
              */

              if (
                previousStatus ===
                "paused"
              ) {
                return "racing";
              }


              /*
                P does nothing during
                countdown / finished.
              */

              return previousStatus;
            }
          );
        };


      window.addEventListener(
        "keydown",
        handlePauseKey
      );


      return () => {
        window.removeEventListener(
          "keydown",
          handlePauseKey
        );
      };
    },
    []
  );


  /* =======================================================
     RESET SPEED
  ======================================================= */

  const resetSpeedLater =
    (duration) => {
      if (
        speedTimerRef.current
      ) {
        clearTimeout(
          speedTimerRef.current
        );
      }


      speedTimerRef.current =
        setTimeout(
          () => {
            setPlayerSpeed(
              playerSpeeds.base
            );


            setSpeedState(
              "normal"
            );


            setFeedback(
              ""
            );
          },
          duration
        );
    };


  /* =======================================================
     SUBMIT ANSWER
  ======================================================= */

  const submitAnswer =
    (event) => {
      event.preventDefault();


      if (
        !answer.trim() ||
        raceStatus !==
          "racing"
      ) {
        return;
      }


      const userAnswer =
        answer
          .trim()
          .toLowerCase();


      const correctAnswer =
        currentQuestion
          .answer
          .trim()
          .toLowerCase();


      if (
        userAnswer ===
        correctAnswer
      ) {
        setPlayerSpeed(
          playerSpeeds.boost
        );


        setSpeedState(
          "boost"
        );


        setFeedback(
          "BOOST!"
        );


        setStreak(
          (previous) =>
            previous +
            1
        );


        resetSpeedLater(
          BOOST_DURATION
        );
      } else {
        setPlayerSpeed(
          playerSpeeds.slow
        );


        setSpeedState(
          "slow"
        );


        setFeedback(
          `Wrong — ${currentQuestion.answer}`
        );


        setStreak(
          0
        );


        resetSpeedLater(
          SLOW_DURATION
        );
      }


      setAnswer(
        ""
      );


      setCurrentQuestionIndex(
        (previous) =>
          (
            previous +
            1
          ) %
          mathsQuestions.length
      );
    };


  /* =======================================================
     RACE PROGRESS
  ======================================================= */

  const handleProgress =
    useCallback(
      (
        id,
        data
      ) => {
        raceDataRef.current[
          id
        ] =
          data;

        /*
          PERFORMANCE PASS 3:
          AI-car progress is kept in the ref immediately, but it does
          not need to re-render the entire React game UI. The player
          callback acts as the single 10 Hz UI heartbeat and captures
          the latest positions of all four cars for the minimap.
        */
        if (
          id !==
          CAR_IDS.PLAYER
        ) {
          return;
        }


        setMiniMapData({
          ...raceDataRef.current,
        });


        const player =
          raceDataRef.current
            .player;


        const displayedLap =
          Math.max(
            1,

            Math.min(
              player.lap +
                1,
              TOTAL_LAPS
            )
          );


        setPlayerLap(
          displayedLap
        );


        /* POSITION */

        const cars =
          Object.entries(
            raceDataRef.current
          );


        cars.sort(
          (
            [
              ,
              carA,
            ],
            [
              ,
              carB,
            ]
          ) =>
            carB.totalProgress -
            carA.totalProgress
        );


        const playerIndex =
          cars.findIndex(
            (
              [
                carId,
              ]
            ) =>
              carId ===
              CAR_IDS.PLAYER
          );


        const currentPosition =
          playerIndex !== -1
            ? playerIndex + 1
            : 4;


        setPlayerPosition(
          currentPosition
        );


        /* FINISH */

        if (
          player.lap >=
            TOTAL_LAPS &&
          !finishHandledRef.current
        ) {
          finishHandledRef.current =
            true;

          setFinalPosition(
            currentPosition
          );

          const isFinalVictory =
            currentLevel === 3 &&
            currentPosition === 1;

          setFinalVictory(
            isFinalVictory
          );

          if (
            finalVictoryTimerRef.current
          ) {
            clearTimeout(
              finalVictoryTimerRef.current
            );
          }

          if (
            isFinalVictory
          ) {
            /*
              Let the camera pull back and the celebration play
              before showing the final GAME COMPLETE panel.
            */
            setShowFinalVictoryPanel(
              false
            );

            finalVictoryTimerRef.current =
              setTimeout(
                () => {
                  setShowFinalVictoryPanel(
                    true
                  );
                },
                5200
              );
          } else {
            setShowFinalVictoryPanel(
              true
            );
          }

          setRaceFinished(
            true
          );

          setRaceStatus(
            "finished"
          );

          setPlayerSpeed(
            0
          );
        }
      },
      [
        currentLevel,
      ]
    );


  /* =======================================================
     CLEAN UP TIMER
  ======================================================= */

  useEffect(
    () => {
      return () => {
        if (
          speedTimerRef.current
        ) {
          clearTimeout(
            speedTimerRef.current
          );
        }

        if (
          finalVictoryTimerRef.current
        ) {
          clearTimeout(
            finalVictoryTimerRef.current
          );
        }
      };
    },
    []
  );


  /* =======================================================
     DISPLAY SPEED
  ======================================================= */

const displayedSpeed =
  raceStatus !==
  "racing"
    ? 0
    : speedState ===
      "boost"
    ? 125
    : speedState ===
      "slow"
    ? 55
    : 80;


/* =======================================================
   POSITION TEXT
======================================================= */

const getOrdinalPosition =
  (position) => {
    switch (position) {
      case 1:
        return "1st";

      case 2:
        return "2nd";

      case 3:
        return "3rd";

      default:
        return "4th";
    }
  };


/* =======================================================
   RESET RACE
======================================================= */

// Temporary shortcut for previewing the complete Level 3 victory sequence.
const testLevelThreeVictory = () => {
  if (currentLevel !== 3 || raceFinished || finishHandledRef.current) return;
  const player = raceDataRef.current.player;
  const lead = Math.max(...Object.values(raceDataRef.current).map((car) => car.totalProgress));
  handleProgress(CAR_IDS.PLAYER, {
    ...player,
    lap: TOTAL_LAPS,
    totalProgress: Math.max(TOTAL_LAPS, lead + 1),
  });
  // Remount at the finish straight so the test camera faces the fireworks.
  setRaceRunId((previous) => previous + 1);
};

const resetRace = () => {
  if (
    speedTimerRef.current
  ) {
    clearTimeout(
      speedTimerRef.current
    );

    speedTimerRef.current =
      null;
  }


  const initialData =
    createInitialRaceData();


  raceDataRef.current =
    initialData;


  setMiniMapData({
    ...initialData,
  });


  finishHandledRef.current =
    false;


  setPlayerLap(
    1
  );

  setPlayerPosition(
    3
  );

  setFinalPosition(
    null
  );

  setFinalVictory(
    false
  );

  setShowFinalVictoryPanel(
    false
  );

  if (
    finalVictoryTimerRef.current
  ) {
    clearTimeout(
      finalVictoryTimerRef.current
    );

    finalVictoryTimerRef.current =
      null;
  }

  setRaceFinished(
    false
  );

  setPlayerSpeed(
    playerSpeeds.base
  );

  setSpeedState(
    "normal"
  );

  setCurrentQuestionIndex(
    0
  );

  setAnswer(
    ""
  );

  setFeedback(
    ""
  );

  setStreak(
    0
  );

  setCountdown(
    3
  );

  setShowGo(
    false
  );

  setShowReady(
    false
  );


  setRaceRunId(
    (
      previous
    ) =>
      previous + 1
  );
};


/* =======================================================
   BEGIN COUNTDOWN
======================================================= */

const beginCountdown = () => {
  setCountdown(
    3
  );

  setShowGo(
    false
  );

  setShowReady(
    true
  );

  setRaceStatus(
    "ready"
  );
};


/* =======================================================
   START GAME
======================================================= */

const startGame = () => {
  setShowHowToPlay(
    false
  );

  setCurrentLevel(
    1
  );

  resetRace();

  beginCountdown();
};


/* =======================================================
   TRY LEVEL AGAIN
======================================================= */

const retryLevel = () => {
  resetRace();

  beginCountdown();
};


/* =======================================================
   GO TO NEXT LEVEL
======================================================= */

const goToNextLevel = () => {
  if (
    currentLevel >= 3
  ) {
    return;
  }


  setCurrentLevel(
    (
      previous
    ) =>
      previous + 1
  );

  resetRace();

  beginCountdown();
};


/* =======================================================
   PLAY AGAIN FROM LEVEL 1
======================================================= */

const playAgain = () => {
  setCurrentLevel(
    1
  );

  resetRace();

  beginCountdown();
};


return (
    <div
      className="maths-racer"
    >

        {/* ===================================================
    WELCOME SCREEN
=================================================== */}

{raceStatus ===
  "menu" && (
  <div className="welcome-overlay">

    <div className="welcome-panel">

      <div className="welcome-logo">
        MATHS
        <span> RACER</span>
      </div>

      <div className="welcome-tagline">
        RACE. SOLVE. WIN.
      </div>

      <button
  className="
    welcome-button
    welcome-play-button
  "
  onClick={
    startGame
  }
  disabled={
    !gameReady
  }
>
  {gameReady
    ? "▶ PLAY LEVEL 1"
    : "LOADING..."}
</button>

<button
  className="
    welcome-button
    level-two-test-button
  "
  onClick={() => {
    setCurrentLevel(2);

    setRaceRunId(
      (previous) =>
        previous + 1
    );

    setRaceStatus(
      "ready"
    );

    setShowReady(
      true
    );
  }}
  disabled={
    !gameReady
  }
>
  🏙 JUMP TO LEVEL 2
</button>

<button
  className="
    welcome-button
    level-three-test-button
  "
  onClick={() => {
    setCurrentLevel(3);

    setRaceRunId(
      (previous) =>
        previous + 1
    );

    setRaceStatus(
      "ready"
    );

    setShowReady(
      true
    );
  }}
  disabled={
    !gameReady
  }
>
  🏖 JUMP TO LEVEL 3
</button>

      <button
        className="welcome-button"
        onClick={() =>
          setShowHowToPlay(
            true
          )
        }
      >
        HOW TO PLAY
      </button>

    </div>

  </div>
)}

{/* ===================================================
    HOW TO PLAY
=================================================== */}

{showHowToPlay && (
  <div className="how-to-play-overlay">

    <div className="how-to-play-panel">

      <button
        className="how-to-play-close"
        onClick={() =>
          setShowHowToPlay(
            false
          )
        }
      >
        ×
      </button>

      <div className="how-to-play-title">
        HOW TO PLAY
      </div>

      <div className="how-to-play-content">

        <div className="how-to-play-step">
          <span>🏎️</span>

          <div>
            <strong>
              RACE
            </strong>

            <p>
              Your yellow car races
              against three opponents.
            </p>
          </div>
        </div>

        <div className="how-to-play-step">
          <span>🧠</span>

          <div>
            <strong>
              SOLVE
            </strong>

            <p>
              Answer the maths
              questions as quickly
              as you can.
            </p>
          </div>
        </div>

        <div className="how-to-play-step">
          <span>⚡</span>

          <div>
            <strong>
              BOOST
            </strong>

            <p>
              Correct answers give
              your car a speed boost.
            </p>
          </div>
        </div>

        <div className="how-to-play-step">
          <span>🐌</span>

          <div>
            <strong>
              WATCH OUT!
            </strong>

            <p>
              Wrong answers slow
              your car down.
            </p>
          </div>
        </div>

        <div className="how-to-play-step">
          <span>🏁</span>

          <div>
            <strong>
              WIN
            </strong>

            <p>
              Complete {TOTAL_LAPS}
              {" "}laps and finish
              in 1st place to unlock
              the next level!
            </p>
          </div>
        </div>

      </div>

      <button
        className="
          welcome-button
          how-to-play-back
        "
        onClick={() =>
          setShowHowToPlay(
            false
          )
        }
      >
        BACK
      </button>

    </div>

  </div>
)}

    {/* ===================================================
    GAME HUD
    Hidden while welcome menu is showing
=================================================== */}

{raceStatus !== "menu" && (
  <>
    {/* ===============================================
        HUD
    =============================================== */}

    <div
      className="racer-hud"
    >
      <div
        className="racer-title"
      >
        MATHS RACER
      </div>


      <div>
        Level{" "}
        {currentLevel}
      </div>


      <div>
        Lap{" "}
        {playerLap}
        {" / "}
        {TOTAL_LAPS}
      </div>


      <div>
        Speed:{" "}
        {displayedSpeed}
        {" mph"}
      </div>


      <div>
        Streak:{" "}
        {streak}
      </div>

      {currentLevel === 3 &&
        (raceStatus === "racing" || raceStatus === "paused") && (
          <button
            type="button"
            onClick={testLevelThreeVictory}
            style={{ pointerEvents: "auto", cursor: "pointer" }}
            title="Temporary shortcut: finish Level 3 in first place"
          >
            END LEVEL 3 (TEST WIN)
          </button>
        )}
    </div>


    {/* ===============================================
        RACE POSITION
    =============================================== */}

    <div
      className={
        `race-position race-position-${playerPosition}`
      }
    >
      {getOrdinalPosition(
        playerPosition
      )}
    </div>


    {/* ===============================================
        MINI MAP
    =============================================== */}

    <MiniMap
      raceData={
        miniMapData
      }
      currentLevel={
        currentLevel
      }
    />
  </>
)}

{/* ===================================================
    READY
=================================================== */}

{showReady && (
  <div
    className="
      race-countdown
      race-ready
    "
  >
    GET READY
  </div>
)}

      {/* ===================================================
          COUNTDOWN
      =================================================== */}

      {raceStatus ===
        "countdown" && (
        <div
          className="race-countdown"

          key={
            countdown
          }
        >
          {countdown}
        </div>
      )}


      {/* ===================================================
          GO
      =================================================== */}

      {showGo && (
        <div
          className="
            race-countdown
            race-go
          "
        >
          GO!
        </div>
      )}


      {/* ===================================================
          PAUSED
      =================================================== */}

      {raceStatus ===
        "paused" && (
        <div
          className="race-pause-overlay"
        >
          <div
            className="race-pause-box"
          >
            <div
              className="race-pause-title"
            >
              PAUSED
            </div>


            <div
              className="race-pause-message"
            >
              Press P to resume
            </div>
          </div>
        </div>
      )}


      {/* ===================================================
          MATHS QUESTION
      =================================================== */}

      {raceStatus ===
  "racing" && (
  <div
    className="maths-question-panel"
  >
          <div
            className="question-label"
          >
            SOLVE TO BOOST
          </div>


          <div
            className="maths-question-text"
          >
            {
              currentQuestion
                .question
            }
          </div>


          <form
            onSubmit={
              submitAnswer
            }
          >
            <input
              type="text"

              value={
                answer
              }

              onChange={
                (event) =>
                  setAnswer(
                    event
                      .target
                      .value
                  )
              }

              placeholder="Type answer"

              autoFocus
            />


            <button
              type="submit"

              disabled={
                raceStatus ===
                "paused"
              }
            >
              ENTER
            </button>
          </form>


          {feedback && (
            <div
              className={
                `race-feedback ${
                  speedState
                }`
              }
            >
              {feedback}
            </div>
          )}
        </div>
      )}


      {/* ===================================================
          FINISH / LEVEL RESULT
      =================================================== */}

      {raceFinished &&
        (
          !finalVictory ||
          showFinalVictoryPanel
        ) && (
        <div
          className="race-finished"
        >
          {finalPosition === 1 ? (
            <>
              <div>
                {currentLevel === 3
                  ? "🏆 GAME COMPLETE!"
                  : "🏆 LEVEL COMPLETE!"}
              </div>


              <div>
                1ST PLACE
              </div>


              <div>
                {currentLevel === 1
                  ? "Level 2 unlocked!"
                  : currentLevel === 2
                  ? "Level 3 unlocked!"
                  : "You completed all three levels!"}
              </div>


              {currentLevel < 3 ? (
                <button
                  className="
                    welcome-button
                    welcome-play-button
                  "
                  onClick={
                    goToNextLevel
                  }
                >
                  {currentLevel === 1
                    ? "CONTINUE TO LEVEL 2"
                    : "CONTINUE TO LEVEL 3"}
                </button>
              ) : (
                <button
                  className="
                    welcome-button
                    welcome-play-button
                  "
                  onClick={
                    playAgain
                  }
                >
                  PLAY AGAIN
                </button>
              )}
            </>
          ) : (
            <>
              <div>
                🏁 RACE FINISHED
              </div>


              <div>
                You finished{" "}
                {getOrdinalPosition(
                  finalPosition ||
                  playerPosition
                )}
              </div>


              <div>
                Finish in 1st place
                to progress.
              </div>


              <button
                className="
                  welcome-button
                  welcome-play-button
                "
                onClick={
                  retryLevel
                }
              >
                TRY AGAIN
              </button>
            </>
          )}
        </div>
      )}


      {/* ===================================================
          THREE.JS WORLD
      =================================================== */}

      <Canvas
        shadows={
          currentLevel === 1
        }

        dpr={
          1
        }

        camera={{
          position: [
            0,
            6,
            15,
          ],

          fov:
            60,

          near:
            0.1,

          far:
            500,
        }}

        gl={{
          antialias:
            currentLevel === 1,

          powerPreference:
            "high-performance",

          toneMapping:
            THREE
              .ACESFilmicToneMapping,

          toneMappingExposure:
            currentLevel === 3
              ? 1.10
              : 0.9,
        }}

        onCreated={({
          gl,
        }) => {
          /*
            Level 2 deliberately has no shadow-casting light.
            Do not keep the shadow-map subsystem enabled there.
          */
          gl.shadowMap.enabled =
            currentLevel === 1;

          if (
            currentLevel === 1
          ) {
            gl.shadowMap.type =
              THREE
                .PCFSoftShadowMap;
          }
        }}
      >
  <GameWorld
  currentLevel={
    currentLevel
  }

  raceRunId={
    raceRunId
  }

  onProgress={
    handleProgress
  }

  playerSpeed={
    playerSpeed
  }

  raceStatus={
    raceStatus
  } 

  countdown={
    countdown
  }

  showGo={
    showGo
  }

  onReady={() => {
  setGameReady(true);
}}

  finalVictory={
    finalVictory
  }

  showFinalVictoryPanel={
    showFinalVictoryPanel
  }
/>
      </Canvas>

    </div>
  );
}
